import { Marker } from '@twitchaxorg/markerclusterer';

export type HandsontableNamespace = typeof import('handsontable').default;
export type HandsontableCore = import('handsontable').default;

export type ChartNamespace = typeof import('chart.js/auto').default;
export type ChartCore = import('chart.js/auto').default;

export interface ObjectId {
    $oid: string;
}

export type UserType = 'Viewer' | 'Owner' | 'Editor' | 'Collaborator';

/// Holds the an email and the associated role
export interface RoleAssignment {
    id: ObjectId;
    email: string; // does not link directly to an account because emails without an account can be added to roles
    role:
        | FolderRoleType
        | TopicRoleType
        | PointRoleType
        | OrganizationRoleType
        | CreatorProfileRoleType
        | DirectoryEntryRoleType;
}

export interface TopicRoleType {
    TopicRoleType: TopicRole;
}

export interface PointRoleType {
    PointRoleType: PointRole;
}

export interface FolderRoleType {
    FolderRoleType: FolderRole;
}

export interface OrganizationRoleType {
    OrganizationRoleType: OrganizationRole;
}

export interface CreatorProfileRoleType {
    CreatorProfileRoleType: CreatorProfileRole;
}

export interface DirectoryEntryRoleType {
    DirectoryEntryRoleType: DirectoryEntryRole;
}

/// Holds the folder_id and associated role for a RoleAssignment
export interface FolderRole {
    folder_id: ObjectId;
    level: FolderPermissionLevel;
}

/// Holds the organization_id and associated role for a RoleAssignment
export interface OrganizationRole {
    organization_id: ObjectId;
    level: OrganizationPermissionLevel;
}

/// Holds the topic_id and associated role for a RoleAssignment
export interface TopicRole {
    topic_id: ObjectId;
    level: TopicPermissionLevel;
}

/// Holds the point_id and associated role for a RoleAssignment
export interface PointRole {
    point_id: ObjectId;
    level: PointPermissionLevel;
}

/// Holds the creator_profile_id and associated role for a RoleAssignment
export interface CreatorProfileRole {
    creator_profile_id: ObjectId;
    level: CreatorProfilePermissionLevel;
}

/// Holds the directory_entry_id and associated role for a RoleAssignment
export interface DirectoryEntryRole {
    directory_entry_id: ObjectId;
    level: DirectoryEntryPermissionLevel;
}

export enum CreatorProfileImageSize {
    SMALL = 'small',
    MEDIUM = 'medium',
    LARGE = 'large'
}

export enum CreatorProfileCardType {
    MAPINFODRAWER = 'mapinfodrawer',
    CREATORPROFILEPAGE = 'creatorprofilepage'
}

/// Permission types for Folders
export enum FolderPermissionLevel {
    /// can add owner + below, and can add any map in folder to another folder
    OWNER = 'OWNER',
    /// can add writer + below, can add/remove maps to the folder + edit name, cannot add maps outside of folder
    WRITER = 'WRITER',
    /// can add other collaborator, can edit maps but cannot edit the folder or filesystem
    COLLABORATOR = 'COLLABORATOR'
}

/// Permission types for Organizations
export enum OrganizationPermissionLevel {
    /// Can edit org properties, can add other people to the org, owner of all maps and Organizations.
    ADMINISTRATOR = 'ADMINISTRATOR',
    /// Owner for all maps and Organizations in the org
    OWNER = 'OWNER',
    /// Writer for all maps and Organizations in the org
    WRITER = 'WRITER',
    /// Collaborator for all maps and Organizations in the org
    COLLABORATOR = 'COLLABORATOR',
    /// Can log into org, can't see maps or Organizations until added to them
    LIMITED = 'LIMITED',
    /// API Reader for all maps and Organizations in the org
    APIREADER = 'APIREADER'
}

/// Permission types for Topics
export enum TopicPermissionLevel {
    /// can add owners + below, can edit a map, can add map to a folder
    OWNER = 'OWNER',
    /// can add writers + below, can edit a map
    WRITER = 'WRITER',
    /// can add a new point, cannot edit existing points or map settings
    COLLABORATOR = 'COLLABORATOR',
    /// can read map, cannot make updates or add points
    READER = 'READER',

    /// Can add writers + below, can edit a map.
    PAYWALLWRITER = 'PAYWALLWRITER',
    /// Can add a new point, cannot edit existing points or map settings.
    PAYWALLCOLLABORATOR = 'PAYWALLCOLLABORATOR',
    /// Can read map, cannot make updates or add points.
    PAYWALLREADER = 'PAYWALLREADER',
}

/// Permission types for Points
export enum PointPermissionLevel {
    /// can add owners + below, can edit or delete a point
    OWNER = 'OWNER',
    /// can add writers + below, can edit or delete a point
    WRITER = 'WRITER',
    /// can read point, cannot make updates to point
    READER = 'READER'
}

/// Permission types for Creator Profile
export enum CreatorProfilePermissionLevel {
    OWNER = 'OWNER'
}

/// Permission types for Directory Entries
export enum DirectoryEntryPermissionLevel {
    /// Can manage contacts, delete entry, transfer ownership.
    OWNER = 'OWNER',
    /// Can edit all fields on the entry.
    EDITOR = 'EDITOR',
    /// Can view the entry but not modify it.
    VIEWER = 'VIEWER'
}

/// Permission types for Creator Profile
export enum TopicCreationSource {
    INTERNAL_CREATE = 'internal_create',
    INTERNAL_DUPLICATE = 'internal_duplicate',
    INTERNAL_FROM_POINT = 'internal_from_point',
    INTERNAL_AI = 'internal_ai',
    MAPSGPT_AI = 'mapsgpt_ai',
    CHROME_CREATE = 'chrome_create',
    CHROME_AI = 'chrome_ai',
    ZAPIER_CREATE = 'zapier_create',
    COLLECTION = 'collection',
}

/// Holds an email and the associated role to be created
export interface CreateRoleRequest {
    email: string;
    level:
        | FolderPermissionLevelType
        | TopicPermissionLevelType
        | PointPermissionLevelType
        | OrganizationPermissionLevelType
        | CreatorProfilePermissionLevel
        | DirectoryEntryPermissionLevelType;
}

export interface OrganizationPermissionLevelType {
    OrganizationPermissionLevelType: OrganizationPermissionLevel;
}

export interface FolderPermissionLevelType {
    FolderPermissionLevelType: FolderPermissionLevel;
}

export interface TopicPermissionLevelType {
    TopicPermissionLevelType: TopicPermissionLevel;
}

export interface PointPermissionLevelType {
    PointPermissionLevelType: PointPermissionLevel;
}

export interface CreatorProfilePermissionLevelType {
    CreatorProfilePermissionLevelType: CreatorProfilePermissionLevel;
}

export interface DirectoryEntryPermissionLevelType {
    DirectoryEntryPermissionLevelType: DirectoryEntryPermissionLevel;
}

export interface TopicCategory {
    _id: ObjectId | undefined;
    name: string;
    description?: string;
    icon: string;
    icon_size: number | undefined;
    marker_shape?: NumberedIconShape;
    can_crowdsource: boolean;
}

export enum NumberedIconShape {
    Circle = 'circle',
    Square = 'square',
    Star = 'star',
    Diamond = 'diamond',
    Hexagon = 'hexagon',
}

export interface TopicStatistics {
    approximate_point_count: number;
    approximate_topic_views: number;
    approximate_topic_renders: number;
    /** Renders by the new MapLibre viewer (the Next.js app). Distinct
     *  from approximate_topic_renders (Google Maps, legacy Lit client)
     *  so internal dashboards can watch renderer adoption during the
     *  migration. Long-term these should be summed for the
     *  customer-facing total. */
    approximate_topic_maplibre_renders: number;
    /** Total clicks across all per-point custom-field actions (phone,
     *  email, url, action-bar buttons) on the topic. Aggregated from
     *  `point_field_clicks` every 12h. Per-button breakdown comes
     *  on-demand from `GET /api/topics/<id>/point_field_clicks`. */
    approximate_point_field_clicks: number;
    approximate_point_views: number;
    approximate_challenge_views: number;
    approximate_topic_renders_this_month: number;
    approximate_topic_thirty_day_views: number;
    approximate_topic_fourteen_day_views: number;
    approximate_topic_seven_day_views: number;
    statistics_updated_at?: string;
}

export interface AddPointCustomization {
    category?: AddPointFieldCustomization;
    location?: AddPointFieldCustomization;
    title?: AddPointFieldCustomization;
    description?: AddPointFieldCustomization;
    tags?: AddPointFieldCustomization;
    more_info?: AddPointFieldCustomization;
    special_offer?: AddPointFieldCustomization;
    photos?: AddPointFieldCustomization;
    terms?: AddPointFieldCustomization;
    email?: AddPointFieldCustomization;
    embedded_video?: AddPointFieldCustomization;
    custom_icon?: AddPointFieldCustomization;
    date_time?: AddPointFieldCustomization;
    custom_fields?: CustomFieldCustomization[];
    email_first?: boolean;
    verify_email?: boolean;
    custom_success_message?: string;
    suppress_confetti?: boolean;
    custom_email_message?: string;
    custom_email_subject?: string;
    custom_email_warning?: string;
    show_add_another?: boolean;
}

export interface CustomFieldCustomization {
    _id?: ObjectId;
    customizations: AddPointFieldCustomization;
    order?: number;
    field_type: TextType | NumberType | DateType | TimeType | PhoneType | URLType | MultiSelectType | SingleSelectType | EmailType | RatingType;
    archived: boolean;
}

export interface TextType {
    Text: CustomFieldTextType;
}

export interface NumberType {
    Number: CustomFieldNumberType;
}

export interface DateType {
    Date: CustomFieldDateType;
}

export interface TimeType {
    Time: CustomFieldTimeType;
}

export interface PhoneType {
    Phone: CustomFieldPhoneType;
}

export interface EmailType {
    Email: CustomFieldEmailType;
}

export interface URLType {
    URL: CustomFieldUrlType;
}

export interface MultiSelectType {
    MultiSelect: CustomFieldMultiSelectType;
}

export interface SingleSelectType {
    SingleSelect: CustomFieldSingleSelectType;
}

export interface RatingType {
    Rating: CustomFieldRatingType;
}

export interface CustomFieldTextType {
    maxLength?: number;
    minLength?: number;
}

export interface CustomFieldNumberType {
    max?: number;
    min?: number;
}

export interface CustomFieldDateType {
    max?: Date;
    min?: Date;
}

export interface CustomFieldTimeType {
    max?: Date;
    min?: Date;
}

export interface CustomFieldPhoneType {
    maxLength?: number;
    minLength?: number;
}

export interface CustomFieldEmailType {
    maxLength?: number;
    minLength?: number;
}


export interface CustomFieldUrlType {
    maxLength?: number;
    minLength?: number;
    limitToDomain?: boolean;
    domains?: string[];
    show_as_button?: boolean;
    open_in_new_tab?: boolean;
    button_color?: string;
    button_icon_url?: string;
    button_text?: string;
}

export interface CustomFieldMultiSelectType {
    options: string[];
}

export interface CustomFieldSingleSelectType {
    options: string[];
}

export interface CustomFieldRatingType {
    maxStars?: number;
}

export interface AddPointFieldCustomization {
    header?: string;
    visibility_rules?: VisibilityRule[][];
    sub_header?: string;
    hide?: boolean;
    mandatory?: boolean;
    hide_on_point_card?: boolean;
}

// Implemented as array of arrays where the top level is ANDs and the second level is ORs
export interface VisibilityRule {
    fieldType: LogicFieldType;
    targetField: string;
    operator: LogicOperator;
    comparisonValue: string;
    // only for fields like parameter and role
    extendedComparisonString: string;
}

export enum LogicFieldType {
   UrlParameter = 'UrlParameter',
   CustomField = 'CustomField',
   StandardField = 'StandardField',
   UserRole = 'UserRole',
}

// These strings match values on the back end. Be careful changing.
export enum LogicOperator {
    IsSet = 'is set',
    IsNotSet = 'is not set',
    Equals = 'equals',
    NotEquals = 'does not equal',
    LessThan = 'less than',
    GreaterThan = 'greater than',
    Contains = 'contains',
    NotContains = 'does not contain',
}

export type AddPointVisibilityFieldType = 'select' | 'text' | 'number' | 'parameter';

export interface VisibilityOption {
    name: string;
    value: string;
}

export interface VisibilityField {
    name: string;
    value: string;
    inputType: AddPointVisibilityFieldType;
    options?: VisibilityOption[];
    formPart: LogicFieldType;
}


export interface AddPointCustomizationComponent { // for creating the component in admin portal
    customization: string,
    userHasEntitlement: boolean,
    componentTitle: string,
    additionalComponentInfo?: boolean,
    hasHideOnForm: boolean,
    hasTitle: boolean,
    hasDescription: boolean,
    hasRequired: boolean,
    hasHideOnPointCard: boolean,
    hideOnForm?: boolean,
    title?: string,
    description?: string,
    required?: boolean,
    hideOnPointCard?: boolean,
    customFieldId?: ObjectId,
    customType?: TextType | NumberType | DateType | TimeType | PhoneType | URLType | MultiSelectType | SingleSelectType | EmailType | RatingType,
    archived?: boolean;
    icon?: string;
    hasVisibilityRules?: boolean;
    visibilityRules?: VisibilityRule[][];
}

export interface GetTopicResponse {
    _id: ObjectId;
    name: string;
    organization_id: ObjectId | undefined;
    email: string | undefined;
    creator: string | undefined;
    categories: TopicCategory[];
    parent_folders: ObjectId[];
    has_dates: boolean;
    has_time_only?: boolean;
    show_easy_date_filters?: boolean;
    has_date_only?: boolean;
    has_end_date_time?: boolean;
    should_filter_past_events: boolean;
    has_likes: boolean;
    discovery_level: DiscoveryLevel;
    disable_google_images: boolean;
    hide_header: boolean;
    show_advanced_filter_row: boolean;
    custom_font: string | undefined;
    show_point_list_on_open?: boolean;
    point_sort_order: PointOrderingOption[];
    disable_location_ui: boolean | undefined;
    disable_build_your_own: boolean | undefined;
    disable_command_center_buttons: boolean | undefined;
    requires_approval: boolean;
    send_crowdsource_email: boolean;
    enable_point_numbering?: boolean;
    hide_featured_on?: boolean;
    embed_gesture_greedy: boolean;
    embed_all_description_links: boolean;
    filter_featured_on?: boolean;
    featured_on_creator_profiles?: ObjectId[];
    replace_icons_with_numbers?: boolean;
    point_number_circle_color?: string;
    marker_shape?: NumberedIconShape;
    enable_panning?: boolean;
    default_map_center: Location;
    default_map_zoom: number;
    default_map_rotation: number;
    default_map_tilt: number;
    marker_cluster_threshold: number;
    marker_cluster_minimum: number;
    icon_size: number;
    header_logo: string;
    header_url: string | undefined;
    header_color: string;
    header_text_color: string;
    mobile_header_size?: number;
    cluster_accent_color: string;
    translation_languages: TranslationLanguage[]
    default_language?: string;
    featured_color: string;
    featured_name: string;
    custom_map_style: string | undefined;
    custom_map_style_name: string | undefined;
    map_renderer: MapRenderer;
    custom_map_geojson: string | undefined;
    custom_map_geojson_style: string | undefined;
    custom_shapes: CustomShape[];
    custom_overlays: CustomOverlay[];
    custom_social_media_image: string | undefined;
    description?: string;
    created_at: string;
    updated_at: string;
    is_owner: boolean;
    is_writer: boolean;
    is_addpointer: boolean;
    is_reader: boolean;
    is_gate_limited: boolean;
    key_level: KeyLevel;
    statistics: TopicStatistics;
    cc_account_admin_link: string | undefined;
    cc_write_link: string | undefined;
    cc_addpoint_link: string | undefined;
    cc_read_link: string | undefined;
    cc_keyless_link: string | undefined;
    discover_details_link: string | undefined;
    standalone_map_link: string | undefined;
    /** MapsGPT map with no workspace behind it; the viewer shows the make-your-own frame. */
    is_unclaimed?: boolean;
    /** Login deep link that brings an unclaimed map into the creator's workspace. */
    claim_link?: string;
    tag_info: TagInformation;
    add_point_customization: AddPointCustomization | undefined;
    pop_ups: PopUpCustomization[] | undefined;
    point_email_capture_customization: PointEmailCaptureCustomization | undefined;
    comment_settings: CommentSettings | undefined;
    default_shape_style?: GeoJsonStyle;
    image_display?: ImageDisplay;
    default_list_view?: PointCardView
    search_pill_settings: SearchPillSettings | undefined;
    share_settings: ShareSettings | undefined;
    creator_profiles: ObjectId[];
    active_add_ons?: ObjectId[];
    hidden_add_ons?: ObjectId[];
    gates: GetTopicGateResponse[];
    creation_source: TopicCreationSource;
    point_name: string,
    text_get_directions: string,
    type_get_directions: DirectionType | undefined,
    text_mark_as_visited: string,
    text_visited: string,
    text_book_experiences: string,
    text_more_info: string,
    disable_get_directions: boolean,
    disable_add_to_map: boolean,
    disable_more_info: boolean,
    disable_mark_as_visited: boolean,
    disable_book_experiences: boolean,
    disable_share_button: boolean,
    disable_list_button: boolean,
    disable_search_button: boolean,
    disable_powered_by_proxi: boolean,
    hide_bookmark_buttons: boolean;
    hide_print_button: boolean;
    hide_get_route: boolean;
    hide_multi_select: boolean;
    hide_map_info_drawer: boolean;
    hide_send_map_to_phone: boolean;
    hide_map_stats_in_info_drawer: boolean;
    use_simple_filters_desktop: boolean;
    use_simple_filters_mobile: boolean;
    text_advanced_filters: string;
    hide_category_filters: boolean;
    hide_tag_filters: boolean;
    hide_keyword_filters: boolean;
    open_links_in_same_tab: boolean;
    webhooks: TopicWebhook[];

    enabled_features?: SubscriptionFeature[];

    /** Projected from `Map.last_edited_in_new_ui_at` when this topic has
     *  a linked Map saved through the new editor. The legacy admin
     *  surfaces a banner when set, nudging curators to switch editors.
     *  ISO timestamp. */
    last_edited_in_new_ui_at?: string;
    /** Projected from `Map.created_in_new_editor` when this topic has a
     *  linked Map. `true` = authored natively in the new editor (no
     *  useful classic-editor counterpart); `false`/undefined = legacy
     *  or migrated. The new admin's map-card uses this to choose between
     *  a single "Edit" button and a "new vs classic" dropdown. */
    created_in_new_editor?: boolean;
    /** Carried through from `Topic.map_id` — the linked Map's ID, set on
     *  any topic that has been migrated (or natively created in the new
     *  editor). Lets the new admin route to `/map-editor/{mapId}` and
     *  build `/embed/m/{mapId}` share/embed URLs without a migrate
     *  roundtrip. Absent on legacy-only topics. */
    map_id?: ObjectId;
    /** Projected from `Map.collection_id` when the topic has a linked
     *  Map. Lets the new admin render a "linked to <Collection>" pill
     *  on the map card without an extra fetch. Absent on legacy-only
     *  topics. */
    collection_id?: ObjectId;
    /** Crawlers should not index the public page. */
    noindex?: boolean;
    /** The town this map is about, when the nightly matcher found one. */
    locality?: TopicLocality;
}

export interface TopicLocality {
    name: string;
    region: string;
    country: string;
    /** City-page address segment, e.g. `walla-walla-wa`. */
    slug: string;
}

export interface ContributedPoint {
    _id: ObjectId;
    topic_id: ObjectId;
    name?: string;
    description?: string;
    location: Location;
    created_at: string;
}

export interface TopicSearchResult {
    _id: ObjectId;
    name: string;
    description?: string;
    categories: TopicCategory[];
    tag_info?: TagInformation;
    email: string | undefined;
    organization_id?: ObjectId;
    default_map_center: Location;
    default_map_zoom: number;
    discovery_level: DiscoveryLevel;
    statistics: TopicStatistics;
    created_at: string;
    updated_at: string;
    is_owner: boolean;
    is_writer: boolean;
    is_addpointer: boolean;
    is_reader: boolean;
    standalone_map_link: string | undefined;
    cc_keyless_link: string | undefined;
    cc_read_link: string | undefined;
    cc_addpoint_link: string | undefined;
    cc_write_link: string | undefined;
    cc_account_admin_link: string | undefined;
    discover_details_link: string | undefined;
    map_renderer?: MapRenderer;
    map_id?: ObjectId;
}

export enum KeyLevel {
    Write = 'write',
    AddPoint = 'addpoint',
    Read = 'read',
    Password = 'password',
    Email = 'email',
    None = 'none'
}

export interface SearchPillSettings {
    enabled: boolean;
    pill_text: string | undefined;
    allow_add: boolean;
    search_types: AutocompleteSearchType[];
    search_filters: string[] | undefined;
}

export enum ChildType {
    Folder = 'folder',
    Topic = 'topic'
}

export enum DirectionType {
    drive = 'drive',
    walk = 'walk',
    bike = 'bike',
    transit = 'transit'
}

export interface ChildEntity {
    _id: ObjectId;
    child_type: ChildType;
}

export interface GetFolderResponse {
    _id: ObjectId;
    name: string;
    organization_id: ObjectId | undefined;
    is_owner: boolean;
    is_writer: boolean;
    is_collaborator: boolean;
    children: ChildEntity[];
    ancestors: ObjectId[];
    created_at: string;
    updated_at: string;
}

export enum BusinessCategory {
    MainStreetDowntownAssociation = 'Main Street / Downtown Association',
    ChamberOfCommerce = 'Chamber of Commerce',
    Events = 'Events',
    RestaurantWeeks = 'Restaurant Weeks',
    DestinationMarketing = 'Destination Marketing',
    EconomicDevelopment = 'Economic Development',
    JournalismMedia = 'Journalism & Media',
    GovernmentCity = 'Government / City',
    ContentCreators = 'Content Creators',
    CorporateTeamBuilding = 'Corporate / Team Building',
    HotelsShortTermRentals = 'Hotels & Short Term Rentals',
    RealEstate = 'Real Estate',
    Retail = 'Retail',
    SalesTeams = 'Sales Teams',
    Other = 'Other'
}

export enum UseCase {
    EventsFestivalMaps = 'Events & Festival Maps',
    InteractiveMaps = 'Interactive Maps',
    PassportsAndChallenges = 'Passports & Challenges',
    AppLikeAndEmbeddableGuides = 'App-Like and Embeddable Guides',
    BestOfAwardsCompetitions = 'Best Of / Awards Competitions',
    CrowdsourcedRecommendations = 'Crowdsourced Locations & Recommendations',
    MemberDirectories = 'Member Directories',
    SponsorshipsMembershipDues = 'Sponsorships & Membership Dues',
    VendorBoothFees = 'Vendor & Booth Fees',
    MembersOnlyContent = 'Members-Only Content',
    EventsCalendar = 'Community Events Calendar',
    BusinessPortal = 'Let Businesses Update Their Listings',
    EmailTextMessaging = 'Email & Text Messaging',
    LoyaltyCouponPrograms = 'Loyalty & Coupon Programs',
    StoreLocator = 'Store Locator',
    InternalEmployeeTooling = 'Internal Employee Tooling',
    IntegrationsWithExistingAppsCRMs = 'Integrations with Existing Apps & CRMs',
    Other = 'Other'
}

export interface OrganizationDemographics {
    business_category: BusinessCategory[] | undefined;
    use_cases: UseCase[] | undefined;
    website: string | undefined;
    team_size: '1' | '2-5' | '5-10' | '20+' | undefined;
    /** Self-reported; staff verification lives on `Organization.nonprofit_verification`. */
    nonprofit?: boolean;
}

/**
 * Viewport used as a *soft bias* (never a hard restriction) for
 * autocomplete, geocoding, and map centering.
 */
export interface Bounds {
    north: number;
    south: number;
    east: number;
    west: number;
}

/**
 * The org's default "home" location. Used as a soft bias so admins don't
 * get autocomplete results from around the world. Set from org settings,
 * or auto-seeded from the first map created in the new UI when null.
 */
export interface DefaultLocation {
    /** Human-readable label shown in settings, e.g. "Bozeman, MT". */
    name: string;
    latitude: number;
    longitude: number;
    /** Optional viewport for regional orgs (DMOs, multi-town BIDs). */
    bounds?: Bounds;
}

export interface GetOrganizationResponse {
    _id: ObjectId;
    name: string;
    chargebee_subscription?: ChargebeeSubscriptionInfo;
    enterprise_billing: boolean;
    enabled_features: SubscriptionFeature[];
    payment_feature_eligible: boolean;
    buyer_covers_program_fees?: boolean;
    logo: string;
    primary_color: string;
    secondary_color: string;
    custom_font: string | undefined;
    sso: string;
    cname: CnameData | undefined;
    is_administrator: boolean;
    is_owner: boolean;
    is_writer: boolean;
    is_collaborator: boolean;
    is_limited: boolean;
    default_map_template_id: ObjectId | undefined;
    referral_code: string | undefined;
    demographics: OrganizationDemographics | undefined;
    default_timezone?: string;
    default_location?: DefaultLocation;
    beta_features_enabled?: boolean;
    calendar_description?: string;
    created_at: string;
    updated_at: string;
}

export interface OrganizationTeamMember {
    role: OrganizationRoleType;
    email: string;
    name: string | undefined;
    avatar: string | undefined;
    free: boolean;
}

export interface UserInfo {
    email: string;
    name: string | undefined;
    avatar: string | undefined;
}

export interface CnameData {
    custom_url: string | undefined;
    validated: boolean;
    dns_verified: boolean;
    reply_to_email: string | undefined;
    homepage_behavior: CustomDomainHomepage;
    homepage_redirect: string | undefined;
    /** Hostnames the org served on before switching domains. Server-controlled. */
    previous_urls?: string[];
}

export enum CustomDomainHomepage {
    Login = 'login',
    Maps = 'maps',
    Redirect = 'redirect'
}

export enum ImageDisplay {
    Cover = 'cover',
    Contain = 'contain',
    ContainBlur = 'contain-blur',
}

export interface GetPublicOrganizationResponse {
    _id: ObjectId;
    name: string;
    /** A template-page draft nobody has claimed yet; the shell shows the claim banner. */
    is_draft?: boolean;
    logo: string;
    primary_color: string;
    secondary_color: string;
    custom_font: string | undefined;
    sso: string;
    cname: CnameData | undefined;
    referral_code: string | undefined;
    beta_features_enabled: boolean;
    enabled_features: SubscriptionFeature[];
    /** Server-resolved: no plan, in a trial, or paying. Drives the "made with Proxi" band. */
    billing_state?: 'free' | 'trial' | 'paid' | 'unknown';
    /** The workspace's own site, when it gave one. */
    website?: string;
    /** `/o/<id>`, absent when the workspace hid its public page. */
    public_page_path?: string;
    created_at: string;
    updated_at: string;
}

export interface GetOrganizationRoleResponse {
    _id: ObjectId;
    email: string;
    role: {
        OrganizationRoleType: {
            organization_id: ObjectId;
            level: string;
        }
    }
}

export interface CreateCreatorProfileResponse {
    _id: ObjectId;
    public_link: string;
    username: string;
    organization_id?: ObjectId;
}

export interface GetCreatorProfileResponse {
    _id: ObjectId;
    organizaiton_id?: ObjectId;
    username: string;
    name?: string;
    tagline?: string;
    description?: string;
    image?: string;
    cover_image?: string;
    custom_urls: CreatorProfileCustomUrl[];
    facebook_url?: string;
    linkedin_url?: string;
    twitter_url?: string;
    instagram_url?: string;
    pinterest_url?: string;
    tiktok_url?: string;
    is_owner: boolean;
    topics: GetCreatorProfileTopic[];
    link_set: CreatorProfileLinkSet;
    created_at: string;
    updated_at: string;
}

// WARNING!!! PLEASE BE SURE TO UPDATE createTopicRequestFromTopic WITH CHANGES OR THERE WILL BE BUGS!!
export interface CreateTopicRequest {
    name: string | undefined;
    email: string | undefined;
    organization_id: ObjectId | undefined;
    use_organization_template?: boolean | undefined;
    creator: string | undefined;
    categories: TopicCategory[] | undefined;
    has_dates: boolean;
    has_time_only?: boolean | undefined;
    has_date_only?: boolean | undefined;
    show_easy_date_filters: boolean | undefined;
    has_end_date_time?: boolean | undefined;
    should_filter_past_events?: boolean | undefined;
    has_likes?: boolean | undefined;
    discovery_level: DiscoveryLevel | undefined;
    disable_google_images: boolean | undefined;
    hide_header: boolean | undefined;
    show_advanced_filter_row: boolean | undefined;
    custom_font: string | undefined;
    show_point_list_on_open?: boolean | undefined;
    point_sort_order?: PointOrderingOption[];
    disable_location_ui?: boolean | undefined;
    disable_build_your_own?: boolean | undefined;
    disable_command_center_buttons?: boolean | undefined;
    requires_approval: boolean | undefined;
    send_crowdsource_email: boolean | undefined;
    enable_point_numbering?: boolean | undefined;
    replace_icons_with_numbers?: boolean | undefined;
    point_number_circle_color?: string;
    marker_shape?: NumberedIconShape;
    image_display: ImageDisplay | undefined;
    default_list_view: PointCardView | undefined;
    default_shape_style?: GeoJsonStyle | undefined;
    search_pill_settings: SearchPillSettings | undefined;
    share_settings: ShareSettings | undefined;
    default_map_center: Location | undefined;
    default_map_zoom: number | undefined;
    default_map_rotation: number | undefined;
    default_map_tilt: number | undefined;
    marker_cluster_threshold: number | undefined;
    marker_cluster_minimum: number | undefined;
    icon_size: number | undefined;
    hide_featured_on?: boolean;
    embed_gesture_greedy?: boolean | undefined;
    embed_all_description_links?: boolean;
    filter_featured_on?: boolean | undefined;
    featured_on_creator_profiles?: ObjectId[] | undefined;
    enable_panning?: boolean | undefined;
    header_logo: string | undefined;
    header_url: string | undefined;
    header_color: string | undefined;
    header_text_color: string | undefined;
    mobile_header_size?: number | undefined;
    cluster_accent_color: string | undefined;
    translation_languages: TranslationLanguage[] | undefined;
    default_language?: string;
    featured_color: string | undefined;
    featured_name: string | undefined;
    custom_map_style: string | undefined;
    custom_map_style_name: string | undefined;
    map_renderer?: MapRenderer;
    custom_map_geojson: string | undefined;
    custom_map_geojson_style: string | undefined;
    custom_shapes?: CustomShape[];
    custom_overlays?: CustomOverlay[];
    custom_social_media_image: string | undefined;
    description?: string;
    tag_info: CreateTagRequest | undefined;
    add_point_customization: AddPointCustomization | undefined;
    pop_ups: PopUpCustomization[] | undefined;
    comment_settings: CommentSettings | undefined;
    active_add_ons?: ObjectId[];
    hidden_add_ons?: ObjectId[];
    gates?: CreateTopicGateRequest[];
    point_email_capture_customization: PointEmailCaptureCustomization | undefined;
    creation_source?: TopicCreationSource;
    point_name?: string,
    text_get_directions?: string,
    type_get_directions?: DirectionType | undefined,
    text_mark_as_visited?: string,
    text_visited?: string,
    text_book_experiences?: string,
    text_more_info?: string,
    disable_get_directions?: boolean,
    disable_add_to_map?: boolean,
    disable_more_info?: boolean,
    disable_mark_as_visited?: boolean,
    disable_book_experiences?: boolean,
    disable_share_button?: boolean,
    disable_list_button?: boolean,
    disable_search_button?: boolean,
    disable_powered_by_proxi?: boolean,
    hide_bookmark_buttons?: boolean;
    hide_print_button?: boolean;
    hide_get_route?: boolean;
    hide_multi_select?: boolean;
    hide_map_info_drawer?: boolean;
    hide_send_map_to_phone?: boolean;
    hide_map_stats_in_info_drawer?: boolean;
    use_simple_filters_desktop?: boolean;
    use_simple_filters_mobile?: boolean;
    text_advanced_filters?: string;
    hide_category_filters?: boolean;
    hide_tag_filters?: boolean;
    hide_keyword_filters?: boolean;
    open_links_in_same_tab?: boolean;
    webhooks?: TopicWebhook[];
}

export interface CreateFolderRequest {
    name: string;
    organization_id: ObjectId | undefined;
    parent_folder_id: ObjectId | undefined;
}

export interface CreateOrganizationRequest {
    name: string;
    logo: string | undefined;
    chargebee_subscription?: ChargebeeSubscriptionReference;
    enterprise_billing: boolean | undefined;
    primary_color: string | undefined;
    secondary_color: string | undefined;
    cname: CnameData | undefined;
    sso: string;
    default_map_template_id: ObjectId | undefined;
    custom_font: string | undefined;
    demographics: OrganizationDemographics | undefined;
    default_timezone?: string;
    default_location?: DefaultLocation;
    calendar_description?: string;
    how_did_you_hear?: string;
    how_did_you_hear_details?: string;
}

export interface UpdateCreatorProfileRequest {
    username: string;
    organization_id?: ObjectId;
    name?: string;
    tagline?: string;
    description?: string;
    image?: string;
    cover_image?: string;
    custom_urls: CreatorProfileCustomUrl[];
    facebook_url?: string;
    linkedin_url?: string;
    twitter_url?: string;
    instagram_url?: string;
    pinterest_url?: string;
    tiktok_url?: string;
}

export interface CreatorProfileCustomUrl {
    cta: string;
    url: string;
}

export interface CreatorProfileTopic {
    topic: GetTopicResponse;
    is_pinned: boolean;
}

export interface GetCreatorProfileTopic {
    topic_id: ObjectId;
    is_pinned: boolean;
}

export interface CreatorProfileLinkSet {
    public_link: string;
}

export interface AvailableCategories {
    _id: ObjectId | undefined;
    name: string;
    count: number;
    icon: string;
}

export interface LatLng {
    lat: number;
    lng: number;
}

export interface NearbySearchLatLng {
    latitude: number;
    longitude: number;
}


export interface Location {
    search: string;
    place_id: string;
    // This is not snake_case so we can keep it to Google's convention.
    latLng: LatLng;
    types?: string[];
}

export interface CreateLocationRequest {
    search: string | undefined;
    place_id: string | undefined;
    // This is not snake_case so we can keep it to Google's convention.
    latLng: LatLng | undefined;
    types?: string[] | undefined;
}

export interface PlaceDetails {
    _id: ObjectId | undefined;
    place_id: string;
    photos: string[] | undefined;
    /** Durable Google photo references, positionally aligned with `photos`. */
    photo_refs: string[] | undefined;
    /** Photographer credit per entry in `photo_refs`, keyed by the reference.
     *  Google requires it be shown wherever the photo is displayed at size. */
    photo_credits: Record<string, string> | undefined;
    types: string[] | undefined;
    /** Set once Google reported the place id obsolete (closed or moved). */
    place_gone_at: string | undefined;
}

export enum VanityType {
    Addpoint = 'addpoint',
    Read = 'read',
    Discovery = 'discovery',
    Keyless = 'keyless'
}

export interface ShareSettings {
    page_title: string | undefined;
    page_description: string | undefined;
    page_image: string | undefined;
    favicon: string | undefined;
    phone_short_name: string | undefined;
    phone_icon_small: string | undefined;
    phone_icon_large: string | undefined;
    google_tag: string | undefined;
}

export enum LoginOrigin {
    Challenge = 'challenge',
    Checkin = 'checkin',
    Election = 'election',
    SigninModule = 'signin_module',
    ChargebeeWebhook = 'chargebee_webhook',
    PasswordPaywall = 'password_paywall',
    AppSumo = 'appsumo',
    Comment = 'comment',
    Game = 'game',
    Viewer = 'viewer'
}

export enum EntityType {
    Organization = 'organization',
    Folder = 'folder',
    Topic = 'topic',
    Point = 'point',
    CreatorProfile = 'creator_profile'
}

export enum TopicAccessLevel {
    Owner = 'owner',
    Writer = 'writer',
    Addpointer = 'addpointer',
    Reader = 'reader'
}

export interface VanityRedirect {
    key: string;
}

export interface SpecialOfferInformation {
    description: string;
    /** Optional start of the offer's validity window. `undefined` = valid
     *  from whenever the point goes live. */
    start?: string;
    /** Optional end of the offer's validity window. `undefined` = no expiry. */
    expiry?: string;
    /** Optional fine print (e.g. "valid Mon-Thu, dine-in only"). */
    terms?: string;
}

export interface PointSource {
    source_type: string; // Example: "ChromeExtension" or "ProxiMap"
    source_url?: string; // For chrome extension, this would be the URL where the point was added. For copy from map, this would the
    source_name?: string; // Populated when copying points from one map to another
}


export interface GeoJsonGeometry {
    type: GeoJsonType;
    coordinates?:
    | [number, number]
    | Array<[number, number]>
    | Array<Array<[number, number]>>
    | Array<Array<Array<[number, number]>>>
    | GeoJsonGeometry[];
    geometries?: GeoJsonGeometry[];
}


export interface GeoJsonFeature {
    type: 'Feature';
    geometry: GeoJsonGeometry;
    properties: GeoJsonProperties;
}


export enum GeoJsonType {
    Point = 'Point',
    MultiPoint = 'MultiPoint',
    LineString = 'LineString',
    MultiLineString = 'MultiLineString',
    Polygon = 'Polygon',
    MultiPolygon = 'MultiPolygon',
    GeometryCollection = 'GeometryCollection',
}

export interface CustomShape {
    _id?: ObjectId;
    name?: string;
    description?: string;
    geojson: GeoJsonGeometry;
    style?: GeoJsonStyle;
    circle_details?: CircleDetails;
    show_in_filter?: boolean;
}


export interface ProcessingOutput {
    geometry: GeoJsonGeometry;
    circle_details?: CircleDetails;
}

export interface CircleDetails {
    radius_m: number;
    center: LatLng;
}

export enum ShapeType {
    Data = 'data',
    Static = 'static',
}

export interface CustomOverlay {
    _id?: ObjectId;
    image_url: string;
    position: OverlayPosition;
    opacity: number;
    show_in_filter?: boolean;
}

export interface DrawnOverlay extends CustomOverlay {
    google_overlay?: any;
}

export interface GetPointResponse {
    _id: ObjectId;
    email: string | undefined;
    topic_id: ObjectId;
    topic_category_id: ObjectId;
    location: Location;
    name: string | undefined;
    description: string | undefined;
    special_offer: SpecialOfferInformation | undefined;
    embedded_video: string | undefined;
    custom_icon_url: string | undefined;
    created_at: string;
    updated_at: string;
    active: boolean;
    featured: boolean;
    start: string | undefined;
    end: string | undefined;
    number: string | undefined;
    source: PointSource | undefined;
    is_gate_hidden: boolean;
    /** Opt-in allowlist for a gated Map's free preview (new viewer). */
    show_in_free_preview?: boolean;
    is_out_of_date: boolean;
    likes: string[] | undefined;
    url: string | undefined;
    opentable_info: OpenTableInformation | undefined;
    is_owner: boolean;
    is_writer: boolean;
    is_reader: boolean;
    write_link: string | undefined;
    /** Direct link to the new admin edit UI. Undefined for older points
     *  created before this field was introduced — admin sheet falls
     *  back to `write_link?new_experience=true`. */
    edit_link?: string;
    tag_info: TagInformation | undefined;
    custom_information: AdditionalPointDetails | undefined;
    custom_shape: CustomShape | undefined;
    directory_entry_id: ObjectId | undefined;
    collection_id: ObjectId | undefined;

    is_minimal: boolean;
}

// ─── Lists ──────────────────────────────────────────────────────────────────────

export type CollectionType = 'static' | 'synced';

export type CollectionContentType = 'locations' | 'events' | 'businesses';

export type CollectionSyncSourceType = 'topic';

export interface CollectionSyncSource {
    source_type: CollectionSyncSourceType;
    topic_id: ObjectId | undefined;
}

export interface CollectionResponse {
    _id: ObjectId;
    organization_id: ObjectId;
    name: string;
    description: string | undefined;
    collection_type: CollectionType;
    content_type: CollectionContentType;
    sync_source: CollectionSyncSource | undefined;
    topic_ids: ObjectId[];
    /** Collection-context custom field definitions. Empty by default;
     *  populated on Collection create or via subsequent edit. */
    custom_fields?: CustomFieldDefinition[];
    /** Set when public submissions are enabled on this Collection
     *  (`POST /collections/{id}/enable-submissions`). Points at the auto-
     *  created `Form`; preserved across disable/re-enable cycles. */
    submission_form_id?: ObjectId;
    /** Set when submissions are turned off; cleared on re-enable.
     *  Denormalized from `Form.disabled_at`. Effective "submissions are on" =
     *  `!!submission_form_id && !submissions_disabled_at`. */
    submissions_disabled_at?: string;
    /** Per-Collection saved view config for the admin's "places" table.
     *  Persisted column visibility + order so curators don't have to
     *  re-pick fields on every visit. Undefined = use the editor's
     *  default view (Name + Address [+ Organization when directory
     *  enabled]). */
    default_table_view?: DefaultTableView;
    created_at: string;
    updated_at: string;
}

/** Saved table view for a Collection's admin places table. The
 *  `column_keys` list drives both visibility (only listed columns
 *  render) AND order (left-to-right matches the list order). Each
 *  entry is one of:
 *    - `"name"` — built-in Name column
 *    - `"address"` — built-in Address column
 *    - `"organization"` — built-in Organization column (only meaningful
 *      when the workspace has Directory enabled)
 *    - any `CustomFieldDefinition._id` hex — renders the value of
 *      that custom field for each point
 */
export interface DefaultTableView {
    column_keys: string[];
}

// ─── Collection custom fields (Phase 6: Topic Split) ─────────────────────────

/** A custom field definition that lives on a `Collection`. Distinct from
 *  `DirectoryEntry` custom fields, which hold entity-wide metadata.
 *
 *  `field_type` is a snake_case wire value matching the Rust `FormFieldType`
 *  enum (which is `#[serde(rename_all = "snake_case")]`). Same set of values
 *  as the legacy `FormFieldType` enum further down this file — kept as
 *  `string` here to avoid coupling new code to that enum's identifier set. */
export interface CustomFieldDefinition {
    _id: ObjectId;
    label: string;
    field_key: string;
    /** Snake-case wire value: 'text' | 'email' | 'phone' | 'number' | 'date'
     *  | 'time' | 'date_time' | 'url' | 'textarea' | 'select' | 'multi_select' |
     *  'checkbox' | 'location' | 'rich_text' | 'photos' | 'image' | 'file' | 'deal' |
     *  'date_range' | 'address' | 'page_break' | 'section'. */
    field_type: string;
    order: number;
    description?: string;
    /** Choice list — only meaningful for `select` and `multi_select` field
     *  types. Each option carries optional visual metadata (`default_icon`,
     *  `default_marker_shape`) so a Map can pick one of the asset's
     *  select fields to drive marker icons. */
    options?: FieldOption[];
    /** Provenance of this definition when it appears in a Map's resolved
     *  field set. `'listing'` marks a field that was folded in from a linked
     *  listing (so the filter bar / markers can use it) rather than defined on
     *  the Collection itself. The point card treats listing-sourced fields as
     *  opt-in — they render only when the curator explicitly turns them on for
     *  the map. Absent / `'collection'` = a native Collection field. */
    field_source?: 'collection' | 'listing';
    /** Marks this `text` field as the source for the public point card's
     *  title. When set + populated, the card uses this field's value
     *  instead of `Point.name`. Empty value falls back to `Point.name`.
     *  At most one field per Collection should have this true. Render-time
     *  override only — does not modify `Point.name`. */
    is_canonical_title?: boolean;
    /** Marks this `rich_text` field as the source for the legacy
     *  `Point.description` synthesis path. */
    is_canonical_description?: boolean;
    /** Marks this `url` field as the source for the legacy `Point.url`
     *  ("More info" button) synthesis path. */
    is_canonical_more_info?: boolean;
    /** Marks this `photos` field as the canonical/legacy gallery for
     *  the entity. The legacy `Image` query path (no `field_id` filter)
     *  returns this field's images. */
    is_canonical_photos?: boolean;
    /** Marks this `date_range` field as the source for the legacy
     *  `Point.start` / `Point.end` synthesis path. */
    is_canonical_event_dates?: boolean;
    /** Marks this `address` field as the canonical/mapping address.
     *  By default the Map plots pins from this field's value (mirrored
     *  into `Point.location` for spatial-index compatibility). At most
     *  one field per Collection can have this true. */
    is_canonical_address?: boolean;
    /** Marks this `image` (single) field as the canonical icon source —
     *  read/writes `Point.custom_icon_url`. New Maps default
     *  `MapMarkers.icon_field_id` to this field so legacy
     *  `custom_icon_url` values render in the new viewer without any
     *  curator action. At most one field per Collection should have
     *  this true. */
    is_canonical_icon?: boolean;
    /** Maximum images allowed for a `photos` field. Undefined =
     *  unlimited. Use case: a Collection with separate "Outside" /
     *  "Inside" / "Food" photo fields each capped at 1. */
    max_photos?: number;
    /** Marks this field as required during create/edit and CSV import.
     *  Honored client-side today; server-side enforcement to follow. */
    is_required?: boolean;
    /** Marks this field as a unique identifier within the Collection —
     *  e.g. an "External ID" or "SKU" column. Surfaced as the default
     *  match-key candidate during upsert imports. */
    is_unique?: boolean;
    /** Regex source string (without delimiters or flags) used to validate
     *  `text`-family field values. Empty/undefined = no pattern check. */
    validation_pattern?: string;
    /** Minimum length for `text` / `textarea` / `rich_text` values. */
    min_length?: number;
    /** Maximum length for `text` / `textarea` / `rich_text` values. */
    max_length?: number;
    /** Minimum numeric value for `number` fields. */
    min?: number;
    /** Maximum numeric value for `number` fields. */
    max?: number;
    /** Archived = retained in the schema and all stored Point values are
     *  kept, but excluded from every public presentation of the asset
     *  the field is on (public submission form, public point card /
     *  popup, public edit form, public calendar). Admin surfaces still
     *  show it so a curator can restore it. Non-destructive replacement
     *  for delete; scoped to the owning asset. */
    archived?: boolean;
    created_at: string;
    updated_at: string;
}

/** One option on a `select` / `multi_select` field. Stable `_id` lets
 *  `Point.custom_information` references survive label edits + reorders.
 *  `default_icon` / `default_marker_shape` only render when a Map picks
 *  this field as its `icon_field_id`; otherwise they're inert metadata. */
export interface FieldOption {
    _id: ObjectId;
    label: string;
    description?: string;
    default_icon?: string;
    default_marker_shape?: NumberedIconShape;
    /** Whether the public add-place flow can target this option. Mirrors
     *  the legacy `TopicCategory.can_crowdsource` semantic. */
    can_crowdsource: boolean;
}

// ─── Forms (Phase 6: Topic Split) ────────────────────────────────────────────

export type FormPurpose =
    | 'lead_capture'
    | 'add_place'
    | 'challenge_registration'
    | 'event_rsvp'
    | 'donation'
    | 'generic';

/** Per-field visibility on a Form's `FormSpecField`.
 *  - `visible`: shown on the public form, editable.
 *  - `hidden` / `internal_only`: skipped on the public form. UI label is
 *    "Internal only"; both wire values mean the same thing today.
 *  - `locked`: rendered read-only on edit-flow URLs (e.g.
 *    `/p/[pointId]/edit`). Behaves like `visible` on create flows since
 *    there's no value to lock. */
export type FormSpecFieldVisibility = 'visible' | 'hidden' | 'internal_only' | 'locked';

export interface FormSpecField {
    key: string;
    label: string;
    /** Optional helper text rendered under the label. Maps to legacy
     *  `AddPointFieldCustomization.sub_header`. */
    description?: string;
    /** Same snake_case wire-value set as `CustomFieldDefinition.field_type`,
     *  plus `'section'` for display-only section heading blocks (no input,
     *  uses `label` as heading and `description` as sub-text), and
     *  `'page_break'` to split the form into pages with Next/Back nav
     *  in the public renderer. */
    field_type: string;
    required?: boolean;
    visibility?: FormSpecFieldVisibility;
    options?: string[];
    collection_field_id?: ObjectId;
    /** Marks this input as listing information. Reserved keys ("phone",
     *  "website", "hours") map to built-in listing columns; anything else
     *  matches a workspace listing field's `field_key`. Values are staged
     *  on the FormResponse until a reviewer applies them. */
    listing_field_key?: string;
    /** When set, marks this field as submitter info (vs place info)
     *  and routes its value into the captured Contact. Reserved keys
     *  map to canonical Contact columns:
     *    - "email"      → Contact.email
     *    - "name"       → Contact.display_name
     *    - "phone"      → Contact.phone_number
     *    - "first_name" → Contact.first_name
     *    - "last_name"  → Contact.last_name
     *  Any other value maps to a custom Contact property
     *  (OrgContactField.field_key). Presence (regardless of value)
     *  is the signal to the renderer to group this under "About you"
     *  at the end of the form. */
    contact_field_key?: string;
    /** Conditional show/hide rules. Outer array is AND-combined; inner
     *  array is OR-combined (CNF). When undefined or empty, the field
     *  always shows (subject to `visibility`). Hidden-by-rule fields
     *  are stripped from the submit payload (so existing stored values
     *  aren't overwritten on edit) and skipped by required-validation. */
    visibility_rules?: VisibilityRule[][];
    /** Archived fields stay in the spec so they can be restored (and so
     *  Collection-linked fields keep their link), but are excluded from
     *  the public renderer, page schema, submit payload, and
     *  required-field validation. Non-destructive replacement for hard
     *  delete. */
    archived?: boolean;
    /** Read-only marker stamped by the admin `getForm` endpoint when this
     *  field is bound to a canonical Collection custom field. Lets the
     *  client dedupe baseline twins exactly instead of guessing by
     *  `field_type`. Never sent on write. */
    canonical_role?: 'title' | 'description' | 'address';
}

export interface FormCopy {
    title: string;
    description?: string;
    cta_button: string;
    success_message: string;
}

export type ApprovalMode = 'automatic' | 'require_review';

export interface PlaceCollectionDestination {
    collection_id: ObjectId;
    approval: ApprovalMode;
    field_mapping?: Record<string, string>;
    location_field?: string;
}

export type PaymentMode = 'free' | 'suggested' | 'required' | 'tiered';

export interface FormPayment {
    mode: PaymentMode;
    amount_cents?: number;
    suggestion_amounts_cents?: number[];
    currency: string;
    stripe_product_id?: ObjectId;
    receipt_text?: string;
}

export type FormNotification =
    | { type: 'email'; to: string; subject_template?: string }
    | { type: 'webhook'; url: string; headers?: Record<string, string> };

export interface Form {
    _id: ObjectId;
    organization_id: ObjectId;
    name: string;
    /** Pretty address — the form also lives at `/forms/<slug>` when set. */
    slug?: string;
    /** Old addresses that still resolve here after a rename. */
    past_slugs?: string[];
    purpose: FormPurpose;
    fields: FormSpecField[];
    copy: FormCopy;
    /** Presentation only. Present on signup forms; absent elsewhere. */
    appearance?: FormAppearance;
    payment?: FormPayment;
    audience_id: ObjectId;
    place_destination?: PlaceCollectionDestination;
    /** Set when this form is a program's registration form. */
    program_id?: ObjectId;
    notifications?: FormNotification[];
    /** Soft-disable timestamp. `undefined` = active; set = disabled at this
     *  time. Re-enabling clears it. */
    disabled_at?: string;
    created_at: string;
    updated_at: string;
}

/** Which built-in look a public signup form wears. */
export type FormStyle = 'banner' | 'side_by_side' | 'simple';

export interface FormAppearance {
    style?: FormStyle;
    /** Overrides the organization's color for this form. */
    brand_color?: string;
    image?: string;
}

// EnableSubmissionsResponse / DisableSubmissionsResponse reference Audience,
// which lives in `apps/web/scripts/patch-client-types.mjs` (the patch script
// appends Audience + related types after the cp). So those response shapes
// are added in the patch alongside Audience, not here.

export interface CollectionWithPointCount extends CollectionResponse {
    point_count: number;
    connected_assets: ConnectedAsset[];
}

export interface ConnectedAsset {
    id: string;
    name: string;
    asset_type: string;
    is_new_model: boolean;
}

export interface CollectionDetailResponse extends CollectionResponse {
    point_count: number;
    connected_assets: ConnectedAsset[];
}

export interface PublicCollectionResponse {
    id: string;
    name: string;
    description: string | undefined;
    point_count: number;
}

export interface PaginatedPointsResponse {
    points: GetPointResponse[];
    total: number;
    limit: number;
    offset: number;
}

export interface ImportFromMapResponse {
    imported_count: number;
}

export enum StrokeStyle {
    Solid = 'solid',
    Dashed = 'dashed',
    Dotted = 'dotted',
    DashDot = 'dash_dot',
}

export interface GeoJsonStyle {
    stroke_weight?: number;
    stroke_color?: string;
    stroke_opacity?: number;
    fill_color?: string;
    fill_opacity?: number;
    stroke_style?: StrokeStyle;
}

export interface GeoJsonProperties {
    [x: string]: number | string | ObjectId | undefined;
}

export interface GetCheckinResponse {
    _id: ObjectId;
    email: string;
    location: Location;
    timestamp: string;
    topic_id: ObjectId;
}

export enum ChallengeType{
    Passport = 'passport',
    Hunt = 'hunt',
    Trivia = 'trivia',
    Guide = 'guide',
    Treasure = 'treasure',
}

export enum ChallengeDefaultTab {
    About = 'about',
    Checkins = 'checkins',
    Activities = 'activities',
    Rewards = 'rewards',
    Leaderboard = 'leaderboard',
    PhotoFeed = 'photo_feed',
    Share = 'share',
}

export interface GetChallengeResponse {
    _id: ObjectId;
    topic_id: ObjectId;
    organization_id: ObjectId | undefined;

    is_live: boolean;
    is_registration_active: boolean;
    challenge_type: ChallengeType;
    name: string;
    description: string;
    terms: string;
    start_time: string | undefined;
    end_time: string | undefined;

    locations: ChallengeLocation[];
    activities: ChallengeActivity[] | undefined;
    rewards: ChallengeReward[];

    should_verify_with_code: boolean; // no longer used remove after migration

    button_text: string;
    should_verify_check_ins: boolean;
    should_show_special_offers: boolean;
    /** Canonical name: true means special offers are hidden until check-in. */
    hide_special_offers_until_checkin?: boolean;
    can_view_challenge_after_end_date: boolean;
    show_leaderboard: boolean;
    show_photo_feed: boolean;
    default_tab: ChallengeDefaultTab;
    wallet_card_id: string | undefined;

    checkin_frequency_quantity: number;
    checkin_frequency_recurrance: 'daily' | 'weekly' | 'monthly' | 'once';

    geofence_radius_m: number | undefined;
    enable_qr_checkin: boolean;
    enable_code_checkin: boolean;
    enable_photo_checkin: boolean;
    photo_auto_approve: boolean;
    enable_receipt_checkin: boolean;
    receipt_auto_approve: boolean;

    photo_instructions: string | undefined;

    allow_internal_notes: boolean;

    registration_settings: RegistrationSettings | undefined;
    receipt_settings: ReceiptSettings | undefined;
    trivia_settings: TriviaSettings | undefined;
    scavenger_hunt_settings: ScavengerHuntSettings | undefined;

    payments: ChallengePayment[] | undefined;

    should_request_user_images: boolean;
    join_code: string | undefined;

    /** The Collection this challenge was created from, if any. */
    collection_id?: ObjectId;

    /** Pre-built registration URL from the server (topic cc_read_link + challenge ID). */
    registration_link?: string;
}

export interface ChallengeActivity {
    _id?: ObjectId;
    name: string;
    description: string;
    image_url: string;
    point_value: number;
    max_redemptions?: number;
    submission_type?: 'text' | 'photos' | 'url' | 'receipt';
    auto_approve?: boolean;
}

export interface GetActivatedChallengeResponse {
    _id: ObjectId;
    challenge_id: ObjectId;


    email?: string; // only available to topic writer,
    display_name?: string;
    phone_number?: string;
    additional_fields?: AdditionalActivatedChallengeFields[];

    checkin_count: number;
    point_count: number;
    // [ARoney] Is this supposed to be `ChallengeLocation`?
    checkin_locations?: Location[]; // only available to topic writer,
    checkins?: CheckIn[]; // only available to topic writer,
    counted_checkins?: CountedCheckIn[]; // only available to topic writer,

    earned_rewards: ChallengeReward[];
    redeemed_rewards: RewardRedemption[];// this is kept separate because awards get re-calculated on every checkin

    pending_checkin_location_ids?: string[];
    activities?: ActivatedMiscPointsActivity[];
    admin_assigned_points?: AdminAssignedPoint[];

    scavenger_hunt_use?: ScavengerHuntUse;

    images?: ActivatedChallengeImage[];
    /** Pending (not-yet-approved) check-in/activity images. Only sent to topic writers for the admin moderation queue. */
    pending_images?: ActivatedChallengeImage[];
    receipts?: ActivatedChallengeReceipt[];

    personal_locations?: PersonalLocations;

    hidden_from_leaderboard: boolean;
    payment_details?: ActivatedChallengePaymentDetails;

    is_tester: boolean;

    key_set?: ActivatedChallengeKeySet,
    link_set?: ActivatedChallengeLinkSet,

    referral_code?: string;

    reset_at?: string;

    created_at: string;
    updated_at: string;
}

export interface PersonalLocations {
    custom: PersonalLocation[];
    saved: ObjectId[];
    saved_at?: SavedLocationEntry[];
}

export interface SavedLocationEntry {
    _id: ObjectId;
    saved_at: string;
}

export interface PersonalLocation {
    _id: ObjectId;
    name: string;
    description: string;
    icon_url: string;
    location: Location;
}

export interface CreatePersonalLocationRequest {
    _id?: ObjectId;
    name: string;
    description: string;
    icon_url: string;
    location: CreateLocationRequest;
}

export interface ActivatedChallengePaymentDetails {
    payment_checkout_url?: string;
    payment_status: ChargebeeTransactionStatus;
    payment_transaction_id?: string;
    payment_amount?: number;
}

export interface RewardRedemption {
    _id: ObjectId;
    timestamp: string;
    redemption_type: RewardRedemptionType;
    admin_email: string | undefined;
}

export enum RewardRedemptionType {
    User = 'user',
    Admin = 'admin',
    Email = 'email',
}

export interface ScavengerHuntUse {
    hint_location_ids: ObjectId[];
    give_up_location_ids: ObjectId[];
}

export interface AdditionalActivatedChallengeFields {
    field_name: string;
    field_value: string;
}

export interface ActivatedMiscPointsActivity {
    activity_id: ObjectId;
    submission: string[];
    redemptions: number;
    approved: boolean;
    total_points: number;
}

interface AdminAssignedPoint {
    _id: ObjectId;
    description: string;
    value: number;
    created_at: string;
}

export interface ActivatedChallengeImage {
    _id: ObjectId;
    challenge_location_id: ObjectId;
    image_url: string;
    can_be_displayed: boolean,
    captured_at?: string;
    approved?: boolean;
}

export interface ActivatedChallengeReceipt {
    _id: ObjectId;
    challenge_location_id: ObjectId;
    image_url: string;
    total_spend_in_cents: number;
    currency: string;
    captured_at?: string;
    approved?: boolean;
}

export interface ActivatedChallengeKeySet {
    write_key_salt: string,
    write_key_hash: string,
    activated_challenge_key: string,
}

export interface ActivatedChallengeLinkSet {
    activated_challenge_map_write_link: string,
    activated_challenge_page_write_link: string,
}

export interface FeedPhotoCardDetails {
    activated_challenge_id: ObjectId;
    image_url: string;
    location_or_activity_id: ObjectId;
    captured_at: string;
    username: string;
}

export interface CreateChallengeCheckin {
    method: GeoLocationCheckIn
        | CodeCheckIn
        | LocationCheckIn
        | PhotoCheckIn
        | ReceiptCheckIn
        | ActivityCheckIn
        | QrCodeCheckIn;
}

export interface QrCodeCheckIn {
    QrCodeCheckIn: QrCodeCheckInDetails;
}

export interface QrCodeCheckInDetails {
    key: string;
}

export interface GeoLocationCheckIn {
    GeoLocationCheckIn: GeoLocationCheckInDetails;
}

export interface GeoLocationCheckInDetails {
    challenge_location_id: string;
    user_location: LatLng;
}

export interface CodeCheckIn {
    CodeCheckIn: CodeCheckInDetails;
}

export interface CodeCheckInDetails {
    code: string;
    timestamp_override?: string;
}

export interface LocationCheckIn {
    LocationCheckIn: LocationCheckInDetails;
}

export interface LocationCheckInDetails {
    challenge_location_id: string;
    code?: string;
    timestamp_override?: string;
}

export interface PhotoCheckIn {
    PhotoCheckIn: PhotoCheckInDetails;
}

export interface PhotoCheckInDetails {
    challenge_location_id: string;
    image_urls: string[],
    can_be_displayed: boolean,
}

export interface ReceiptCheckIn {
    ReceiptCheckIn: ReceiptCheckInDetails;
}

export interface ReceiptCheckInDetails {
    challenge_location_id: string;
    total_spend_in_cents: number;
    image_url: string;
    currency: string;
}

export interface ActivityCheckIn {
    ActivityCheckIn: ActivityCheckInDetails;
}

export interface ActivityCheckInDetails {
    activity_id: string;
    submission: string[],
    receipt_info?: ReceiptCheckInDetails;
    submission_type: 'text' | 'photos' | 'url' | 'receipt';
    can_be_displayed: boolean,
}

export interface ChallengeMessage {
    subject_line: string;
    message: string;
    send_date?: string;
    activated_challenge_ids?: string[];
}

export interface CreateMessageRequest {
    _id: ObjectId | undefined;
    entity_type: EventTargetType;
    entity_id: ObjectId;
    channel: MessageChannel;
    payload: MessagePayload;
    recipient_group: MessageRecipientGroup;
    run_at: RunType;
    is_draft?: boolean;
}

export interface GetMessageResponse {
    _id: ObjectId;
    entity_type: EventTargetType;
    entity_id: ObjectId;
    channel: MessageChannel;
    payload: MessagePayload;
    recipient_group: MessageRecipientGroup;
    run_at: RunType;
    sent_at?: string; // ISO 8601 date string, only present if the message has been sent
    successful_sends?: number;
    failed_sends?: FailedSend[];
    status: MessageStatus;
    /** Why the last send stopped before finishing, when it did. */
    last_error?: string;
    /** Stamped after every batch while a send is running. */
    last_progress_at?: string;
    created_at: string;
    updated_at: string;
}

export interface FailedSend {
    error: string;
    recipient: string;
}

export type RunType =
    'Immediate' | Scheduled | OnEvent;

export interface Scheduled {
    Scheduled: string; // ISO 8601 date string
}

export interface OnEvent {
    OnEvent: AppEvent;
};

export type AppEvent = ActivatedChallengeEvent | FormEvent | EmailCaptureEvent | AudienceEvent;
export type AppEventType = ActivatedChallengeEventType | FormEventType | EmailCaptureEventType;


export interface ActivatedChallengeEvent {
    ActivatedChallenge: ActivatedChallengeEventType;
}

export enum ActivatedChallengeEventType {
    Registration = 'registered',
    RewardEarned = 'reward_earned',
    LocationCheckin = 'location_checked_in',
    LocationSaved = 'location_saved',
    CheckinApproved = 'checkin_approved',
    ActivityApproved = 'activity_approved',
}

export interface FormEvent {
    Form: FormEventType;
}

export enum FormEventType {
    SubmissionCreated = 'submission_created',
    SubmissionApproved = 'submission_approved',
    SubmissionUpdated = 'submission_updated',
    SubmissionDeleted = 'submission_deleted',
}

export interface EmailCaptureEvent {
    EmailCapture: EmailCaptureEventType;
}

export enum EmailCaptureEventType {
    PopUpCapture = 'pop_up_captured',
    GateCapture = 'gate_captured',
    PointInquiryCapture = 'point_inquiry_captured',
}

export interface AudienceEvent {
    Audience: AudienceEventType;
}

// Deliberately NOT in AppEventType/eventMap: 'submission_approved' would
// collide with FormEventType's key in the flat record. Audience events are
// constructed directly ({ Audience: AudienceEventType.X }) where needed.
export enum AudienceEventType {
    ContactAdded = 'contact_added',
    SubmissionCreated = 'submission_created',
    SubmissionApproved = 'submission_approved',
    MembershipCanceled = 'membership_canceled',
    ChallengeRewardEarned = 'challenge_reward_earned',
    ChallengeCheckedIn = 'challenge_checked_in',
    ChallengeActivityCompleted = 'challenge_activity_completed',
    ChallengeVoteCast = 'challenge_vote_cast',
}

/** Backs the composer's live "this will reach N people" preview. */
export interface EstimateRecipientsRequest {
    audience_id: ObjectId;
    channel: MessageChannel;
    submission_status?: SubmissionSegment;
    challenge_segments?: ChallengeSegmentCondition[];
}

export interface EstimateRecipientsResponse {
    recipients: number;
    audience_total: number;
}

export const eventMap: Record<AppEventType, AppEvent> = {
    [ActivatedChallengeEventType.Registration]: { ActivatedChallenge: ActivatedChallengeEventType.Registration },
    [ActivatedChallengeEventType.RewardEarned]: { ActivatedChallenge: ActivatedChallengeEventType.RewardEarned },
    [ActivatedChallengeEventType.LocationCheckin]: { ActivatedChallenge: ActivatedChallengeEventType.LocationCheckin },
    [ActivatedChallengeEventType.LocationSaved]: { ActivatedChallenge: ActivatedChallengeEventType.LocationSaved },
    [ActivatedChallengeEventType.CheckinApproved]: { ActivatedChallenge: ActivatedChallengeEventType.CheckinApproved },
    [ActivatedChallengeEventType.ActivityApproved]: { ActivatedChallenge: ActivatedChallengeEventType.ActivityApproved },

    [FormEventType.SubmissionCreated]: { Form: FormEventType.SubmissionCreated },
    [FormEventType.SubmissionApproved]: { Form: FormEventType.SubmissionApproved },
    [FormEventType.SubmissionUpdated]: { Form: FormEventType.SubmissionUpdated },
    [FormEventType.SubmissionDeleted]: { Form: FormEventType.SubmissionDeleted },

    [EmailCaptureEventType.PopUpCapture]: { EmailCapture: EmailCaptureEventType.PopUpCapture },
    [EmailCaptureEventType.GateCapture]: { EmailCapture: EmailCaptureEventType.GateCapture },
    [EmailCaptureEventType.PointInquiryCapture]: { EmailCapture: EmailCaptureEventType.PointInquiryCapture },
};

export interface CreateMessageResponse {
    _id: ObjectId;
    run_at: RunType;
    status?: MessageStatus;
}

export interface UpdateMessageRequest {
    channel?: MessageChannel;
    payload?: MessagePayload;
    recipient_group?: MessageRecipientGroup;
    run_at?: RunType;
    is_draft?: boolean;
}

export type MessageRecipientGroup =
    'ActivatedChallengeParticipants' |
    'CheckinCountZero' |
    'SaveCountZero' |
    ActivatedChallengeLocationVisitorsType |
    ActivatedChallengeRewardRecipientsType |
    ActivatedChallengeActivityCompletersType |
    ActivatedChallengeLocationSaversType |
    CheckinCountFewerThanType |
    CheckinCountMoreThanType |
    SaveCountFewerThanType |
    SaveCountMoreThanType |
    FormSubmissionOwnersType |
    FormSubmissionEmailFieldType |
    AudienceRecipientGroupType;

export enum MessageRecipientGroupType {
    ActivatedChallengeParticipants = 'activated_challenge_participants',
    ActivatedChallengeLocationVisitors = 'activated_challenge_location_visitors',
    ActivatedChallengeRewardRecipients = 'activated_challenge_reward_recipients',
    ActivatedChallengeActivityCompleters = 'activated_challenge_activity_completers',
    FormSubmissionOwners = 'form_submission_owners',
    FormSubmissionEmailField = 'form_submission_email_field',
    ActivatedChallengeLocationSavers = 'activated_challenge_location_savers',
    CheckinCountZero = 'checkin_count_zero',
    CheckinCountFewerThan = 'checkin_count_fewer_than',
    CheckinCountMoreThan = 'checkin_count_more_than',
    SaveCountZero = 'save_count_zero',
    SaveCountFewerThan = 'save_count_fewer_than',
    SaveCountMoreThan = 'save_count_more_than',
    Audience = 'audience',
}

export interface ActivatedChallengeLocationVisitorsType {
    ActivatedChallengeLocationVisitors: { challenge_location_id : ObjectId },
}

export interface ActivatedChallengeRewardRecipientsType {
    ActivatedChallengeRewardRecipients: { reward_id: ObjectId },
}

export interface ActivatedChallengeActivityCompletersType {
    ActivatedChallengeActivityCompleters: { activity_id: ObjectId },
}

export interface ActivatedChallengeLocationSaversType {
    ActivatedChallengeLocationSavers: { point_id: ObjectId },
}

export interface FormSubmissionOwnersType {
    FormSubmissionOwners: {include_admins: boolean},
}

export interface FormSubmissionEmailFieldType {
    FormSubmissionEmailField: { custom_field_id: ObjectId },
}

export interface CheckinCountFewerThanType {
    CheckinCountFewerThan: { threshold: number },
}

export interface CheckinCountMoreThanType {
    CheckinCountMoreThan: { threshold: number },
}

export interface SaveCountFewerThanType {
    SaveCountFewerThan: { threshold: number },
}

export interface SaveCountMoreThanType {
    SaveCountMoreThan: { threshold: number },
}

export interface AudienceRecipientGroupType {
    Audience: { audience_id: ObjectId, submission_status?: SubmissionSegment, challenge_segments?: ChallengeSegmentCondition[], listing_scope?: ListingScope, member_level?: MemberLevelSegment },
}

/** Narrows listing contacts to the places belonging to one asset. Mirror of
 *  the Rust `ListingScope`; the next kind of scope is one more member. */
export type ListingScope =
    | { Experience: { challenge_id: ObjectId } }
    | { SponsorshipPackage: { program_id: ObjectId, product_id?: ObjectId } };

/** One "only send to" condition on an experience audience — matched against
 *  live participant records at send time. Conditions on a message AND together. */
export enum ChallengeSegmentKind {
    CheckedIn = 'checked_in',
    EarnedReward = 'earned_reward',
    CompletedActivity = 'completed_activity',
    PostedPhoto = 'posted_photo',
    Voted = 'voted',
    Rated = 'rated',
    RedeemedReward = 'redeemed_reward',
}

export interface ChallengeSegmentCondition {
    kind: ChallengeSegmentKind;
    /** Flips the condition: "has not checked in", "has not voted", ... */
    negate?: boolean;
    /** A specific reward/activity/stop; absent means "any". */
    target_id?: ObjectId;
    /** Minimum count for checked_in (distinct places); defaults to 1. */
    threshold?: number;
}

// create a map tying MessageRecipientGroupType to MessageRecipientGroup so we can easily look up the group by type
export const recipientGroupMap: Record<MessageRecipientGroupType, MessageRecipientGroup> = {
    [MessageRecipientGroupType.ActivatedChallengeParticipants]: 'ActivatedChallengeParticipants',
    [MessageRecipientGroupType.ActivatedChallengeLocationVisitors]: { ActivatedChallengeLocationVisitors: { challenge_location_id: { $oid: '' } } },
    [MessageRecipientGroupType.ActivatedChallengeLocationSavers]: { ActivatedChallengeLocationSavers: { point_id: { $oid: '' } } },
    [MessageRecipientGroupType.ActivatedChallengeRewardRecipients]: { ActivatedChallengeRewardRecipients: { reward_id: { $oid: '' } } },
    [MessageRecipientGroupType.ActivatedChallengeActivityCompleters]: { ActivatedChallengeActivityCompleters: { activity_id: { $oid: '' } } },
    [MessageRecipientGroupType.CheckinCountZero]: 'CheckinCountZero',
    [MessageRecipientGroupType.CheckinCountFewerThan]: { CheckinCountFewerThan: { threshold: 1 } },
    [MessageRecipientGroupType.CheckinCountMoreThan]: { CheckinCountMoreThan: { threshold: 1 } },
    [MessageRecipientGroupType.SaveCountZero]: 'SaveCountZero',
    [MessageRecipientGroupType.SaveCountFewerThan]: { SaveCountFewerThan: { threshold: 1 } },
    [MessageRecipientGroupType.SaveCountMoreThan]: { SaveCountMoreThan: { threshold: 1 } },
    [MessageRecipientGroupType.FormSubmissionOwners]: { FormSubmissionOwners: { include_admins: false } },
    [MessageRecipientGroupType.FormSubmissionEmailField]: { FormSubmissionEmailField: { custom_field_id: { $oid: '' } } },
    [MessageRecipientGroupType.Audience]: { Audience: { audience_id: { $oid: '' } } },
};

export enum EventTargetType {
    ActivatedChallenge = 'activated_challenge',
    Form = 'form',
    EmailCapture = 'email_capture',
    Audience = 'audience',
}

export enum MessageChannel {
    Email = 'email',
    SMS = 'sms',
}

export enum MessageSendStatus {
    Sent = 'sent',
    Failed = 'failed',
}

export interface MessageUsageResponse {
    emails_sent: number;
    emails_limit: number;
    sms_sent: number;
    sms_limit: number;
    email_credits?: number;
    sms_credits?: number;
    month: string;
    messaging_state?: MessagingState;
}

export interface MessagePackStatusResponse {
    eligible: boolean;
    lookups_eligible: boolean;
    email_credits: number;
    sms_credits: number;
    lookup_credits: number;
    email_pack_unit: number;
    sms_pack_unit: number;
    lookup_pack_unit: number;
}

export enum UsagePackKind {
    Email = 'email',
    Sms = 'sms',
    BusinessLookups = 'business_lookups',
}

export interface MessagePackChangeRequest {
    pack: UsagePackKind;
    quantity: number;
}

export interface ContactActivityResponse {
    audiences: ContactAudienceMembership[];
    events: ContactEventEntry[];
    messages: ContactMessageSendEntry[];
}

export interface ContactAudienceMembership {
    audience_id: ObjectId;
    name: string;
    source_type?: AutoAudienceSourceType;
}

export interface ContactEventEntry {
    event: AppEvent;
    audience_id?: ObjectId;
    source_name?: string;
    created_at: string;
}

export interface ContactMessageSendEntry {
    message_id: ObjectId;
    subject?: string;
    channel: MessageChannel;
    status: MessageSendStatus;
    created_at: string;
}

export enum AutoAudienceSourceType {
    ChallengeRegistrants = 'challenge_registrants',
    ChallengeLocationVisitors = 'challenge_location_visitors',
    ChallengeRewardRecipients = 'challenge_reward_recipients',
    TopicEmailCaptures = 'topic_email_captures',
    TopicGatedAccess = 'topic_gated_access',
    TopicFormSubmissions = 'topic_form_submissions',
    EventGroupSubmitters = 'event_group_submitters',
    EventRsvpAttendees = 'event_rsvp_attendees',
    BulletinBoardSubmitters = 'bulletin_board_submitters',
    FormSubmissions = 'form_submissions',
    ProgramMembers = 'program_members',
    ListingContacts = 'listing_contacts',
    OrganizationChallengeParticipants = 'organization_challenge_participants',
    OrganizationChallengeRewardEarners = 'organization_challenge_reward_earners',
    SponsorshipPayers = 'sponsorship_payers',
    ElectionVoters = 'election_voters',
}

export enum MessageStatus {
    Draft = 'draft',
    Pending = 'pending',
    Scheduled = 'scheduled',
    Sent = 'sent',
    Failed = 'failed',
    PendingReview = 'pending_review',
    AutomationActive = 'automation_active',
    AutomationDisabled = 'automation_disabled',
}

/** Whether a workspace can send messages yet. */
export enum MessagingState {
    Held = 'held',
    Approved = 'approved',
    Blocked = 'blocked',
}

export type MessagePayload = ActivatedChallengeMessagePayload |
    FormMessagePayload |
    AudienceMessagePayload;


export interface BasePayload {
    subject: string;
    body: string;
    accent_color: string;
    button_text_color: string;
    reply_to: string | undefined;
}

export enum EmailStyle {
    Announcement = 'announcement',
    Letter = 'letter',
    Promo = 'promo',
    Notification = 'notification',
}

export interface EmailCtaButton {
    title: string;
    link: string;
}

export interface AudienceMessagePayload {
    Audience: AudiencePayload;
}

export interface AudiencePayload extends BasePayload {
    style: EmailStyle;
    cover_image: string;
    cta?: EmailCtaButton;
    include_source_button?: boolean;
    /** Used in place of a name in personalization tokens (default "there"). */
    name_fallback?: string;
    /** Page background behind the white card. Empty = accent color. */
    background_color?: string;
    /** Header logo override. Empty = the organization's logo. */
    logo?: string;
    /** Resolved server-side at send time — per recipient for personal kinds. */
    standard_button?: StandardButtonKind;
    /** Unified button list. When non-empty, fully replaces the legacy
     *  cta/source_button/standard_button trio. */
    buttons?: MessageButton[];
}

/** A button: an author-controlled label plus a destination that's either a
 *  per-recipient smart link (`standard`) or a static `url` (`standard` wins). */
export interface MessageButton {
    label: string;
    standard?: StandardButtonKind;
    url?: string;
}

/** Standard buttons offered per audience type; resolved server-side. */
export enum StandardButtonKind {
    SubmissionLink = 'submission_link',
    PortalSignIn = 'portal_sign_in',
    EventPage = 'event_page',
    Calendar = 'calendar',
    ProgramPage = 'program_page',
    ChallengeLink = 'challenge_link',
}

/** Recipient segment for form/event-source audiences. */
export type SubmissionSegment = 'approved' | 'pending';

/** Which slice of a program's members a message goes to. Levels match on what
 *  someone holds now, so a message aimed at a lower level stops reaching
 *  anyone who has since moved up. */
export type MemberLevelSegment =
    | { level: { level_id: ObjectId } }
    | 'no_level'
    | 'lapsed';

export interface ActivatedChallengeMessagePayload {
    ActivatedChallenge: ActivatedChallengePayload
}

export interface FormMessagePayload {
    Form: FormPayload;
}

// ─── Reusable Templates ──────────────────────────────────────────────────────
// Saved, reusable templates. The envelope (name/owner/type/category) is shared
// across asset kinds; only `content` is typed per kind. Message is the only kind
// today; Map/Form/etc. slot in later as new TemplateContent members.

export enum TemplateType {
    Message = 'message',
}

/** Externally-tagged union — mirrors the Rust TemplateContent enum. */
export type TemplateContent = MessageTemplateContentWrapper;

export interface MessageTemplateContentWrapper {
    Message: MessageTemplateContent;
}

/** The portable core of a message template (subject/body/colors) plus optional
 *  email-only styling that's used wherever the target asset supports it. */
export interface MessageTemplateContent {
    channel: MessageChannel;
    subject: string;
    body: string;
    accent_color: string;
    button_text_color: string;
    reply_to?: string;
    style?: EmailStyle;
    cover_image?: string;
    background_color?: string;
}

export interface Template {
    _id: ObjectId;
    /** Owning workspace. Absent for Proxi-provided (public) templates. */
    organization_id?: ObjectId;
    name: string;
    description?: string;
    template_type: TemplateType;
    content: TemplateContent;
    category?: string;
    tags: string[];
    created_at: string;
    updated_at: string;
}

export interface CreateTemplateRequest {
    name: string;
    description?: string;
    template_type: TemplateType;
    content: TemplateContent;
    category?: string;
    tags?: string[];
}

export interface UpdateTemplateRequest {
    name?: string;
    description?: string;
    content?: TemplateContent;
    category?: string;
    tags?: string[];
}

export interface ActivatedChallengePayload extends BasePayload {
    include_unique_link: boolean;
}

export interface FormPayload extends BasePayload {
    include_unique_link: boolean;
    include_map_admins: boolean;
}


// Template definitions for different message types and states
export interface MessageTemplate {
    automated: {
      eventDescription: string;
      recipientDescription: string;
    };
    scheduled: {
      recipientDescription: string;
    };
    sent: {
      description: string;
    };
  }

  // Function to get dynamic parts of message template
  export type MessageTemplateFunction<T> = (params: T) => string;

  // Template configuration for all message types
  export const MESSAGE_TEMPLATES: Record<EventTargetType, {
    automated: Array<{
        dropdown_text: string;
        recipient_group: MessageRecipientGroupType;
        exclude_list?: ChallengeType[]; // for making it not show up in certain situations
        eventDescription: string;
        event: ActivatedChallengeEventType | FormEventType | EmailCaptureEventType;
        default_email_payload: BasePayload;
        default_sms_payload: BasePayload;
        recipientDescription: string | MessageTemplateFunction<any>;
    }>;
    scheduled: Array<{
        dropdown_text: string;
        exclude_list?: ChallengeType[]; // for making it not show up in certain situations
        recipient_group: MessageRecipientGroupType;
        recipientDescription: MessageTemplateFunction<any> | string;
        default_email_payload: BasePayload;
        default_sms_payload: BasePayload;
    }>;
  }> = {
    [EventTargetType.ActivatedChallenge]: {
    automated: [
        {
        dropdown_text: 'Automated: When A User Registers',
        recipient_group: MessageRecipientGroupType.ActivatedChallengeParticipants,
        eventDescription: 'When a user registers',
        event: ActivatedChallengeEventType.Registration,
        default_email_payload: {
            subject: 'Welcome!',
            body: `
            <h1>Welcome!</h1>
            <p>
                Thank you for registering. We're excited to have you join our community!
                <br><br>
                Get ready for fun activities, rewards, and new experiences.
                <br><br>
                If you have any questions, reply to this email or visit our page for more details.
                <br><br>
                Good luck, and let the adventure begin!
            </p>
            `,
            accent_color: '#4CAF50',
            button_text_color: '#FFFFFF',
            reply_to: undefined,
        },
        default_sms_payload: {
            subject: 'Registration Confirmation Automated Text',
            body: 'Thank you for registering. We\'re excited to have you join our community! Get ready for fun activities, rewards, and new experiences.',
            accent_color: '#4CAF50',
            button_text_color: '#FFFFFF',
            reply_to: undefined,
        },
        recipientDescription: 'to any user when they register for the challenge'
        },
        {
        dropdown_text: 'Automated: When A User Earns a Reward',
        recipient_group: MessageRecipientGroupType.ActivatedChallengeRewardRecipients,
        eventDescription: 'When a user earns a reward',
        event: ActivatedChallengeEventType.RewardEarned,
        exclude_list: [ChallengeType.Guide], // exclude this option for guide challenges
        default_email_payload: {
            subject: 'Congratulations! You Earned a Reward',
            body: `
            <h1>Congratulations!</h1>
            <p>
                You've just earned a reward for your dedication and effort.
                <br><br>
                Enjoy your reward and keep striving for more achievements!
                <br><br>
                Check your dashboard for more opportunities.
                <br><br>
                Celebrate your success and share it with friends!
            </p>
            `,
            accent_color: '#FFD700',
            button_text_color: '#FFFFFF',
            reply_to: undefined,
        },
        default_sms_payload: {
            subject: 'Reward Confirmation Automated Text',
            body: 'Congratulations! You\'ve just earned a reward for your dedication and effort. Enjoy your reward and keep striving for more achievements.',
            accent_color: '#FFD700',
            button_text_color: '#FFFFFF',
            reply_to: undefined,
        },
        recipientDescription:  (params: { rewardName: string; }) => `to any registered user when they earn the reward ${params.rewardName}`,
        },
        {
        dropdown_text: 'Automated: When A User Checks In at a Location',
        recipient_group: MessageRecipientGroupType.ActivatedChallengeLocationVisitors,
        event: ActivatedChallengeEventType.LocationCheckin,
        exclude_list: [ChallengeType.Trivia],
        eventDescription: 'When a user checks in at a location',
        default_email_payload: {
            subject: 'Check-In Confirmed!',
            body: `
            <h1>Thanks for Checking In!</h1>
            <p>
                Your visit has been recorded.
                <br><br>
                Keep exploring more locations to unlock new rewards and achievements.
                <br><br>
                Don't forget to share your experience with the community!
            </p>
            `,
            accent_color: '#2196F3',
            button_text_color: '#FFFFFF',
            reply_to: undefined,
        },
        default_sms_payload: {
            subject: 'Check-In Confirmation Automated Text',
            body: 'Your visit has been recorded. Keep exploring more locations to unlock new rewards and achievements.',
            accent_color: '#2196F3',
            button_text_color: '#FFFFFF',
            reply_to: undefined,
        },
        recipientDescription: (params: { locationName: string; }) => `to any registered user when they check in at the location ${params.locationName}`,
        },
        {
        dropdown_text: 'Automated: When A User Saves a Location',
        recipient_group: MessageRecipientGroupType.ActivatedChallengeLocationSavers,
        event: ActivatedChallengeEventType.LocationSaved,
        exclude_list: [ChallengeType.Trivia, ChallengeType.Passport, ChallengeType.Hunt, ChallengeType.Treasure],
        eventDescription: 'When a user saves a location',
        default_email_payload: {
            subject: 'Save Confirmed!',
            body: `
            <h1>Thanks for Saving!</h1>
            <p>
                Your save has been recorded.
                <br><br>
                Keep exploring more locations to unlock new rewards and achievements.
                <br><br>
                Don't forget to share your experience with the community!
            </p>
            `,
            accent_color: '#2196F3',
            button_text_color: '#FFFFFF',
            reply_to: undefined,
        },
        default_sms_payload: {
            subject: 'Save Confirmation Automated Text',
            body: 'Your save has been recorded. Keep exploring more locations to unlock new rewards and achievements.',
            accent_color: '#2196F3',
            button_text_color: '#FFFFFF',
            reply_to: undefined,
        },
        recipientDescription: (params: { locationName: string; }) => `to any registered user when they save the location ${params.locationName}`,
        },
        {
        dropdown_text: 'Automated: When A Check-In Is Approved',
        recipient_group: MessageRecipientGroupType.ActivatedChallengeParticipants,
        event: ActivatedChallengeEventType.CheckinApproved,
        exclude_list: [ChallengeType.Guide, ChallengeType.Trivia],
        eventDescription: 'When an admin approves a pending check-in',
        default_email_payload: {
            subject: 'Your Check-In Has Been Approved!',
            body: `
            <h1>Check-In Approved!</h1>
            <p>
                Great news — your check-in has been reviewed and approved.
                <br><br>
                Your progress has been updated. Keep exploring and checking in to earn more rewards!
                <br><br>
                Check your dashboard to see your updated status.
            </p>
            `,
            accent_color: '#4CAF50',
            button_text_color: '#FFFFFF',
            reply_to: undefined,
        },
        default_sms_payload: {
            subject: 'Check-In Approved Automated Text',
            body: 'Great news — your check-in has been reviewed and approved! Your progress has been updated. Keep exploring to earn more rewards!',
            accent_color: '#4CAF50',
            button_text_color: '#FFFFFF',
            reply_to: undefined,
        },
        recipientDescription: 'to the participant whose check-in was just approved',
        },
        {
        dropdown_text: 'Automated: When An Activity Is Approved',
        recipient_group: MessageRecipientGroupType.ActivatedChallengeParticipants,
        event: ActivatedChallengeEventType.ActivityApproved,
        exclude_list: [ChallengeType.Guide, ChallengeType.Trivia],
        eventDescription: 'When an admin approves a pending activity submission',
        default_email_payload: {
            subject: 'Your Activity Submission Has Been Approved!',
            body: `
            <h1>Activity Approved!</h1>
            <p>
                Great news — your activity submission has been reviewed and approved.
                <br><br>
                Your progress has been updated. Keep completing activities to earn more rewards!
                <br><br>
                Check your dashboard to see your updated status.
            </p>
            `,
            accent_color: '#4CAF50',
            button_text_color: '#FFFFFF',
            reply_to: undefined,
        },
        default_sms_payload: {
            subject: 'Activity Approved Automated Text',
            body: 'Great news — your activity submission has been reviewed and approved! Your progress has been updated. Keep going to earn more rewards!',
            accent_color: '#4CAF50',
            button_text_color: '#FFFFFF',
            reply_to: undefined,
        },
        recipientDescription: 'to the participant whose activity submission was just approved',
        },

    ],

    scheduled: [
        {
        dropdown_text: 'All Registered Users',
        recipient_group: MessageRecipientGroupType.ActivatedChallengeParticipants,
        recipientDescription: 'to all users who are registered as part of the challenge',
        default_email_payload: {
            subject: 'Passport Update: New Opportunities Await!',
            body: `
            <h1>Exciting Updates!</h1>
            <p>
                Hello Challenger,<br><br>
                We have some exciting news and updates for you! New activities, rewards, and locations have been added.
                <br><br>
                Make sure to check your dashboard and stay engaged for a chance to win exclusive prizes.
                <br><br>
                Keep up the great work and let us know if you have any feedback!
            </p>
            `,
            accent_color: '#FF9800',
            button_text_color: '#FFFFFF',
            reply_to: undefined,
        },
        default_sms_payload: {
            subject: 'Registration Bulk Text',
            body: 'Congratulations! You\'ve just earned a reward for your dedication and effort. Enjoy your reward and keep striving for more achievements.',
            accent_color: '#FFD700',
            button_text_color: '#FFFFFF',
            reply_to: undefined,
        },
    },
        {
        dropdown_text: 'All Users Who Have Earned A Certain Reward',
        recipient_group: MessageRecipientGroupType.ActivatedChallengeRewardRecipients,
        recipientDescription: (params: { rewardName: string; }) => `to any registered user who has so far earned the reward ${params.rewardName}`,
        default_email_payload: {
            subject: 'Congratulations on Earning a Reward!',
            body: `
            <h1>You Did It!</h1>
            <h2>Here’s Your Well-Earned Reward</h2>
            <p>
                Congratulations!<br><br>
                Your effort is paying off — you’ve unlocked a reward for your progress.
                <br><br>
                Keep pushing forward, and more rewards are on the horizon!
            </p>
            `,
            accent_color: '#FF9800',
            button_text_color: '#FFFFFF',
            reply_to: undefined,
        },
        exclude_list: [ChallengeType.Guide], // exclude this option for guide challenges
        default_sms_payload: {
            subject: 'Reward Bulk Text',
            body: 'Congratulations! You\'ve just earned a reward for your dedication and effort. Enjoy your reward and keep striving for more achievements.',
            accent_color: '#FFD700',
            button_text_color: '#FFFFFF',
            reply_to: undefined,
        },
    },
        {
        dropdown_text: 'All Users Who Have Visited a Location',
        recipient_group: MessageRecipientGroupType.ActivatedChallengeLocationVisitors,
        recipientDescription: (params: { locationName: string; }) => `to any registered user who has checked in at the location ${params.locationName}`,
        default_email_payload: {
            subject: 'Thanks for Visiting!',
            body: `
            <h1>Thank You for Your Visit!</h1>
            <p>
                Your check-in has been recorded.
                <br><br>
                Keep exploring and checking in at more locations to unlock new rewards and experiences.
                <br><br>
                We appreciate your participation!
            </p>
            `,
            accent_color: '#FF9800',
            button_text_color: '#FFFFFF',
            reply_to: undefined,
        },
        default_sms_payload: {
            subject: 'Location Check-In Bulk Text',
            body: 'Your check-in has been recorded. Keep exploring and checking in at more locations to unlock new rewards and experiences.',
            accent_color: '#FF9800',
            button_text_color: '#FFFFFF',
            reply_to: undefined,
        },
    },
        {
        dropdown_text: 'All Users Who Have Saved a Location',
        recipient_group: MessageRecipientGroupType.ActivatedChallengeLocationSavers,
        exclude_list: [ChallengeType.Trivia, ChallengeType.Passport, ChallengeType.Hunt, ChallengeType.Treasure],
        recipientDescription: (params: { locationName: string; }) => `to any registered user who has saved the location ${params.locationName}`,
        default_email_payload: {
            subject: 'Thanks for Saving!',
            body: `
            <h1>Thank You for Your Save!</h1>
            <p>
                Your save has been recorded.
                <br><br>
                Keep exploring and saving more locations to unlock new rewards and experiences.
                <br><br>
                We appreciate your participation!
            </p>
            `,
            accent_color: '#FF9800',
            button_text_color: '#FFFFFF',
            reply_to: undefined,
        },
        default_sms_payload: {
            subject: 'Location Save Bulk Text',
            body: 'Your save has been recorded. Keep exploring and saving more locations to unlock new rewards and experiences.',
            accent_color: '#FF9800',
            button_text_color: '#FFFFFF',
            reply_to: undefined,
        },
    },
    {
        dropdown_text: 'Users With Zero Check-Ins',
        recipient_group: MessageRecipientGroupType.CheckinCountZero,
        exclude_list: [ChallengeType.Guide, ChallengeType.Trivia],
        recipientDescription: 'to all registered users who have not checked in at any location yet',
        default_email_payload: {
            subject: "Don't Miss Out — Start Exploring!",
            body: `
            <h1>Ready to Get Started?</h1>
            <p>
                You signed up, but you haven't checked in at any locations yet!
                <br><br>
                Head out and visit your first location to start earning points and unlocking rewards.
                <br><br>
                The adventure is waiting for you!
            </p>
            `,
            accent_color: '#FF5722',
            button_text_color: '#FFFFFF',
            reply_to: undefined,
        },
        default_sms_payload: {
            subject: 'Zero Check-Ins Nudge',
            body: "You signed up but haven't checked in yet! Head out and visit your first location to start earning rewards.",
            accent_color: '#FF5722',
            button_text_color: '#FFFFFF',
            reply_to: undefined,
        },
    },
    {
        dropdown_text: 'Users With Fewer Than X Check-Ins',
        recipient_group: MessageRecipientGroupType.CheckinCountFewerThan,
        exclude_list: [ChallengeType.Guide, ChallengeType.Trivia],
        recipientDescription: (params: { threshold: number }) => `to all registered users with fewer than ${params.threshold} check-ins`,
        default_email_payload: {
            subject: 'Keep Going — More Locations Await!',
            body: `
            <h1>You're Making Progress!</h1>
            <p>
                You've started checking in, but there are still more locations to discover.
                <br><br>
                Keep exploring to unlock new rewards and climb the leaderboard!
            </p>
            `,
            accent_color: '#FF9800',
            button_text_color: '#FFFFFF',
            reply_to: undefined,
        },
        default_sms_payload: {
            subject: 'Check-In Nudge',
            body: "You've started checking in — keep going! There are more locations to discover and rewards to unlock.",
            accent_color: '#FF9800',
            button_text_color: '#FFFFFF',
            reply_to: undefined,
        },
    },
    {
        dropdown_text: 'Users With More Than X Check-Ins',
        recipient_group: MessageRecipientGroupType.CheckinCountMoreThan,
        exclude_list: [ChallengeType.Guide, ChallengeType.Trivia],
        recipientDescription: (params: { threshold: number }) => `to all registered users with more than ${params.threshold} check-ins`,
        default_email_payload: {
            subject: "You're on Fire!",
            body: `
            <h1>Amazing Progress!</h1>
            <p>
                You've been checking in like a pro!
                <br><br>
                Thank you for being such an active participant. Keep it up — there may be more rewards ahead!
            </p>
            `,
            accent_color: '#4CAF50',
            button_text_color: '#FFFFFF',
            reply_to: undefined,
        },
        default_sms_payload: {
            subject: 'Power User Thanks',
            body: "You've been checking in like a pro! Thank you for being such an active participant.",
            accent_color: '#4CAF50',
            button_text_color: '#FFFFFF',
            reply_to: undefined,
        },
    },
    {
        dropdown_text: 'Users With Zero Saves',
        recipient_group: MessageRecipientGroupType.SaveCountZero,
        exclude_list: [ChallengeType.Trivia, ChallengeType.Passport, ChallengeType.Hunt, ChallengeType.Treasure],
        recipientDescription: 'to all registered users who have not saved any locations yet',
        default_email_payload: {
            subject: 'Start Building Your List!',
            body: `
            <h1>Your Personal Guide Awaits</h1>
            <p>
                You signed up, but you haven't saved any locations yet!
                <br><br>
                Browse the map and save places that interest you to build your personal guide.
            </p>
            `,
            accent_color: '#FF5722',
            button_text_color: '#FFFFFF',
            reply_to: undefined,
        },
        default_sms_payload: {
            subject: 'Zero Saves Nudge',
            body: "You signed up but haven't saved any locations yet! Browse the map and start building your personal guide.",
            accent_color: '#FF5722',
            button_text_color: '#FFFFFF',
            reply_to: undefined,
        },
    },
    {
        dropdown_text: 'Users With Fewer Than X Saves',
        recipient_group: MessageRecipientGroupType.SaveCountFewerThan,
        exclude_list: [ChallengeType.Trivia, ChallengeType.Passport, ChallengeType.Hunt, ChallengeType.Treasure],
        recipientDescription: (params: { threshold: number }) => `to all registered users with fewer than ${params.threshold} saved locations`,
        default_email_payload: {
            subject: 'More Great Places to Discover!',
            body: `
            <h1>Keep Exploring!</h1>
            <p>
                You've started saving locations — great start!
                <br><br>
                There are more amazing places waiting to be added to your personal guide.
            </p>
            `,
            accent_color: '#FF9800',
            button_text_color: '#FFFFFF',
            reply_to: undefined,
        },
        default_sms_payload: {
            subject: 'Save Nudge',
            body: "You've started saving locations — keep going! There are more amazing places to add to your guide.",
            accent_color: '#FF9800',
            button_text_color: '#FFFFFF',
            reply_to: undefined,
        },
    },
    {
        dropdown_text: 'Users With More Than X Saves',
        recipient_group: MessageRecipientGroupType.SaveCountMoreThan,
        exclude_list: [ChallengeType.Trivia, ChallengeType.Passport, ChallengeType.Hunt, ChallengeType.Treasure],
        recipientDescription: (params: { threshold: number }) => `to all registered users with more than ${params.threshold} saved locations`,
        default_email_payload: {
            subject: "You're a Super Explorer!",
            body: `
            <h1>What a Collection!</h1>
            <p>
                You've saved an impressive number of locations to your guide.
                <br><br>
                Thank you for being so engaged! We hope you enjoy visiting all these amazing places.
            </p>
            `,
            accent_color: '#4CAF50',
            button_text_color: '#FFFFFF',
            reply_to: undefined,
        },
        default_sms_payload: {
            subject: 'Power Saver Thanks',
            body: "You've saved an impressive number of locations! Thank you for being so engaged.",
            accent_color: '#4CAF50',
            button_text_color: '#FFFFFF',
            reply_to: undefined,
        },
    }
    ]
    },
    [EventTargetType.Form]: {
    automated: [
        {
        dropdown_text: 'Automated: A Point or Form is Submitted',
        recipient_group: MessageRecipientGroupType.FormSubmissionOwners,
        eventDescription: 'When a user submits a point or form',
        event: FormEventType.SubmissionCreated,
        default_email_payload: {
            subject: 'Thank You for Your Submission!',
            body: `
            <h1>Submission Received</h1>
            <p>
                Thank you for submitting your entry.<br><br>
                We appreciate your input and will review your submission as soon as possible.
                <br><br>
                If you have any questions or need to make changes, please reply to this email.
                <br><br>
                Have a great day!
            </p>
            `,
            accent_color: '#4CAF50',
            button_text_color: '#FFFFFF',
            reply_to: undefined,
        },
        default_sms_payload: {
            subject: 'Submission Confirmation Automated Text',
            body: 'Thank you for your submission. We appreciate your input and will review your submission as soon as possible.',
            accent_color: '#4CAF50',
            button_text_color: '#FFFFFF',
            reply_to: undefined,
        },
        recipientDescription: (params: { includeAdmin: boolean; pointName: string }) =>
        `when any user ${params.includeAdmin ? '' : 'who is not a map admin'} submits a ${params.pointName}`,},
        {
        dropdown_text: 'Automated: A Submission Is Approved',
        recipient_group: MessageRecipientGroupType.FormSubmissionOwners,
        eventDescription: 'When an admin approves a submitted point',
        event: FormEventType.SubmissionApproved,
        default_email_payload: {
            subject: 'Your Submission Has Been Approved!',
            body: `
            <h1>Submission Approved!</h1>
            <p>
                Great news — your submission has been reviewed and approved. It is now live and visible to everyone.
                <br><br>
                Thank you for contributing! You can view or edit your submission using the link below.
                <br><br>
                If you have any questions, feel free to reply to this email.
            </p>
            `,
            accent_color: '#4CAF50',
            button_text_color: '#FFFFFF',
            reply_to: undefined,
        },
        default_sms_payload: {
            subject: 'Submission Approved Automated Text',
            body: 'Great news — your submission has been reviewed and approved! It is now live and visible to everyone. Thank you for contributing!',
            accent_color: '#4CAF50',
            button_text_color: '#FFFFFF',
            reply_to: undefined,
        },
        recipientDescription: (params: { includeAdmin: boolean; pointName: string }) =>
        `when an admin approves a ${params.pointName} submitted by ${params.includeAdmin ? 'any user' : 'a non-admin user'}`,}
    ],

    scheduled: [
        {
        dropdown_text: 'All Point Submitters / Owners',
        recipient_group: MessageRecipientGroupType.FormSubmissionOwners,
        recipientDescription: (params: { includeAdmin: boolean; pointName: string }) =>
          `all users ${params.includeAdmin ? '' : 'who are not a map admins'} that have submitted a ${params.pointName}`,
        default_email_payload: {
            subject: 'Your Contribution Has Been Received!',
            body: `
            <h1>Thank You for Your Contribution!</h1>
            <p>
                We have received your submission.
                <br><br>
                You can edit or update your entry at any time using the link below.
                <br><br>
                Your participation helps us improve and grow our community.
                <br><br>
                Thank you for being an active contributor!
            </p>
            `,
            accent_color: '#FF9800',
            button_text_color: '#FFFFFF',
            reply_to: undefined,
        },
        default_sms_payload: {
            subject: 'New Submission Confirmation Automated Text',
            body: 'Your contribution has been received. Thank you for being an active participant in our community!',
            accent_color: '#FF9800',
            button_text_color: '#FFFFFF',
            reply_to: undefined,
        },
    },
        {
        dropdown_text: 'Emails in a Custom Field',
        recipient_group: MessageRecipientGroupType.FormSubmissionEmailField,
        recipientDescription: (params: { customFieldName: string }) => `all emails in the custom field ${params.customFieldName}`,
        default_email_payload: {
            subject: 'You are listed as a contact for a point',
            body: `
            <h1>Contact Information for a Point</h1>
            <p>
                You are listed as a contact for a point on our map.
                <br><br>
                If you have any questions or need to update your information, please reply to this email.
                <br><br>
                Thank you for being part of our community!
            </p>
            `,
            accent_color: '#FF9800',
            button_text_color: '#FFFFFF',
            reply_to: undefined,
        },
        default_sms_payload: {
            subject: 'Contact Information for a Point Automated Text',
            body: 'You are listed as a contact for a point on our map. If you have any questions or need to update your information, please reply to this message.',
            accent_color: '#FF9800',
            button_text_color: '#FFFFFF',
            reply_to: undefined,
        },
    }
    ]
    },

    [EventTargetType.EmailCapture]: {
    automated: [],
    scheduled: []
    },

    [EventTargetType.Audience]: {
    automated: [],
    scheduled: []
    }
};

export interface GetAddOnResponse {
    _id: ObjectId;
    title: string;
    subtitle: string;
    description: string;
    icon_path: string; // these are all custom icons, not from material
    availability: AddOnAvailability;
    required_entitlements?: FeatureType[];
    component: AddOnType;
    gated?: boolean;
    owner: string;
    video_demo_link?: string;
    documentation_link?: string;
    support_message?: string;
    example_map_ids?: ObjectId[];
    is_featured: boolean;
    is_public: boolean;
    marketing_page_link?: string;
    associated_blogs?: string[];
}

export interface GetEmailCaptureResponse {
    _id: ObjectId;
    topic_id: ObjectId;
    email: string;
    ip_address: string;
    timestamp: string;
    text: EmailCaptureText;
    point_id?: string;
    name?: string;
    additional_info?: string;
    pop_up_id?: ObjectId;
}

export interface EmailCaptureWithTopic extends GetEmailCaptureResponse {
    capture_topic_id: string;
    capture_topic_name: string;
}

export interface EmailCaptureText {
    title: string;
    description: string;
    cta: string;
}

// everything except the most important parts because those are a little different in HOT and Creating the request
// most of these are things that are optional in the request
// when adding here make sure you add to createDummyPoint
export interface CreatePointBase {
    email: string | undefined;
    topic_id: ObjectId;
    name: string | undefined;
    description: string | undefined;
    special_offer: SpecialOfferInformation | undefined;
    embedded_video: string | undefined;
    custom_icon_url: string | undefined;
    url: string | undefined;
    active: boolean | undefined;
    featured: boolean | undefined;
    start: string | undefined;
    end: string | undefined;
    number: string | undefined;
    source: PointSource | undefined;
    is_gate_hidden: boolean | undefined;
    /** Opt-in allowlist for a gated Map's free preview (new viewer). */
    show_in_free_preview?: boolean;
    custom_information: AdditionalPointDetails | undefined;
    // these two can actually not be present b/c it makes it easier to go frm get point response to create point request
    custom_shape: CustomShape | undefined;
    url_images?: uploadImageUrlRequest[];
}

export interface HOTRow extends CreatePointBase {
    _id: ObjectId;
    is_new: boolean | undefined;
    topic_id: ObjectId;
    topic_category_id: ObjectId | undefined;
    location: Location | undefined;
    tag_info: TagInformation | undefined;
    created_at: string | undefined;
    updated_at: string | undefined;
}

// Make sure you update HOT row or else spreadsheet updates will overwrite your changes
export interface CreatePointRequest extends CreatePointBase {
    _id: ObjectId | undefined;
    topic_id: ObjectId;
    topic_category_id: ObjectId;
    location: CreateLocationRequest;
    tag_info: AddTagRequest;
    // is nomination is only used client side to know which API to call
    is_nomination?: boolean;
}

export interface AdditionalPointDetails {
    custom_field_entries?: PointFieldEntry[];
    passthrough_parameters?: PointPassthroughParameter[];
}

export interface PointFieldEntry {
    field_id: ObjectId;
    /** Used by single-value field types (text, email, select, rich_text, etc.). */
    field_value?: string;
    /** Used by `multi_select` fields. Each entry is an option `_id` hex. */
    field_values?: string[];
    /** Structured value for field types that don't fit a flat string —
     *  `photos` (`{ images: [{ image_id, caption? }, ...] }`),
     *  `deal` (`{ body, expiry?, terms? }`), `date_range`
     *  (`{ start, end? }`). */
    field_value_json?: unknown;
    /** Read-time marker: value is inherited from the linked listing, not
     *  stored on the point. Never include such entries in a save payload. */
    from_listing?: boolean;
}

export interface PointListingSyncResponse {
    overridden_keys: string[];
    /** The linked listing's public field values — prefill hints for the point editor. */
    listing_field_entries?: PointFieldEntry[];
}

export interface PointPassthroughParameter {
    key: string;
    value: string;
}


export interface uploadImageUrlRequest {
    name: string;
    url: string;
}

export interface CreateCheckinRequest {
    location: Location;
    topic_id: ObjectId;
}

export interface CreateEmailCaptureRequest {
    email: string;
    ip_address: string;
    text: EmailCaptureText;
    point_id?: string;
    name?: string;
    additional_info?: string;
    pop_up_id?: ObjectId;
}

export interface CreateGatedAccessEventRequest {
    email?: string;
    ip_address: string;
    gate_type: TopicGateType;
    paywall_gate_id?: ObjectId;
    email_gate_id?: ObjectId;
    email_verification_code?: string;
    password?: string;
    name?: string;
    transaction_id: string | undefined;
    transaction_amount?: number;
    transaction_percent_to_creator?: number;
}

export interface CreateGatedAccessEventResponse {
    _id: ObjectId;
}
export interface GetGatedAccessEventResponse {
    _id: ObjectId;
    topic_id: ObjectId;
    gate_id: ObjectId;
    email?: string;
    ip_address: string;
    timestamp: string;
    gate_type: TopicGateType;
    gate_name?: string;
    name?: string;
    transaction_id?: string;
    transaction_amount?: number;
    transaction_percent_to_creator?: number;
}

export interface GatedAccessData {
    accesses: GetGatedAccessEventResponse[];
    paywall_accesses: GetGatedAccessEventResponse[];
    password_accesses: GetGatedAccessEventResponse[];
    email_accesses: GetGatedAccessEventResponse[];
    total_earned: number;
}

export enum TopicGateType {
    Paywall = 'paywall',
    Password = 'password',
    Email = 'email',
}

export interface CreateChallengeRequest {
    is_live: boolean;
    is_registration_active: boolean;
    challenge_type: ChallengeType;

    name: string;
    description: string;
    terms: string;
    start_time: string | undefined;
    end_time: string | undefined;
    locations: CreateChallengeLocationRequest[];
    activities: ChallengeActivity[] | undefined;
    rewards: CreateChallengeRewardRequest[];

    button_text: string;
    should_verify_check_ins: boolean;
    should_show_special_offers: boolean;
    /** Canonical name: true means special offers are hidden until check-in. */
    hide_special_offers_until_checkin?: boolean;
    can_view_challenge_after_end_date: boolean;
    show_leaderboard: boolean;
    show_photo_feed: boolean;
    default_tab: ChallengeDefaultTab;
    wallet_card_id: string | undefined;

    should_verify_with_code: boolean; // no longer used but kept until migration is complete

    checkin_frequency_quantity: number;
    checkin_frequency_recurrance: 'daily' | 'weekly' | 'monthly' | 'once' | 'unlimited';

    geofence_radius_m: number | undefined;
    enable_qr_checkin: boolean;
    enable_code_checkin: boolean;
    enable_photo_checkin: boolean;
    photo_auto_approve: boolean;
    enable_receipt_checkin: boolean;
    receipt_auto_approve: boolean;

    photo_instructions: string | undefined;

    allow_internal_notes: boolean;

    should_request_user_images: boolean | undefined;
    scavenger_hunt_settings: ScavengerHuntSettings | undefined;
    trivia_settings: TriviaSettings | undefined;
    registration_settings: RegistrationSettings | undefined;
    receipt_settings: ReceiptSettings | undefined;

    join_code: string | undefined;
}

export interface ChallengePayment {
    email: string;
    activated_challenge_id: ObjectId;
    invoice_id: string;
    payment_status: ChargebeeTransactionStatus;
    payment_amount: number; // in cents
    payment_currency: string; // e.g., "USD"
    percent_to_organization: number; // in percentage
}

export enum ChargebeeTransactionStatus {
    Pending = 'Pending',
    Success = 'Success',
    Failed = 'Failed',
    Cancelled = 'Cancelled',
    AdminOverridden = 'AdminOverridden', // for when the admin says the user doesn't have to
}

export type RegistrationPaymentDetails = 'Free' | RequiredPaymentDetails | OptionalPaymentDetails;

export interface OptionalPaymentDetails {
    Optional: {
        suggested_amounts: number[]; // in cents
        currency: string; // e.g., "USD"
    };
}
export interface RequiredPaymentDetails {
    Required: {
        amount: number; // in cents
        currency: string; // e.g., "USD"
    };
}

export interface TriviaSettings {
    manual_progression: boolean;
    show_correct_answer_before_next_question: boolean;
    seconds_per_question: number;
    max_number_of_attempts: number;
}

export interface ScavengerHuntSettings {
    display_one_clue_at_a_time: boolean;
    hint_use_deduction: number;
    give_up_behavior: 'skip_location' | 'go_to_location';
}


export interface RegistrationSettings {
    heading: string;
    description: string | undefined;
    button_label: string;
    logo_url: string | undefined;
    show_logo_background: boolean;
    cover_image_url: string | undefined;
    email_required: boolean;
    show_email: boolean;
    phone_required: boolean;
    show_phone: boolean;
    show_username?: boolean;
    username_required: boolean;
    opt_in_required: boolean;
    additional_fields?: RegistrationField[];
    payment_details: RegistrationPaymentDetails | undefined;
}

export interface RegistrationField {
    label: string;
    field_type: FormFieldType;
    required: boolean;
    options?: string[];
}

export interface ReceiptSettings {
    currency: string;
    instructions?: string;
}

export interface ChallengeLocation {
    _id: ObjectId | undefined;
    location: Location | undefined;
    value: number;
    is_active: boolean;
    hide_from_leaderboard?: boolean;
    check_in_code: string | undefined;
    qr_code_key: string | undefined;
    can_geofence_checkin: boolean | undefined;
    allow_multiple_visits: boolean | undefined;
    visit_cadence: 'daily' | 'weekly' | 'monthly' | 'once' | undefined;
    max_visits: number | undefined;
    clue_details: LocationClueDetails | undefined;
}

export interface ChallengeReward {
    _id: ObjectId | undefined;
    title: string;
    description: string;
    image_url: string;
    reward_criteria: RewardCriteria;
    reward_criteria_threshold: number;
    completion_message: string | undefined;
    completion_url: string | undefined;
    selected_category_or_tag_id: ObjectId | undefined;
    selected_location_id: ObjectId | undefined;
    redeemable: boolean;
    bonus_points?: number;
}

export interface CreateChallengeLocationRequest {
    _id?: ObjectId | undefined;
    location: Location | undefined;
    value: number;
    is_active: boolean;
    hide_from_leaderboard?: boolean;
    check_in_code: string | undefined;
    qr_code_key: string | undefined;
    can_geofence_checkin: boolean | undefined;
    allow_multiple_visits: boolean | undefined;
    visit_cadence: 'daily' | 'weekly' | 'monthly' | 'once' | undefined;
    max_visits: number | undefined;
    clue_details: LocationClueDetails | undefined;
}

export interface LocationClueDetails {
    clue: string;
    image_url: string;
    hint: string;
}

export interface CreateChallengeRewardRequest {
    _id?: ObjectId | undefined;
    title: string;
    description: string;
    image_url: string;
    reward_criteria: RewardCriteria;
    reward_criteria_threshold: number;
    completion_message: string | undefined;
    completion_url?: string;
    selected_category_or_tag_id: ObjectId | undefined;
    selected_location_id: ObjectId | undefined;
    redeemable?: boolean;
    bonus_points?: number;
}

export enum RewardCriteria {
    AllPoints = 'all_points',
    AllPlaces = 'all_places',
    NumberOfPoints = 'number_of_points',
    NumberOfPlaces = 'number_of_places',
    VisitsToLocation = 'visits_to_location',
    VisitsToAnyLocation = 'visits_to_any_location',
    VisitsToAllLocations = 'visits_to_all_locations',
    VerifiedSpend = 'verified_spend',
}

export interface ActivatedChallengeApprovalDetails {
    type: 'activity' | 'location';
    username?: string;
    email?: string;
    phone_number?: string;
    location_activity_name: string;
    location_activity_id: string;
    submission: [string, string[]];
    activated_challenge_id: string;
    activity_submission_type?: 'text' | 'photos' | 'url' | 'receipt';
    /** Pre-formatted, currency-aware spend amount for receipt submissions (e.g. "$12.00", "£9.50"). */
    spend_label?: string;
}
export interface CreatePointsResponse {
    failed_geocodes: CreatePointRequest[];
    failed_validations: CreatePointValidationError[];
    updated_point_ids: string[],
}

export interface CreatePointValidationError {
    message: string;
    point_request: CreatePointRequest;
}

export interface CreatePointResponse {
    _id: ObjectId;
    write_key: string;
    read_key: string;
}

export interface CreateTopicResponse {
    _id: ObjectId;
    write_key: string;
    addpoint_key: string;
    read_key: string;
    cc_account_admin_link: string;
    cc_write_link: string;
    cc_addpoint_link: string;
    cc_read_link: string;
    cc_keyless_link: string;
    should_use_keyless_links: boolean;
    view_link?: string;
}

export interface CreateFolderResponse {
    _id: ObjectId;
}

export interface CreateOrganizationResponse {
    _id: ObjectId;
}

export interface CreateCheckinResponse {
    _id: ObjectId;
    timestamp: string;
    topic_id: ObjectId;
    location: Location;
}

// slightly different to allow for use when user is not logged in and has key
export interface CheckIn {
    topic_id?: ObjectId;
    _id?: ObjectId;
    timestamp?: string;
    location: Location;
}

export interface CountedCheckIn {
    _id: ObjectId;
    location_id: ObjectId;
    email: string;
    timestamp: string;
    period_key: string; // e.g. "2023-10-01" for daily, "2023-10" for monthly
    point_value: number; // the number of points awarded for this checkin
}

export interface CreateChallengeResponse {
    _id: ObjectId;
}

export interface CreateActivatedChallengeResponse {
    _id: ObjectId;
    created_at: string;
    email: string;
    link_set: ActivatedChallengeLinkSet;
    key_set: ActivatedChallengeKeySet;
    ac_write_key: string;
}

export interface CreateActivatedChallengeRequest {
    challenge_id: ObjectId;
    email?: string;
    phone_number?: string;
    sms_opted_out: boolean;
    display_name?: string;
    additional_fields?: AdditionalActivatedChallengeFields[];
    referred_by: string | undefined;
}

export interface UpdateActivatedChallengeRequest {
    email?: string;
    phone_number?: string;
    display_name?: string;
    hidden_from_leaderboard?: boolean;
    is_tester?: boolean;
    trivia_attempts?: TriviaAttempt[];
    additional_fields?: AdditionalActivatedChallengeFields[];
}

export interface TriviaAttempt {
    challenge_location_id: ObjectId;
    submission: string;
    correct?: boolean;
}

export interface CreateAddOnResponse {
    _id: ObjectId;
}

export interface CreateImageRequest {
    name: string;
    data: string;
    original_orientation?: number;
    new_orientation?: number;
}

export interface CreateAddOnRequest {
    title: string;
    subtitle: string;
    description: string;
    icon_path: string; // these are all custom icons, not from material
    availability: AddOnAvailability;
    required_entitlements?: FeatureType[];
    component: AddOnType;
    gated?: boolean;
    owner: string;
}

export interface Image {
    _id: ObjectId;
    entity_id: ObjectId;
    entity_type: EntityType;
    name: string;
    sas_url: string;
    sas_expiration: string;
    created_at: string;
    /** Which `CustomFieldDefinition` (a `photos` field) this image
     *  belongs to. `undefined` = canonical/legacy gallery (preserves
     *  pre-Phase-1 behavior). Curators who add a second `photos` field
     *  to a Collection get its `_id` stamped here on uploads. */
    field_id?: ObjectId;
    /** Display order within the gallery — lower first. Set in batch via
     *  `PUT /api/points/<point_id>/images/_reorder`. Existing rows
     *  deserialize with `0` (no migration); ties fall back to
     *  `created_at` for stable rendering. */
    order?: number;
}

export interface ImageUpload {
    file: File;
    orientation?: number;
    rotate_value?: number;
}

export interface ImageWithType {
    image: string;
    type: ImageType;
}

export enum ImageType {
    Google = 'GOOGLE',
    User = 'USER',
    Internal = 'INTERNAL',
    Embed = 'EMBED'
}

export interface GetGoogleKeyResponse {
    key: string;
}

export interface ToastSate {
    opened: boolean;
    text: string;
}

export interface TopLevelMarkerHolder {
    halo?: TopLevelMarkerHolder;
    iconUrl?: string;
    zIndex?: number;
    id: string;
    inner: Marker;
    point: GetPointResponse;
    customShape?: TopLevelShapeHolder;
    focusDivs?: IconFocusDivs;
    badges?: Badge[];
    map?: google.maps.Map;
}

export interface Badge {
    name: string;
    icon: string;
    filterText?: string;
}

export interface IconFocusDivs {
    aMBackgroundCircle?: HTMLElement;
    aMTextBelow?: HTMLElement;
    mBackgroundOverlay?: any;
    mTextBelowOverlay?: any;
}

export interface TopLevelShapeHolder {
    shape: CustomShape,
    inner: google.maps.Polygon | google.maps.Polyline | Array<google.maps.Polygon | google.maps.Polyline> | undefined;
    id: string | undefined,
}

export interface WebError {
    status: number;
    message: string;
    backtrace: string | undefined;
}

export interface Chip {
    _id: ObjectId | undefined;
    name: string;
    icon: string;
}

export enum ActivePanel {
    None = 'NONE',
    CategoriesTags = 'CATEGORIES_TAGS',
    MapStyle = 'MAP_STYLE',
    AdvancedFeatures = 'ADVANCED_FEATURES',
    PointSettings = 'POINT_SETTINGS',
    MapSettings= 'MAP_SETTINGS',
    IconSettings= 'ICON_SETTINGS',
    ShareSettings = 'SHARE_SETTINGS',
    CollabSettings = 'COLLAB_SETTINGS',
    Upload = 'UPLOAD',
    PointsList = 'POINTS_LIST',
    Publish = 'PUBLISH',
    BlogToMapAI = 'BLOG_TO_MAP_AI',
    Challenge = 'CHALLENGE',
    Guide = 'GUIDE',
    ZapierIntegration = 'ZAPIER_INTEGRATION',
    Translation = 'TRANSLATION',
    Election = 'ELECTION',
    AddOns = 'ADD_ONS',
    Insights = 'INSIGHTS',
    Passwords = 'PASSWORDS',
    Paywalls = 'PAYWALLS',
    EmailGate = 'EMAIL_GATE',
    PointEmailCapture = 'POINT_EMAIL_CAPTURE',
    PopUpEmailCapture = 'POP_UP_EMAIL_CAPTURE',
    KmlUpload = 'KML_UPLOAD',
    AiPointGeneration = 'AI_POINT_GENERATION',
    Comments = 'COMMENTS',
    ScavengerHunt = 'SCAVENGER_HUNT',
    IntegrationAutomation = 'INTEGRATION_AUTOMATION',
    TreasureHunt = 'TREASURE_HUNT',
}

export enum MapDrawer {
    MapInfo = 'MAP_INFO',
    PointCardList = 'POINT_CARD_LIST',
    AddPoint = 'ADD_POINT',
    EditPoint = 'EDIT_POINT',
    ChallengeCheckIn = 'CHALLENGE_CHECK_IN',
    ChallengeProgress = 'CHALLENGE_PROGRESS',
    ElectionBallot = 'ELECTION_BALLOT',
    AdvancedFiltering = 'ADVANCED_FILTERING',
    EmailCaptureMobile = 'EMAIL_CAPTURE',
    EditShape = 'EDIT_SHAPE_MODAL',
    EditGeoJson = 'EDIT_GEO_JSON',
    ChallengeAwardAnnouncement = 'CHALLENGE_AWARD_ANNOUNCEMENT',
    ChallengeUncheck = 'CHALLENGE_UNCHECK',
    PointShare = 'POINT_SHARE',
    AddPointsToMap = 'ADD_POINTS_TO_MAP',
    GetRouteFromPoints = 'GET_ROUTE_FROM_POINTS',
    SendToPhone = 'SEND_TO_PHONE',
    SendToPhoneHomescreen = 'SEND_TO_PHONE_HOMESCREEN',
    ShareMap = 'SHARE_MAP',
    OverlayEditor = 'OVERLAY_EDITOR',
    Nomination = 'NOMINATION',
    PostSubmissionPopUpMobile = 'POST_SUBMISSION_POP_UP',
}

export enum MapPopUp {
    EmailCaptureDesktop = 'EMAIL_CAPTURE',
    PostSubmissionPopUpDesktop = 'POST_SUBMISSION_POP_UP',
}

export interface TelemetryEntry {
    _id: ObjectId;
    metric_key: ObjectId;
    start_time: string;
    window_minutes: number;
    value: number;
    attributes: any;
}

/** Org-wide engagement rollup returned by
 *  `GET /api/organizations/<org_id>/engagement?<organization_key>`.
 *  Totals come from pre-aggregated `Topic.statistics` (12h freshness);
 *  leaderboards from raw `point_field_clicks` (near-real-time). */
export interface OrganizationEngagement {
    topic_count: number;
    directory_entry_count: number;
    total_topic_views: number;
    total_topic_renders: number;
    total_topic_maplibre_renders: number;
    total_point_field_clicks: number;
    top_topics: OrganizationTopicRollup[];
    top_directory_entries: OrganizationDirectoryEntryRollup[];
    top_fields: DirectoryEntryFieldRollup[];
}

export interface OrganizationTopicRollup {
    topic_id: ObjectId;
    topic_name: string;
    views: number;
    field_clicks: number;
}

export interface OrganizationDirectoryEntryRollup {
    directory_entry_id: ObjectId;
    directory_entry_name: string;
    point_count: number;
    point_views: number;
    field_clicks: number;
}

/** All-time vendor engagement rollup returned by
 *  `GET /api/directory_entries/<org>/engagement?<directory_entry_id>`.
 *  Aggregates point views + per-button clicks across every Point the
 *  vendor's `DirectoryEntry` is currently linked to. Powers the
 *  per-vendor insights panel in the directory entry sheet. */
export interface DirectoryEntryEngagement {
    point_count: number;
    total_point_views: number;
    total_field_clicks: number;
    field_clicks_by_field: DirectoryEntryFieldRollup[];
    by_point: DirectoryEntryPointRollup[];
}

export interface DirectoryEntryFieldRollup {
    field_id: string;
    field_type: string;
    field_header: string;
    action_kind: string;
    count: number;
}

export interface DirectoryEntryPointRollup {
    point_id: ObjectId;
    point_name: string;
    topic_id: ObjectId;
    topic_name?: string;
    point_views: number;
    field_clicks: number;
}

/** One row per (point, field, action_kind) returned by
 *  `GET /api/topics/<id>/point_field_clicks`. Powers the per-button
 *  breakdown on the activity dashboard so curators can see e.g. "View
 *  Menu got 312 clicks, Book a Table got 47" instead of one aggregated
 *  number. */
export interface PointFieldClickRollup {
    point_id: ObjectId;
    field_id: string;
    field_type: string;
    field_header: string;
    action_kind: string;
    count: number;
}

export interface RenderGroup {
    start_date: string;
    end_date: string;
    total_renders: number;
    renders: TelemetryEntry[];
}

export interface SearchIcon {
    id: ObjectId;
    commonName: string;
    platform: string;
}

export interface EmbedResponse {
    topic_id: ObjectId;
    addpoint_embed: string;
    read_embed: string;
}

export interface SearchBounds {
    east: number;
    west: number;
    north: number;
    south: number;
}

export type SearchFilterFn = (p: GetPointResponse) => boolean;

export type FilterName = 'ListSearch' | 'DateSearch' | 'FilterChip' | 'SpecialOffer' | 'ElectionFilter' | 'CheckInFilter' | 'LikeFilter' | 'CategoryFilter' | 'TagFilter' | 'DistanceFilter'
export interface FilterFunction {
    name: FilterName;
    filterFn: SearchFilterFn;
}

// Type for CreatePointRequests used in bulk registration
export type BulkRegPointRequest = CreatePointRequest & {
    bulkCounter: number;
    catName: string;
};

export interface PointLocationInputEvent {
    name?: string;
    location: string;
}

export interface SettingsChangeEvent {
    updatedValue: any; // this will be the type of the corresponding field in CreateTopicRequest
    field: keyof CreateTopicRequest;
    subField?: string;
    refreshMarkers?: boolean;
    refreshPage?: boolean;
    callback?: () => Promise<void>;
}

export interface AccountInformation {
    email: string;
    secret: string;
    device_name: string;
    expires_at: string;
}

export interface CreateApiKeyRequest {
    access_level: ApiAccessLevel;
}

export enum ApiAccessLevel {
    Read = 'read',
    Write = 'write',
}

export interface ApiKeyInfo {
    id: ObjectId;
    access_level: ApiAccessLevel;
    created_at: string;
    expires_at: string;
}

export interface Bookmark {
    topic_id: ObjectId;
    topic_key: string | undefined;
}

export interface CreateAccountRequest {
    _id: ObjectId;
    email: string;
    full_name: string | undefined;
    bookmarks: Bookmark[];
    followed_profiles: ObjectId[];
    phone_number: string | undefined;
    image_url: string | undefined;
    migrated_from_personal: boolean | undefined;
}

export interface GetAccountResponse {
    _id: ObjectId;
    email: string;
    full_name: string | undefined;
    bookmarks: Bookmark[];
    followed_profiles: ObjectId[];
    chargebee_subscription?: ChargebeeSubscriptionInfo;
    enabled_features: SubscriptionFeature[];
    ai_tool_uses: AccountAiToolUsage | undefined;
    routes_uses: AccountRoutesUsage | undefined;
    appsumo_license?: AppSumoLicense;
    legacy_account: boolean;
    phone_number: string | undefined;
    image_url: string | undefined;
    migrated_from_personal: boolean;
    game_progress?: GameProgress;
    referral_code?: string;
}

export interface GameProgress {
    city_challenge_stats?: CityChallengeStats;
    custom_data?: { [key: string]: any };
}

export interface CityChallengeStats {
    total_games_played: number;
    total_wins: number;
    current_streak: number;
    longest_streak: number;
    last_played_date?: string;
    daily_scores: { [date: string]: { won: boolean; guesses: number; city: string } };
}

export interface AppSumoLicense {
    appsumo_id: string;
    appsumo_plan_id: AppSumoTier | undefined;
    appsumo_email: string;
    appsumo_invoice_item_id: string | undefined;
}

export enum AppSumoTier {
    AppSumoTier1 = 'proxi_tier1',
    AppSumoTier2 = 'proxi_tier2',
    AppSumoTier3 = 'proxi_tier3',
    AppSumoFree = 'proxi_free',
}


export interface AccountAiToolUsage {
    num_ai_uses_month:  number,
    ai_use_startdate: string,
}

export interface AccountRoutesUsage {
    num_routes_uses_month:  number,
    routes_use_startdate: string,
}

export interface ChargebeeSubscriptionInfo {
    subscription_id: string;
    customer_id: string;
    plan_id: string;
    activated_at: string | undefined;
    billing_period: number | undefined;
    billing_period_unit: string | undefined;
    created_at: string | undefined;
    current_term_end: string | undefined;
    due_invoices_count: number | undefined;
    due_since: string | undefined;
    mrr_total: number | undefined;
    next_billing_at: string | undefined;
    remaining_billing_cycles: number | undefined;
    status: string | undefined;
    subscription_items: ChargebeeSubscriptionItem[] | undefined;
    total_dues: number | undefined;
    updated_at: string | undefined;
    add_ons: string[] | undefined;
    num_users: number | undefined;
    cf_organization_id: string | undefined;
}

export interface ChargebeeSubscriptionReference {
    subscription_id: string;
    customer_id: string;
    plan_id: string;
}

export interface ChargebeeSubscriptionItem {
    amount: number;
    billing_cycles: number | undefined;
    free_quantity: number | undefined;
    item_price_id: string;
    item_type: string;
    object: string;
    quantity: number;
    unit_price: number;
    pretty_item_name: string | undefined;
    discount_name: string | undefined;
    discount_amount: number | undefined;
    item_discount_amount: number | undefined;
}


export enum GeocodeType {
    Address = 'Address',
    Place = 'Place',
}

export interface SubscriptionFeature {
    key: FeatureType;
    value: string;
}

export enum FeatureTierLevel {
    Basic = 'Free',
    Starter = 'Starter',
    Pro = 'Pro (Legacy)',
    ProBase = 'Pro',
    Teams = 'Teams',
    AppSumoTier1 = 'AppSumo Tier 1',
    AppSumoTier2 = 'AppSumo Tier 2',
    AppSumoTier3 = 'AppSumo Tier 3',
}

function isFeatureTierLevel(value: unknown): value is FeatureTierLevel {
    return (Object.values(FeatureTierLevel) as unknown[]).includes(value);
}

export enum CoreTiers {
    Pro = 'Pro',
}

export enum SpecialTiers {
    Internal = 'Internal',
}

export enum StandardProAddOns {
    PassportChallenge = 'Passport Challenge',
    Guide = 'Guide',
    ScavengerHunt = 'Scavenger Hunt',
    Trivia = 'Trivia',
    PasswordPaywall = 'Password and Paywall',
    CustomForms = 'Crowdsourcing & Field Customization',
    IntegrationAutomation = 'Integration & Automation',
    LeadGeneration = 'Lead Generation',
    CustomDomain = 'Custom Domain & Email',
    BestOf = 'Best Of',
    BulletinBoard = 'Bulletin Board',
    Calendar = 'Calendar',
    HaveItAll = 'Have It All',
}

export enum UsageBasedAddOns {
    PointsPerMap = 'Points Per Map',
    Translation = 'Auto Translation',
    Views = 'Map Views',
}

export enum AddOnAvailability {
    Available = 'available',
    ComingSoon = 'coming_soon',
    GetNotified = 'get_notified'
}

export enum AddOnType {
    Paywall = 'paywall',
    Password = 'password',
    EmailGate = 'email-gate',
    Challenge = 'challenge',
    Guide = 'guide',
    CustomForm = 'custom-form',
    IntegrationAutomation = 'integration-automation',
    LeadPopUp = 'lead-popup',
    BestOf = 'best-of',
    LeadPointForm = 'lead-point-form',
    Translation = 'translation',
    Zapier = 'zapier',
    BlogToMapAI = 'blog-to-map-ai',
    SearchAndAddAI = 'search-and-add-ai',
    AddOn = 'add-on',
    ScavengerHunt = 'scavenger-hunt',
    KmlUpload = 'kml-upload',
    Comments = 'comments',
    TreasureHunt = 'treasure-hunt',
}

export interface PopUpCustomization {
    _id?: ObjectId;
    pop_up_type: PopUpTypeOld;
    is_active: boolean;
    pop_up_header: string;
    pop_up_description: string;
    cta_button_text: string;
    background_color: string;
    text_color: string;
    cta_background_color: string;
    cta_text_color: string;
    opt_in_language: string;
    display_when_embeded: boolean;
    display_number: number;
    display_once: boolean;
    collect_email?: boolean;
    organization_id: ObjectId | undefined;
    entity_ids: ObjectId[] | undefined;
    cta_link?: string;
}

export enum PopUpTypeOld {
    LeadGeneration = 'lead-generation',
    PostPointSubmission = 'post-point-submission',
}

// ===== NEW STANDALONE POPUP TYPES =====

export interface GeoTrigger {
    lat: number;
    lng: number;
    radius_meters: number;
}

export enum PopUpTrigger {
    OnMapLoad = 'on-map-load',
    LogicBased = 'logic-based',
    PostSubmission = 'post-submission',
}

export type PopUpAction =
    | { type: 'none' }
    | { type: 'collect-email'; data: { opt_in_language: string } }
    | { type: 'link-out'; data: { url: string; button_text: string } }
    | { type: 'open-point'; data: { point_id: ObjectId; button_text: string } };

export interface GetPopUpResponse {
    _id: ObjectId;
    organization_id: ObjectId;
    topic_ids: ObjectId[];
    name: string;
    pop_up_type: PopUpTrigger;
    action: PopUpAction;
    is_active: boolean;
    header: string;
    description: string;
    background_color: string;
    text_color: string;
    cta_background_color: string;
    cta_text_color: string;
    display_when_embedded: boolean;
    display_delay_seconds: number;
    active_from: string | undefined;
    active_until: string | undefined;
    geo_trigger: GeoTrigger | undefined;
    priority: number;
    recurrence: 'once' | 'daily' | 'weekly' | 'monthly';
    header_logo_url?: string;
    show_distance_to_user: boolean;
    show_point_actions: boolean;
    show_rating_field_id?: ObjectId;
    created_at: string;
    updated_at: string;
}

export interface CreatePopUpRequest {
    _id?: ObjectId;
    organization_id: ObjectId;
    topic_ids: ObjectId[];
    name: string;
    pop_up_type: PopUpTrigger;
    action: PopUpAction;
    is_active: boolean;
    header: string;
    description: string;
    background_color: string;
    text_color: string;
    cta_background_color: string;
    cta_text_color: string;
    display_when_embedded: boolean;
    display_delay_seconds: number;
    active_from: string | null;
    active_until: string | null;
    geo_trigger: GeoTrigger | null;
    priority: number;
    recurrence: 'once' | 'daily' | 'weekly' | 'monthly';
    header_logo_url?: string;
    show_distance_to_user: boolean;
    show_point_actions: boolean;
    show_rating_field_id?: ObjectId;
}

export interface PopUpAnalytics {
    popup_id: ObjectId;
    name: string;
    impression_count: number;
    click_count: number;
    dismiss_count: number;
    email_capture_count: number;
}

export interface FormField {
    name: string;
    label: string;
    field_type: FormFieldType;
    is_required: boolean;
    is_active: boolean;
    placeholder: string;
}

export enum FormFieldType {
    Text = 'text',
    Email = 'email',
    Phone = 'phone',
    Number = 'number',
    /** Short alphanumeric label (letters, digits, hyphen; ≤8 chars) — booth/stall codes, pin labels. */
    ShortCode = 'short_code',
    Date = 'date',
    Time = 'time',
    DateTime = 'date_time',
    Url = 'url',
    TextArea = 'textarea',
    Select = 'select',
    Checkbox = 'checkbox',
    /** Markdown source. */
    RichText = 'rich_text',
    /** 0..N images linked via the `Image` collection. UI label: "Image (images)". */
    Photos = 'photos',
    /** Single image URL. Used as a custom field and as the source for
     *  the canonical "Icon" projection (sentinel `000000000000000000000004`)
     *  which read/writes `Point.custom_icon_url`. UI label: "Image (single)". */
    Image = 'image',
    /** Special offer / deal — structured (body + optional expiry + terms). */
    Deal = 'deal',
    /** Paired (start, end?) datetimes. Replaces legacy `Point.start`/`end`. */
    DateRange = 'date_range',
    /** One uploaded PDF as `{ url, name, size }` on `field_value_json`. */
    File = 'file',
}
export interface PointEmailCaptureCustomization {
    is_active: boolean;
    cta_button_text: string;
    success_message: string;
    destination_type: 'static_email' | 'point_owner' | 'custom_field_email';
    custom_field_id: ObjectId | undefined;
    destination_email: string;
    name_field: FormField;
    email_field: FormField;
    phone_field: FormField;
    additional_info_field: FormField;
}

export enum PointEmailDestinationType {
    StaticEmail = 'static_email',
    PointOwner = 'point_owner',
    CustomFieldEmail = 'custom_field_email',
}

export enum FeatureType {
    // BASIC

    // Map management
    Backpack = 'backpack',
    CreatorProfiles = 'creator-profiles',
    Folders = 'folders',

    // Map sharing
    QrCodes = 'qr-codes',
    EmbedLink = 'embed-link',
    CollaborativeMap = 'collaborative-map',

    // Map settings
    ProxiAI = 'proxi-ai',
    CategoriesAndCustomIcons = 'categories-and-custom-icons',
    Tags = 'tags',
    CustomUrls = 'custom-urls',
    HeaderBasicCustomization = 'header-basic-customization',
    HeaderLogo = 'header-logo',
    SocialMediaImage = 'social-media-image',
    MapStyling = 'map-styling',
    CustomMapStyling = 'custom-map-styling',
    CustomShapes = 'custom-shapes',
    Upvoting = 'upvoting',
    AutomaticallyShowPointCardList = 'automatically-show-point-card-list',
    PointNumbering = 'point-numbering',
    PanningBetweenPointsInList = 'panning-between-points-in-list',
    RequireApprovalOnPoints = 'require-approval-on-points',
    SpreadsheetView = 'spreadsheet-view',
    KmlUpload = 'kml-upload',
    BasicInsights = 'basic-insights',
    PointImageUpload = 'point-image-upload',

    // STARTER

    // Map management
    AddMapEditors = 'add-map-editors',
    AddFolderEditors = 'add-folder-editors',

    // Map sharing
    PrintableMap = 'printable-map',
    TextHtmlEmbed = 'text-html-embed',
    MakeMapsPrivate = 'make-maps-private',
    ShareMap = 'share-map',

    // Map settings
    HeaderLinkOut = 'header-link-out',
    DateTime = 'date-time',
    DisableGoogleImages = 'disable-google-images',
    CustomFont = 'custom-font',
    DisableLocation = 'disable-location',
    SpecialOffersOnPoints = 'special-offers-on-points',
    AdvancedInsights = 'advanced-insights',

    // PRO

    // Map management
    DuplicateMap = 'duplicate-map',

    // Map sharing

    // Map settings
    DisableAlsoFeaturedOn = 'disable-also-featured-on',
    CustomizeAddPointForm = 'customize-add-point-form',
    HideBuildYourOwnMap = 'hide-build-your-own-map',
    HideAllCommandCenterButtons = 'hide-all-command-center-buttons',
    HideAddToMapInPoints = 'hide-add-to-map-in-points',
    AdditionalCustomFormElements = 'additional-custom-form-elements',
    ZapierAccess = 'zapier-access',
    PasswordProtection = 'password-protection',
    PaywallProtection = 'paywall-protection',
    EmailProtection = 'email-protection',
    AudienceMessaging = 'audience-messaging',
    EmailCaptureOnPoint = 'email-capture-on-point',
    EmailCapture = 'email-capture-on-map',
    CustomizeAboutMapDrawer = 'customize-about-map-drawer',
    HideGetRoute = 'hide-get-route',
    HideMultiSelect = 'hide-multi-select',
    HideBookmarkButtons = 'hide-bookmark-buttons',
    CustomFilteringOptions = 'custom-filtering-options',
    SetCustomZoom = 'set-custom-zoom',
    ApiAccess = 'api-access',


    // COUNTS
    NumberOfMapsPerEmail = 'number-of-maps-per-email',
    PointsPerMap = 'points-per-map',
    NumberOfEditorsPerMap = 'number-of-editors-per-map',
    NumberOfEditorsPerFolder = 'number-of-editors-per-folder',
    NumberOfUsesOfProxiAI = 'number-of-uses-of-proxi-ai',
    NumberOfUsesOfRoutes = 'number-of-uses-of-routes',
    NumberOfViewsPerMap = 'number-of-views-per-map',
    CreatorPaywallTake = 'creator-paywall-take',
    AutotranslationLanguages = 'autotranslation-languages',
    GeocodesPerMonth = 'geocodes-per-month',
    EmailsPerMonth = 'emails-per-month',
    SmsPerMonth = 'sms-per-month',

    // TEAMS

    // Advanced Tools
    ChallengePassport = 'challenge-passport',
    Guide = 'guide',
    ScavengerHunt = 'scavenger-hunt',
    Trivia = 'trivia',
    Election = 'election',
    HideHeader = 'hide-header',
    BrandStandards = 'brand-standards',
    TeamSharedBackpack = 'team-shared-backpack',
    AnalyticsAcrossMaps = 'analytics-across-maps',
    LeadsAcrossMaps = 'leads-across-maps',
    MonthlyAdFreeViewsPerMap = 'monthly-ad-free-views-per-map',
    CustomEmail= 'custom-email',
    CustomDomain = 'custom-domain',
    Comments = 'comments',
    Calendar = 'calendar',
    BulletinBoard = 'bulletin-board',
    Directory = 'directory',
    Sponsorship = 'sponsorship',
}

// Billing Options
export enum BillingOption {
    Monthly = 'Monthly',
    Quarterly = 'Quarterly',
    Annual = 'Annual'
}

export enum TierItemIds {
    ProxiStarterLegacyUSDMonthly = 'Proxi-Starter-2-USD-Monthly',
    ProxiStarterLegacyUSDYearly = 'Proxi-Starter-2-USD-Yearly',
    ProxiProLegacyMonthly = 'Proxi-Starter-Experiment-USD-Monthly',
    ProxiProLegacyYearly = 'Proxi-Starter-Experiment-USD-Yearly',
    ProxiTeamsLegacyMonthly = 'Proxi-Pro-Experiment-USD-Monthly',
    ProxiTeamsLegacyYearly = 'Proxi-Pro-Experiment-USD-Yearly',
    AppSumoTier1USDYearly = 'AppSumo-Tier-1-USD-Yearly',
    AppSumoTier2USDYearly = 'AppSumo-Tier-2-USD-Yearly',
    AppSumoTier3USDYearly = 'AppSumo-Tier-3-USD-Yearly',

    ProxiProMonthly = 'Proxi-Pro-USD-Monthly',
    ProxiProQuarterly = 'Proxi-Pro-USD-Every-3-months',
    ProxiProYearly = 'Proxi-Pro-USD-Yearly',

    ProxiFree = ''
};

/**
 * Seats beyond the first are billed as their own Chargebee add-on rather than by raising the plan
 * quantity, so the seat price stays decoupled from the plan price. Deliberately not a
 * `StandardProAddOns` member — it is not a feature and must never appear in add-on pickers.
 */
export enum SeatItemIds {
    AdditionalUserMonthly = 'Additional-Pro-User-USD-Monthly',
    AdditionalUserQuarterly = 'Additional-Pro-User-USD-Every-3-months',
    AdditionalUserAnnual = 'Additional-Pro-User-USD-Yearly',
};

export const AdditionalUserItemIdMap: {
    [key in BillingOption]: SeatItemIds;
} = {
    [BillingOption.Monthly]: SeatItemIds.AdditionalUserMonthly,
    [BillingOption.Quarterly]: SeatItemIds.AdditionalUserQuarterly,
    [BillingOption.Annual]: SeatItemIds.AdditionalUserAnnual,
};

/** Charge per extra seat for the whole billing period — a flat $25/month on every term. */
export const AdditionalUserPriceMap: {
    [key in BillingOption]: number;
} = {
    [BillingOption.Monthly]: 25,
    [BillingOption.Quarterly]: 75,
    [BillingOption.Annual]: 300,
};

export function isAdditionalUserItemId(itemPriceId: string): boolean {
    return Object.values(SeatItemIds).includes(itemPriceId as SeatItemIds);
}

/** Total seat charge for one billing period: the plan covers the first seat, the rest are flat. */
export function seatTotalForPeriod(firstSeatPrice: number, userCount: number, billing: BillingOption): number {
    return firstSeatPrice + AdditionalUserPriceMap[billing] * Math.max(userCount - 1, 0);
}

/**
 * Which billing toggle to show. Anyone already subscribed keeps the term they're on; everyone else
 * starts on yearly. Chargebee reports quarterly as a 3-month period, not its own unit.
 */
export function billingOptionForSubscription(billingPeriod?: number | null, billingPeriodUnit?: string | null): BillingOption {
    if (billingPeriodUnit === 'year') return BillingOption.Annual;
    if (billingPeriodUnit === 'month') return billingPeriod === 3 ? BillingOption.Quarterly : BillingOption.Monthly;
    return BillingOption.Annual;
}

export const TierItemMap: {
    [key: string]: FeatureTierLevel;
} = {
    [TierItemIds.ProxiStarterLegacyUSDMonthly]: FeatureTierLevel.Starter,
    [TierItemIds.ProxiStarterLegacyUSDYearly]: FeatureTierLevel.Starter,
    [TierItemIds.ProxiProLegacyMonthly]: FeatureTierLevel.Pro,
    [TierItemIds.ProxiProLegacyYearly]: FeatureTierLevel.Pro,
    [TierItemIds.ProxiTeamsLegacyMonthly]: FeatureTierLevel.Teams,
    [TierItemIds.ProxiTeamsLegacyYearly]: FeatureTierLevel.Teams,
    [TierItemIds.AppSumoTier1USDYearly]: FeatureTierLevel.AppSumoTier1,
    [TierItemIds.AppSumoTier2USDYearly]: FeatureTierLevel.AppSumoTier2,
    [TierItemIds.AppSumoTier3USDYearly]: FeatureTierLevel.AppSumoTier3,
    [TierItemIds.ProxiProMonthly]: FeatureTierLevel.ProBase,
    [TierItemIds.ProxiProQuarterly]: FeatureTierLevel.ProBase,
    [TierItemIds.ProxiProYearly]: FeatureTierLevel.ProBase,
};


// Monthly price billed monthly
export const FeatureTierMonthlyPriceMap: {
    [key in FeatureTierLevel]: { [key in BillingOption]: number | undefined };
} = {
    [FeatureTierLevel.Basic]: { Monthly: 0, Annual: 0, Quarterly: undefined },
    [FeatureTierLevel.Starter]: { Monthly: 18, Annual: 9 , Quarterly: undefined },
    [FeatureTierLevel.Pro]: { Monthly: 39, Annual: 29 , Quarterly: undefined },
    [FeatureTierLevel.ProBase]: { Monthly: 65, Annual: 379 , Quarterly: 129 },
    [FeatureTierLevel.Teams]: { Monthly: 399, Annual: 299 , Quarterly: undefined },
    [FeatureTierLevel.AppSumoTier1]: { Monthly: 0, Annual: 0 , Quarterly: undefined },
    [FeatureTierLevel.AppSumoTier2]: { Monthly: 0, Annual: 0 , Quarterly: undefined },
    [FeatureTierLevel.AppSumoTier3]: { Monthly: 0, Annual: 0 , Quarterly: undefined },
};

export const TierItemIdMap: {
    [key in FeatureTierLevel]: { [key in BillingOption]: TierItemIds | undefined };
} = {
    [FeatureTierLevel.Starter]: { [BillingOption.Monthly]: TierItemIds.ProxiStarterLegacyUSDMonthly, [BillingOption.Annual]: TierItemIds.ProxiStarterLegacyUSDYearly , [BillingOption.Quarterly]: undefined },
    [FeatureTierLevel.Pro]: { [BillingOption.Monthly]: TierItemIds.ProxiProLegacyMonthly, [BillingOption.Annual]: TierItemIds.ProxiProLegacyYearly , [BillingOption.Quarterly]: undefined },
    [FeatureTierLevel.Teams]: { [BillingOption.Monthly]: TierItemIds.ProxiTeamsLegacyMonthly, [BillingOption.Annual]: TierItemIds.ProxiTeamsLegacyYearly , [BillingOption.Quarterly]: undefined },
    [FeatureTierLevel.AppSumoTier1]: { [BillingOption.Monthly]: TierItemIds.AppSumoTier1USDYearly, [BillingOption.Annual]: TierItemIds.AppSumoTier1USDYearly , [BillingOption.Quarterly]: undefined },
    [FeatureTierLevel.AppSumoTier2]: { [BillingOption.Monthly]: TierItemIds.AppSumoTier2USDYearly, [BillingOption.Annual]: TierItemIds.AppSumoTier2USDYearly , [BillingOption.Quarterly]: undefined },
    [FeatureTierLevel.AppSumoTier3]: { [BillingOption.Monthly]: TierItemIds.AppSumoTier3USDYearly, [BillingOption.Annual]: TierItemIds.AppSumoTier3USDYearly , [BillingOption.Quarterly]: undefined },

    [FeatureTierLevel.ProBase]: {
        [BillingOption.Monthly]: TierItemIds.ProxiProMonthly,
        [BillingOption.Quarterly]: TierItemIds.ProxiProQuarterly,
        [BillingOption.Annual]: TierItemIds.ProxiProYearly },

    [FeatureTierLevel.Basic]: { [BillingOption.Monthly]: TierItemIds.ProxiFree, [BillingOption.Annual]: TierItemIds.ProxiFree , [BillingOption.Quarterly]: undefined },
};

export enum AddOnItemIds {
    PassportChallengeMonthly = 'Challenge-Passport-Add-On-USD-Monthly',
    PassportChallengeQuarterly = 'Challenge-Passport-Add-On-USD-Every-3-months',
    PassportChallengeAnnual = 'Challenge-Passport-Add-On-USD-Yearly',
    GuideMonthly = 'Guide-Add-On-USD-Monthly',
    GuideQuarterly = 'Guide-Add-On-USD-Every-3-months',
    GuideAnnual = 'Guide-Add-On-USD-Yearly',
    ScavengerHuntMonthly = 'Scavenger-Hunt-USD-Monthly',
    ScavengerHuntQuarterly = 'Scavenger-Hunt-USD-Every-3-months',
    ScavengerHuntAnnual = 'Scavenger-Hunt-USD-Yearly',
    TriviaMonthly = 'Trivia-Add-On-USD-Monthly',
    TriviaQuarterly = 'Trivia-Add-On-USD-Every-3-months',
    TriviaAnnual = 'Trivia-Add-On-USD-Yearly',
    PasswordPaywallMonthly = 'Monetization-Tools-Add-On-USD-Monthly',
    PasswordPaywallQuarterly = 'Monetization-Tools-Add-On-USD-Every-3-months',
    PasswordPaywallAnnual = 'Monetization-Tools-Add-On-USD-Yearly',
    CustomFormsMonthly = 'Custom-Forms-and-Fields-Add-On-USD-Monthly',
    CustomFormsQuarterly = 'Custom-Forms-and-Fields-Add-On-USD-Every-3-months',
    CustomFormsAnnual = 'Custom-Forms-and-Fields-Add-On-USD-Yearly',
    IntegrationAutomationMonthly = 'Api-Automation-Add-On-USD-Monthly',
    IntegrationAutomationQuarterly = 'Api-Automation-Add-On-USD-Every-3-months',
    IntegrationAutomationAnnual = 'Api-Automation-Add-On-USD-Yearly',
    CustomDomainMonthly = 'Custom-Domain-and-Email-Add-On-USD-Monthly',
    CustomDomainQuarterly = 'Custom-Domain-and-Email-Add-On-USD-Every-3-months',
    CustomDomainAnnual = 'Custom-Domain-and-Email-Add-On-USD-Yearly',
    LeadGenerationMonthly = 'Lead-Generation-Kit-Add-On-USD-Monthly',
    LeadGenerationQuarterly = 'Lead-Generation-Kit-Add-On-USD-Every-3-months',
    LeadGenerationAnnual = 'Lead-Generation-Kit-Add-On-USD-Yearly',
    BestOfMonthly = 'Best-Of-Voting-Kit-Add-On-USD-Monthly',
    BestOfQuarterly = 'Best-Of-Voting-Kit-Add-On-USD-Every-3-months',
    BestOfAnnual = 'Best-Of-Voting-Kit-Add-On-USD-Yearly',
    BulletinBoardMonthly = 'Bulletin-Board-Add-On-USD-Monthly',
    BulletinBoardQuarterly = 'Bulletin-Board-Add-On-USD-Every-3-months',
    BulletinBoardAnnual = 'Bulletin-Board-Add-On-USD-Yearly',
    CalendarMonthly = 'Calendar-Add-On-USD-Monthly',
    CalendarQuarterly = 'Calendar-Add-On-USD-Every-3-months',
    CalendarAnnual = 'Calendar-Add-On-USD-Yearly',
    HaveItAllMonthly = 'Have-It-All-Add-On-USD-Monthly',
    HaveItAllQuarterly = 'Have-It-All-Add-On-USD-Every-3-months',
    HaveItAllAnnual = 'Have-It-All-Add-On-USD-Yearly',
}

function getBillingFromItemId(itemId: string): BillingOption {
    if (itemId.includes('Monthly')) {
        return BillingOption.Monthly;
    } else if (itemId.includes('Every-3-months')) {
        return BillingOption.Quarterly;
    } else if (itemId.includes('Yearly')) {
        return BillingOption.Annual;
    } else {
        console.error(`Unknown billing for item ID: ${itemId}`);
        return BillingOption.Monthly; // default to monthly
    }
}

// Or get both add-on type and billing:
export function getPriceFromItemId(itemId: string): { price_in_cents: number; billing: BillingOption } | undefined {
    const item = AddOnItemMap[itemId] ?? TierItemMap[itemId];
    const billing = getBillingFromItemId(itemId);

    if (isAdditionalUserItemId(itemId)) {
        return { price_in_cents: AdditionalUserPriceMap[billing] * 100, billing };
    }

    if (item === undefined || item === null) {
        console.warn(`Unknown item ID: ${itemId}`);
        return undefined;
    }
    let price: number | undefined;
    if (Object.values(StandardProAddOns).includes(item)) {
        price = AddOnMonthlyPriceMap[item][billing];
    } else if (isFeatureTierLevel(item)) {
        price = FeatureTierMonthlyPriceMap[item][billing];
    } else {
        console.error(`Unknown item type for item ID: ${itemId}`);
        return undefined;
    }

    if (price === undefined) {
        console.error(`Price not found for item ID: ${itemId} with billing: ${billing}`);
        return undefined;
    }

    return { price_in_cents: price * 100, billing };
}

// create a map to get the StandardProAddOn from the item id
export const AddOnItemMap: {
    [key: string]: StandardProAddOns;
} = {
    [AddOnItemIds.PassportChallengeMonthly]: StandardProAddOns.PassportChallenge,
    [AddOnItemIds.PassportChallengeQuarterly]: StandardProAddOns.PassportChallenge,
    [AddOnItemIds.PassportChallengeAnnual]: StandardProAddOns.PassportChallenge,
    [AddOnItemIds.GuideMonthly]: StandardProAddOns.Guide,
    [AddOnItemIds.GuideQuarterly]: StandardProAddOns.Guide,
    [AddOnItemIds.GuideAnnual]: StandardProAddOns.Guide,
    [AddOnItemIds.ScavengerHuntMonthly]: StandardProAddOns.ScavengerHunt,
    [AddOnItemIds.ScavengerHuntQuarterly]: StandardProAddOns.ScavengerHunt,
    [AddOnItemIds.ScavengerHuntAnnual]: StandardProAddOns.ScavengerHunt,
    [AddOnItemIds.TriviaMonthly]: StandardProAddOns.Trivia,
    [AddOnItemIds.TriviaQuarterly]: StandardProAddOns.Trivia,
    [AddOnItemIds.TriviaAnnual]: StandardProAddOns.Trivia,
    [AddOnItemIds.PasswordPaywallMonthly]: StandardProAddOns.PasswordPaywall,
    [AddOnItemIds.PasswordPaywallQuarterly]: StandardProAddOns.PasswordPaywall,
    [AddOnItemIds.PasswordPaywallAnnual]: StandardProAddOns.PasswordPaywall,
    [AddOnItemIds.CustomFormsMonthly]: StandardProAddOns.CustomForms,
    [AddOnItemIds.CustomFormsQuarterly]: StandardProAddOns.CustomForms,
    [AddOnItemIds.CustomFormsAnnual]: StandardProAddOns.CustomForms,
    [AddOnItemIds.IntegrationAutomationMonthly]: StandardProAddOns.IntegrationAutomation,
    [AddOnItemIds.IntegrationAutomationQuarterly]: StandardProAddOns.IntegrationAutomation,
    [AddOnItemIds.IntegrationAutomationAnnual]: StandardProAddOns.IntegrationAutomation,
    [AddOnItemIds.CustomDomainMonthly]: StandardProAddOns.CustomDomain,
    [AddOnItemIds.CustomDomainQuarterly]: StandardProAddOns.CustomDomain,
    [AddOnItemIds.CustomDomainAnnual]: StandardProAddOns.CustomDomain,
    [AddOnItemIds.LeadGenerationMonthly]: StandardProAddOns.LeadGeneration,
    [AddOnItemIds.LeadGenerationQuarterly]: StandardProAddOns.LeadGeneration,
    [AddOnItemIds.LeadGenerationAnnual]: StandardProAddOns.LeadGeneration,
    [AddOnItemIds.BestOfMonthly]: StandardProAddOns.BestOf,
    [AddOnItemIds.BestOfQuarterly]: StandardProAddOns.BestOf,
    [AddOnItemIds.BestOfAnnual]: StandardProAddOns.BestOf,
    [AddOnItemIds.BulletinBoardMonthly]: StandardProAddOns.BulletinBoard,
    [AddOnItemIds.BulletinBoardQuarterly]: StandardProAddOns.BulletinBoard,
    [AddOnItemIds.BulletinBoardAnnual]: StandardProAddOns.BulletinBoard,
    [AddOnItemIds.CalendarMonthly]: StandardProAddOns.Calendar,
    [AddOnItemIds.CalendarQuarterly]: StandardProAddOns.Calendar,
    [AddOnItemIds.CalendarAnnual]: StandardProAddOns.Calendar,
    [AddOnItemIds.HaveItAllMonthly]: StandardProAddOns.HaveItAll,
    [AddOnItemIds.HaveItAllQuarterly]: StandardProAddOns.HaveItAll,
    [AddOnItemIds.HaveItAllAnnual]: StandardProAddOns.HaveItAll,
};


/** Display only — `NONPROFIT_DISCOUNT_PERCENT` in server/core/src/base/types.rs mints the actual coupon. */
export const NONPROFIT_DISCOUNT_RATE = 0.25;

export function applyNonprofitDiscount(amount: number): number {
    return Math.round(amount * (1 - NONPROFIT_DISCOUNT_RATE));
}

export const AddOnMonthlyPriceMap: {
    [key in StandardProAddOns]: { [key in BillingOption]: number };
} = {
    [StandardProAddOns.PassportChallenge]: { Monthly: 201, Quarterly: 408, Annual: 99*12 },
    [StandardProAddOns.Guide]: { Monthly: 201, Quarterly: 408, Annual: 99*12 },
    [StandardProAddOns.ScavengerHunt]: { Monthly: 201, Quarterly: 408, Annual: 99*12 },
    [StandardProAddOns.Trivia]: { Monthly: 201, Quarterly: 408, Annual: 99*12 },
    [StandardProAddOns.PasswordPaywall]: { Monthly: 59, Quarterly: 117,Annual: 29*12 },
    [StandardProAddOns.CustomForms]: { Monthly: 59, Quarterly: 117,Annual: 29*12 },
    [StandardProAddOns.IntegrationAutomation]: { Monthly: 59, Quarterly: 117,Annual: 29*12 },
    [StandardProAddOns.CustomDomain]: { Monthly: 59, Quarterly: 117,Annual: 29*12 },
    [StandardProAddOns.LeadGeneration]: { Monthly: 59, Quarterly: 117,Annual: 29*12 },
    [StandardProAddOns.BestOf]: { Monthly: 201, Quarterly: 408, Annual: 99*12 },
    [StandardProAddOns.BulletinBoard]: { Monthly: 59, Quarterly: 117, Annual: 29*12 },
    [StandardProAddOns.Calendar]: { Monthly: 59, Quarterly: 117, Annual: 29*12 },
    [StandardProAddOns.HaveItAll]: { Monthly: 334, Quarterly: 670, Annual:1620 },
};


export const AddOnItemIdMap: {
    [key in StandardProAddOns]: { [key in BillingOption]: string };
} = {
    [StandardProAddOns.PassportChallenge]: { [BillingOption.Monthly]: AddOnItemIds.PassportChallengeMonthly, [BillingOption.Annual]: AddOnItemIds.PassportChallengeAnnual, [BillingOption.Quarterly]: AddOnItemIds.PassportChallengeQuarterly },
    [StandardProAddOns.Guide]: { [BillingOption.Monthly]: AddOnItemIds.GuideMonthly, [BillingOption.Annual]: AddOnItemIds.GuideAnnual, [BillingOption.Quarterly]: AddOnItemIds.GuideQuarterly },
    [StandardProAddOns.ScavengerHunt]: { [BillingOption.Monthly]: AddOnItemIds.ScavengerHuntMonthly, [BillingOption.Annual]: AddOnItemIds.ScavengerHuntAnnual, [BillingOption.Quarterly]: AddOnItemIds.ScavengerHuntQuarterly },
    [StandardProAddOns.Trivia]: { [BillingOption.Monthly]: AddOnItemIds.TriviaMonthly, [BillingOption.Annual]: AddOnItemIds.TriviaAnnual, [BillingOption.Quarterly]: AddOnItemIds.TriviaQuarterly },
    [StandardProAddOns.PasswordPaywall]: { [BillingOption.Monthly]: AddOnItemIds.PasswordPaywallMonthly, [BillingOption.Annual]: AddOnItemIds.PasswordPaywallAnnual, [BillingOption.Quarterly]: AddOnItemIds.PasswordPaywallQuarterly },
    [StandardProAddOns.CustomForms]: { [BillingOption.Monthly]: AddOnItemIds.CustomFormsMonthly, [BillingOption.Annual]: AddOnItemIds.CustomFormsAnnual, [BillingOption.Quarterly]: AddOnItemIds.CustomFormsQuarterly },
    [StandardProAddOns.IntegrationAutomation]: { [BillingOption.Monthly]: AddOnItemIds.IntegrationAutomationMonthly, [BillingOption.Annual]: AddOnItemIds.IntegrationAutomationAnnual, [BillingOption.Quarterly]: AddOnItemIds.IntegrationAutomationQuarterly },
    [StandardProAddOns.LeadGeneration]: { [BillingOption.Monthly]: AddOnItemIds.LeadGenerationMonthly, [BillingOption.Annual]: AddOnItemIds.LeadGenerationAnnual , [BillingOption.Quarterly]: AddOnItemIds.LeadGenerationQuarterly },
    [StandardProAddOns.BestOf]: { [BillingOption.Monthly]: AddOnItemIds.BestOfMonthly, [BillingOption.Annual]: AddOnItemIds.BestOfAnnual , [BillingOption.Quarterly]: AddOnItemIds.BestOfQuarterly },
    [StandardProAddOns.BulletinBoard]: { [BillingOption.Monthly]: AddOnItemIds.BulletinBoardMonthly, [BillingOption.Annual]: AddOnItemIds.BulletinBoardAnnual, [BillingOption.Quarterly]: AddOnItemIds.BulletinBoardQuarterly },
    [StandardProAddOns.Calendar]: { [BillingOption.Monthly]: AddOnItemIds.CalendarMonthly, [BillingOption.Annual]: AddOnItemIds.CalendarAnnual, [BillingOption.Quarterly]: AddOnItemIds.CalendarQuarterly },
    [StandardProAddOns.HaveItAll]: { [BillingOption.Monthly]: AddOnItemIds.HaveItAllMonthly, [BillingOption.Annual]: AddOnItemIds.HaveItAllAnnual , [BillingOption.Quarterly]: AddOnItemIds.HaveItAllQuarterly },
    [StandardProAddOns.CustomDomain]: { [BillingOption.Monthly]: AddOnItemIds.CustomDomainMonthly, [BillingOption.Annual]: AddOnItemIds.CustomDomainAnnual , [BillingOption.Quarterly]: AddOnItemIds.CustomDomainQuarterly },
};

export const FeatureTierLevelMap: {
    [key: string]: Array<FeatureTierLevel | CoreTiers | StandardProAddOns | UsageBasedAddOns>;
} = {
    // Basic

    // Map management
    [FeatureType.CreatorProfiles]: [
        FeatureTierLevel.Basic,
        FeatureTierLevel.Starter,
        FeatureTierLevel.Pro,
        FeatureTierLevel.ProBase,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier1,
        FeatureTierLevel.AppSumoTier2,
        FeatureTierLevel.AppSumoTier3,
    ],
    [FeatureType.Folders]: [
        FeatureTierLevel.Basic,
        FeatureTierLevel.Starter,
        FeatureTierLevel.Pro,
        FeatureTierLevel.ProBase,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier1,
        FeatureTierLevel.AppSumoTier2,
        FeatureTierLevel.AppSumoTier3,
    ],

    // Map sharing
    [FeatureType.QrCodes]: [
        FeatureTierLevel.Basic,
        FeatureTierLevel.Starter,
        FeatureTierLevel.Pro,
        FeatureTierLevel.ProBase,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier1,
        FeatureTierLevel.AppSumoTier2,
        FeatureTierLevel.AppSumoTier3,
    ],
    [FeatureType.EmbedLink]: [
        FeatureTierLevel.Basic,
        FeatureTierLevel.Starter,
        FeatureTierLevel.Pro,
        FeatureTierLevel.ProBase,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier1,
        FeatureTierLevel.AppSumoTier2,
        FeatureTierLevel.AppSumoTier3,
    ],
    [FeatureType.CollaborativeMap]: [
        FeatureTierLevel.Basic,
        FeatureTierLevel.Starter,
        FeatureTierLevel.Pro,
        FeatureTierLevel.ProBase,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier1,
        FeatureTierLevel.AppSumoTier2,
        FeatureTierLevel.AppSumoTier3,
    ],

    // Map settings
    [FeatureType.ProxiAI]: [
        FeatureTierLevel.Basic,
        FeatureTierLevel.Starter,
        FeatureTierLevel.Pro,
        FeatureTierLevel.ProBase,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier1,
        FeatureTierLevel.AppSumoTier2,
        FeatureTierLevel.AppSumoTier3,
    ],
    [FeatureType.CategoriesAndCustomIcons]: [
        FeatureTierLevel.Basic,
        FeatureTierLevel.Starter,
        FeatureTierLevel.Pro,
        FeatureTierLevel.ProBase,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier1,
        FeatureTierLevel.AppSumoTier2,
        FeatureTierLevel.AppSumoTier3,
    ],
    [FeatureType.Tags]: [
        FeatureTierLevel.Basic,
        FeatureTierLevel.Starter,
        FeatureTierLevel.Pro,
        FeatureTierLevel.ProBase,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier1,
        FeatureTierLevel.AppSumoTier2,
        FeatureTierLevel.AppSumoTier3,
    ],
    [FeatureType.CustomUrls]: [
        FeatureTierLevel.Basic,
        FeatureTierLevel.Starter,
        FeatureTierLevel.Pro,
        FeatureTierLevel.ProBase,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier1,
        FeatureTierLevel.AppSumoTier2,
        FeatureTierLevel.AppSumoTier3,
    ],
    [FeatureType.HeaderBasicCustomization]: [
        FeatureTierLevel.Basic,
        FeatureTierLevel.Starter,
        FeatureTierLevel.Pro,
        FeatureTierLevel.ProBase,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier1,
        FeatureTierLevel.AppSumoTier2,
        FeatureTierLevel.AppSumoTier3,
    ],
    [FeatureType.SocialMediaImage]: [
        FeatureTierLevel.Basic,
        FeatureTierLevel.Starter,
        FeatureTierLevel.Pro,
        FeatureTierLevel.ProBase,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier1,
        FeatureTierLevel.AppSumoTier2,
        FeatureTierLevel.AppSumoTier3,
    ],
    [FeatureType.MapStyling]: [
        FeatureTierLevel.Basic,
        FeatureTierLevel.Starter,
        FeatureTierLevel.Pro,
        FeatureTierLevel.ProBase,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier1,
        FeatureTierLevel.AppSumoTier2,
        FeatureTierLevel.AppSumoTier3,
    ],
    [FeatureType.CustomMapStyling]: [
        FeatureTierLevel.Basic,
        FeatureTierLevel.Starter,
        FeatureTierLevel.Pro,
        FeatureTierLevel.ProBase,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier1,
        FeatureTierLevel.AppSumoTier2,
        FeatureTierLevel.AppSumoTier3,
    ],
    [FeatureType.Upvoting]: [
        FeatureTierLevel.Basic,
        FeatureTierLevel.Starter,
        FeatureTierLevel.Pro,
        FeatureTierLevel.ProBase,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier1,
        FeatureTierLevel.AppSumoTier2,
        FeatureTierLevel.AppSumoTier3,
    ],
    [FeatureType.AutomaticallyShowPointCardList]: [
        FeatureTierLevel.Basic,
        FeatureTierLevel.Starter,
        FeatureTierLevel.Pro,
        FeatureTierLevel.ProBase,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier1,
        FeatureTierLevel.AppSumoTier2,
        FeatureTierLevel.AppSumoTier3,
    ],
    [FeatureType.PointNumbering]: [
        FeatureTierLevel.Basic,
        FeatureTierLevel.Starter,
        FeatureTierLevel.Pro,
        FeatureTierLevel.ProBase,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier1,
        FeatureTierLevel.AppSumoTier2,
        FeatureTierLevel.AppSumoTier3,
    ],
    [FeatureType.PanningBetweenPointsInList]: [
        FeatureTierLevel.Basic,
        FeatureTierLevel.Starter,
        FeatureTierLevel.Pro,
        FeatureTierLevel.ProBase,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier1,
        FeatureTierLevel.AppSumoTier2,
        FeatureTierLevel.AppSumoTier3,
    ],
    [FeatureType.RequireApprovalOnPoints]: [
        FeatureTierLevel.Basic,
        FeatureTierLevel.Starter,
        FeatureTierLevel.Pro,
        FeatureTierLevel.ProBase,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier1,
        FeatureTierLevel.AppSumoTier2,
        FeatureTierLevel.AppSumoTier3,
    ],
    [FeatureType.SpreadsheetView]: [
        FeatureTierLevel.Basic,
        FeatureTierLevel.Starter,
        FeatureTierLevel.Pro,
        FeatureTierLevel.ProBase,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier1,
        FeatureTierLevel.AppSumoTier2,
        FeatureTierLevel.AppSumoTier3,
    ],
    [FeatureType.KmlUpload]: [
        FeatureTierLevel.Basic,
        FeatureTierLevel.Starter,
        FeatureTierLevel.Pro,
        FeatureTierLevel.ProBase,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier1,
        FeatureTierLevel.AppSumoTier2,
        FeatureTierLevel.AppSumoTier3,
    ],
    [FeatureType.BasicInsights]: [
        FeatureTierLevel.Basic,
        FeatureTierLevel.Starter,
        FeatureTierLevel.Pro,
        FeatureTierLevel.ProBase,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier1,
        FeatureTierLevel.AppSumoTier2,
        FeatureTierLevel.AppSumoTier3,
    ],
    [FeatureType.PointImageUpload]: [
        FeatureTierLevel.Basic,
        FeatureTierLevel.Starter,
        FeatureTierLevel.Pro,
        FeatureTierLevel.ProBase,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier1,
        FeatureTierLevel.AppSumoTier2,
        FeatureTierLevel.AppSumoTier3,
    ],

    // STARTER

    // Map management
    [FeatureType.Backpack]: [
        FeatureTierLevel.Starter,
        FeatureTierLevel.Pro,
        FeatureTierLevel.ProBase,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier1,
        FeatureTierLevel.AppSumoTier2,
        FeatureTierLevel.AppSumoTier3,
    ],
    [FeatureType.AddMapEditors]: [
        FeatureTierLevel.Starter,
        FeatureTierLevel.Pro,
        FeatureTierLevel.ProBase,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier1,
        FeatureTierLevel.AppSumoTier2,
        FeatureTierLevel.AppSumoTier3,
    ], // This feature is used to determine if a user is in the Starter level. If you change this, you must change the logic elsewhere.
    [FeatureType.AddFolderEditors]: [ FeatureTierLevel.Starter,
        FeatureTierLevel.Pro,
        FeatureTierLevel.ProBase,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier1,
        FeatureTierLevel.AppSumoTier2,
        FeatureTierLevel.AppSumoTier3,
    ],

    // Map sharing
    [FeatureType.PrintableMap]: [ FeatureTierLevel.Starter,
        FeatureTierLevel.Pro,
        FeatureTierLevel.ProBase,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier1,
        FeatureTierLevel.AppSumoTier2,
        FeatureTierLevel.AppSumoTier3,
    ],
    [FeatureType.TextHtmlEmbed]: [ FeatureTierLevel.Starter,
        FeatureTierLevel.Pro,
        FeatureTierLevel.ProBase,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier1,
        FeatureTierLevel.AppSumoTier2,
        FeatureTierLevel.AppSumoTier3,
    ],
    [FeatureType.MakeMapsPrivate]: [ FeatureTierLevel.Starter,
        FeatureTierLevel.Pro,
        FeatureTierLevel.ProBase,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier1,
        FeatureTierLevel.AppSumoTier2,
        FeatureTierLevel.AppSumoTier3,
    ],
    [FeatureType.ShareMap]: [ FeatureTierLevel.Starter,
        FeatureTierLevel.Pro,
        FeatureTierLevel.ProBase,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier1,
        FeatureTierLevel.AppSumoTier2,
        FeatureTierLevel.AppSumoTier3,
    ],

    // Map settings
    [FeatureType.HeaderLinkOut]: [ FeatureTierLevel.Starter,
        FeatureTierLevel.Pro,
        FeatureTierLevel.ProBase,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier1,
        FeatureTierLevel.AppSumoTier2,
        FeatureTierLevel.AppSumoTier3,
    ],
    [FeatureType.HeaderLogo]: [ FeatureTierLevel.Starter,
        FeatureTierLevel.Pro,
        FeatureTierLevel.ProBase,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier1,
        FeatureTierLevel.AppSumoTier2,
        FeatureTierLevel.AppSumoTier3,
    ],
    [FeatureType.HideHeader]: [ FeatureTierLevel.Starter,
        FeatureTierLevel.Pro,
        FeatureTierLevel.ProBase,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier1,
        FeatureTierLevel.AppSumoTier2,
        FeatureTierLevel.AppSumoTier3,
    ],
    [FeatureType.DateTime]: [ FeatureTierLevel.Starter,
        FeatureTierLevel.Pro,
        FeatureTierLevel.ProBase,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier1,
        FeatureTierLevel.AppSumoTier2,
        FeatureTierLevel.AppSumoTier3,
    ],
    [FeatureType.DisableGoogleImages]: [ FeatureTierLevel.Starter,
        FeatureTierLevel.Pro,
        FeatureTierLevel.ProBase,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier1,
        FeatureTierLevel.AppSumoTier2,
        FeatureTierLevel.AppSumoTier3,
    ],
    [FeatureType.DisableLocation]: [ FeatureTierLevel.Starter,
        FeatureTierLevel.Pro,
        FeatureTierLevel.ProBase,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier1,
        FeatureTierLevel.AppSumoTier2,
        FeatureTierLevel.AppSumoTier3,
    ],
    [FeatureType.SpecialOffersOnPoints]: [ FeatureTierLevel.Starter,
        FeatureTierLevel.Pro,
        FeatureTierLevel.ProBase,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier1,
        FeatureTierLevel.AppSumoTier2,
        FeatureTierLevel.AppSumoTier3,
    ],
    [FeatureType.CustomShapes]: [ FeatureTierLevel.Starter,
        FeatureTierLevel.Pro,
        FeatureTierLevel.ProBase,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier1,
        FeatureTierLevel.AppSumoTier2,
        FeatureTierLevel.AppSumoTier3,
    ],
    [FeatureType.PointsPerMap]: [ FeatureTierLevel.Starter,
        FeatureTierLevel.Pro,
        FeatureTierLevel.ProBase,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier1,
        FeatureTierLevel.AppSumoTier2,
        FeatureTierLevel.AppSumoTier3,
    ],

    // PRO

    // Map management
    [FeatureType.DuplicateMap]: [
        FeatureTierLevel.Pro,
        FeatureTierLevel.ProBase,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier2,
        FeatureTierLevel.AppSumoTier3,
    ], // This feature is used to determine if a user is in the Pro level. If you change this, you must change the logic elsewhere.
    [FeatureType.AdvancedInsights]: [
        FeatureTierLevel.Pro,
        FeatureTierLevel.ProBase,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier2,
        FeatureTierLevel.AppSumoTier3,
    ],

    // Map sharing

    // Map settings
    [FeatureType.DisableAlsoFeaturedOn]: [
        FeatureTierLevel.Pro,
        FeatureTierLevel.ProBase,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier2,
        FeatureTierLevel.AppSumoTier3,
    ],
    [FeatureType.CustomizeAddPointForm]: [
        FeatureTierLevel.ProBase,
        StandardProAddOns.CustomForms,
        StandardProAddOns.HaveItAll,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier2,
        FeatureTierLevel.AppSumoTier3,
    ],
    [FeatureType.HideBuildYourOwnMap]: [
        FeatureTierLevel.Pro,
        FeatureTierLevel.ProBase,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier2,
        FeatureTierLevel.AppSumoTier3,
    ],
    [FeatureType.HideAllCommandCenterButtons]: [
        FeatureTierLevel.Pro,
        FeatureTierLevel.ProBase,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier2,
        FeatureTierLevel.AppSumoTier3,
    ],
    [FeatureType.HideAddToMapInPoints]: [
        FeatureTierLevel.Pro,
        FeatureTierLevel.ProBase,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier2,
        FeatureTierLevel.AppSumoTier3,
    ],
    [FeatureType.CustomFont]: [
        FeatureTierLevel.Pro,
        FeatureTierLevel.ProBase,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier2,
        FeatureTierLevel.AppSumoTier3,
    ],
    [FeatureType.AdditionalCustomFormElements]: [
        FeatureTierLevel.ProBase,
        StandardProAddOns.CustomForms,
        StandardProAddOns.HaveItAll,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier2,
        FeatureTierLevel.AppSumoTier3,
    ],
    [FeatureType.ZapierAccess]: [
        FeatureTierLevel.Pro,
        FeatureTierLevel.ProBase,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier2,
        FeatureTierLevel.AppSumoTier3,
    ],
    [FeatureType.PasswordProtection]: [
        StandardProAddOns.PasswordPaywall,
        StandardProAddOns.HaveItAll,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier3,
        // not AppSumoTier2
    ],
    [FeatureType.PaywallProtection]: [
        StandardProAddOns.PasswordPaywall,
        StandardProAddOns.HaveItAll,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier3,
        // not AppSumoTier2
    ],
    [FeatureType.EmailProtection]: [
        StandardProAddOns.PasswordPaywall,
        StandardProAddOns.LeadGeneration,
        StandardProAddOns.HaveItAll,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier3,
        // not AppSumoTier2
    ],
    [FeatureType.AudienceMessaging]: [
        StandardProAddOns.HaveItAll,
        FeatureTierLevel.Teams,
    ],
    [FeatureType.CustomizeAboutMapDrawer]: [
        FeatureTierLevel.Pro,
        FeatureTierLevel.ProBase,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier2,
        FeatureTierLevel.AppSumoTier3,
    ],
    [FeatureType.HideGetRoute]: [
        FeatureTierLevel.Pro,
        FeatureTierLevel.ProBase,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier2,
        FeatureTierLevel.AppSumoTier3,
    ],
    [FeatureType.HideMultiSelect]: [
        FeatureTierLevel.Pro,
        FeatureTierLevel.ProBase,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier2,
        FeatureTierLevel.AppSumoTier3,
    ],
    [FeatureType.HideBookmarkButtons]: [
        FeatureTierLevel.Pro,
        FeatureTierLevel.ProBase,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier2,
        FeatureTierLevel.AppSumoTier3,
    ],
    [FeatureType.CustomFilteringOptions]: [
        FeatureTierLevel.Pro,
        FeatureTierLevel.ProBase,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier2,
        FeatureTierLevel.AppSumoTier3,
    ],
    [FeatureType.SetCustomZoom]: [
        FeatureTierLevel.Pro,
        FeatureTierLevel.ProBase,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier2,
        FeatureTierLevel.AppSumoTier3,
    ],

    // AI
    [FeatureType.NumberOfUsesOfProxiAI]: [
        FeatureTierLevel.Pro,
        FeatureTierLevel.ProBase,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier2,
        FeatureTierLevel.AppSumoTier3,
    ], // This is Pro and Teams but we want to upgrade people to Pro if they are on Basic or Starter

    [FeatureType.NumberOfUsesOfRoutes]: [
        FeatureTierLevel.Pro,
        FeatureTierLevel.ProBase,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier2,
        FeatureTierLevel.AppSumoTier3,
    ], // This is Pro and Teams but we want to upgrade people to Pro if they are on Basic or Starter

    // TEAMS

    // Advanced Tools
    [FeatureType.ChallengePassport]: [
        StandardProAddOns.PassportChallenge,
        StandardProAddOns.HaveItAll,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier3,
    ],
    [FeatureType.Guide]: [
        StandardProAddOns.Guide,
        StandardProAddOns.HaveItAll,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier3,
    ],
    [FeatureType.ScavengerHunt]: [
        StandardProAddOns.ScavengerHunt,
        StandardProAddOns.HaveItAll,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier3,
    ],
    [FeatureType.Trivia]: [
        StandardProAddOns.Trivia,
        StandardProAddOns.HaveItAll,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier3,
    ],
    [FeatureType.Election]: [
        StandardProAddOns.BestOf,
        StandardProAddOns.HaveItAll,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier3,
    ],
    [FeatureType.BrandStandards]: [
        FeatureTierLevel.Teams
    ],
    [FeatureType.TeamSharedBackpack]: [
        FeatureTierLevel.Teams,
    ],
    [FeatureType.AnalyticsAcrossMaps]: [
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier3,
    ],
    [FeatureType.LeadsAcrossMaps]: [
        StandardProAddOns.LeadGeneration,
        StandardProAddOns.HaveItAll,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier3,
    ],
    [FeatureType.EmailCapture]: [
        StandardProAddOns.LeadGeneration,
        StandardProAddOns.HaveItAll,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier3,
    ],
    [FeatureType.EmailCaptureOnPoint]: [
        StandardProAddOns.LeadGeneration,
        StandardProAddOns.HaveItAll,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier2,
        FeatureTierLevel.AppSumoTier3,
    ],
    [FeatureType.CustomDomain]: [
        StandardProAddOns.CustomDomain,
        StandardProAddOns.HaveItAll,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier3,
    ],
    [FeatureType.CustomEmail]: [
        StandardProAddOns.CustomDomain,
        StandardProAddOns.HaveItAll,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier3,
    ],
    [FeatureType.Comments]: [
        StandardProAddOns.CustomForms,
        StandardProAddOns.HaveItAll,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier3,
    ],
    [FeatureType.ApiAccess]: [
        StandardProAddOns.IntegrationAutomation,
        StandardProAddOns.HaveItAll,
        FeatureTierLevel.Teams,
    ],
    [FeatureType.Calendar]: [
        FeatureTierLevel.ProBase,
        StandardProAddOns.Calendar,
        StandardProAddOns.HaveItAll,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier3,
    ],
    [FeatureType.BulletinBoard]: [
        StandardProAddOns.BulletinBoard,
        StandardProAddOns.HaveItAll,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier3,
    ],
    [FeatureType.Directory]: [
        FeatureTierLevel.ProBase,
        StandardProAddOns.HaveItAll,
        FeatureTierLevel.Teams,
        FeatureTierLevel.AppSumoTier3,
    ],
    [FeatureType.Sponsorship]: [
        FeatureTierLevel.ProBase,
        FeatureTierLevel.Pro,
        FeatureTierLevel.Teams,
    ],
};

export const FeatureActionStringMap: {
    [key: string]: string;
} = {
    // Basic

    // Map management
    [FeatureType.CreatorProfiles]: 'creator profiles',
    [FeatureType.Folders]: 'folders',

    // Map sharing
    [FeatureType.QrCodes]: 'qr codes',
    [FeatureType.EmbedLink]: 'embed link',
    [FeatureType.CollaborativeMap]: 'collaborative map',

    // Map settings
    [FeatureType.ProxiAI]: 'Proxi AI',
    [FeatureType.CategoriesAndCustomIcons]: 'categories and custom icons',
    [FeatureType.Tags]: 'tags',
    [FeatureType.CustomUrls]: 'custom URLs',
    [FeatureType.HeaderBasicCustomization]: 'header basic customization',
    [FeatureType.SocialMediaImage]: 'social media image',
    [FeatureType.MapStyling]: 'map styling',
    [FeatureType.CustomMapStyling]: 'custom map styling',
    [FeatureType.CustomShapes]: 'add more shapes and overlays to your map',
    [FeatureType.Upvoting]: 'upvoting',
    [FeatureType.AutomaticallyShowPointCardList]: 'automatically show point card list',
    [FeatureType.PointNumbering]: 'point numbering',
    [FeatureType.PanningBetweenPointsInList]: 'panning between points in list',
    [FeatureType.RequireApprovalOnPoints]: 'require approval on points',
    [FeatureType.SpreadsheetView]: 'spreadsheet view',
    [FeatureType.KmlUpload]: 'KML upload',
    [FeatureType.BasicInsights]: 'basic insights',
    [FeatureType.PointImageUpload]: 'point image upload',

    // STARTER

    // Map management
    [FeatureType.AddMapEditors]: 'invite co-editors',
    [FeatureType.AddFolderEditors]: 'organize your maps with folders',
    [FeatureType.EmailCapture]: 'collect emails from your map viewers',

    // Map sharing
    [FeatureType.PrintableMap]: 'print your map',
    [FeatureType.MakeMapsPrivate]: 'make maps private',
    [FeatureType.ShareMap]: 'share your map',

    // Map settings
    [FeatureType.DisableAlsoFeaturedOn]: 'hide the “also featured on” section',

    // Map settings
    [FeatureType.HeaderLinkOut]: 'add a custom redirect link in your header',
    [FeatureType.HeaderLogo]: 'add a custom header logo',
    [FeatureType.HideHeader]: 'hide the map header',
    [FeatureType.DateTime]: 'add dates and times to points',
    [FeatureType.DisableGoogleImages]: 'hide default images on points',
    [FeatureType.DisableLocation]: 'hide the exact location, addresses, and get directions button',
    [FeatureType.SpecialOffersOnPoints]: 'add “special offers” and deals to points',

    // PRO

    // Map management
    [FeatureType.DuplicateMap]: 'duplicate your map',
    [FeatureType.AdvancedInsights]: 'get detailed analytics',
    [FeatureType.Backpack]: 'access all of your maps',

    // Map sharing

    // Map settings
    [FeatureType.CustomizeAddPointForm]: 'customize the “add point” form text, fields, logic',
    [FeatureType.HideBuildYourOwnMap]: 'hide default, on-map buttons',
    [FeatureType.HideAllCommandCenterButtons]: 'make your maps highly customized',
    [FeatureType.HideAddToMapInPoints]: 'hide default, on-map buttons',
    [FeatureType.AdditionalCustomFormElements]: 'add custom form elements',
    [FeatureType.ZapierAccess]: 'integrate your map with other data sources using Zapier',
    [FeatureType.PasswordProtection]: 'password protect your map',
    [FeatureType.PaywallProtection]: 'charge for access to your map',
    [FeatureType.SetCustomZoom]: 'set min and max zoom levels for your map',
    [FeatureType.EmailCaptureOnPoint]: 'collect emails from your map viewers',
    [FeatureType.CustomizeAboutMapDrawer]: 'customize the “about map” drawer',
    [FeatureType.HideGetRoute]: 'hide the “get route” button',
    [FeatureType.HideMultiSelect]: 'hide the “multi-select” button',
    [FeatureType.HideBookmarkButtons]: 'hide the “bookmark” buttons',
    [FeatureType.CustomFilteringOptions]: 'add custom filtering options',
    [FeatureType.ApiAccess]: 'access to the Proxi API and webhooks for advanced integrations',


    // Counts

    [FeatureType.NumberOfMapsPerEmail]: 'build more proxi maps',
    [FeatureType.PointsPerMap]: 'add more points to your map',
    [FeatureType.NumberOfEditorsPerMap]: 'add more than one co-editor to your map',
    [FeatureType.NumberOfEditorsPerFolder]: 'add more than one co-editor to your folder',
    [FeatureType.NumberOfUsesOfProxiAI]: 'unlock more uses of proxi AI',
    [FeatureType.NumberOfUsesOfRoutes]: 'unlock more uses of routes',
    [FeatureType.NumberOfViewsPerMap]: 'unlock more than 100K views',

    // TEAMS

    // Advanced Tools

    [FeatureType.ChallengePassport]: 'turn your map into a gamified experience',
    [FeatureType.ScavengerHunt]: 'run a scavenger hunt on your map',
    [FeatureType.Trivia]: 'run a trivia game on your map',
    [FeatureType.Election]: 'run a live voting competition on your map',
    [FeatureType.CustomFont]: 'use a custom font',
    [FeatureType.BrandStandards]: 'maintain brand standards',
    [FeatureType.TeamSharedBackpack]: 'share and create unlimited maps across your team',
    [FeatureType.AnalyticsAcrossMaps]: 'get analytics across all your maps',
    [FeatureType.LeadsAcrossMaps]: 'get leads across all your maps',
    [FeatureType.CustomEmail]: 'set a custom reply-to email for all emails sent from your maps',
    [FeatureType.CustomDomain]: 'use a custom domain to share your maps',
    [FeatureType.Calendar]: 'add calendar events to your map',
    [FeatureType.BulletinBoard]: 'create bulletin boards with digital signage slides',
    [FeatureType.Sponsorship]: 'publish sponsorship programs and collect payments from local businesses',

};

export const FeatureUseCaseStringMap: {
    [key: string]: string;
} = {
    // Basic

    // STARTER

    // Map management
    [FeatureType.Backpack]: 'Maintin access to you maps with over 25 points with a Proxi Subscription',
    [FeatureType.AddMapEditors]:
        'Use this feature to keep you and your team on the same page and to easily divide and conquer your map building tasks. For example, you are creating a map for a big event coming up hosted by your organization. You and your teammates might need to collaborate to map certain points of interest for event attendees. ',
    [FeatureType.EmailCapture]:
        'Building an active and engaged email list is one of the most valuable investments a business can make. And with Proxi at your side,it is easier than ever to connect with your audience and keep them coming back for more. For example, you have an event map for a local non-profit organization and want to contact event attendees after.',


    // Map sharing

    [FeatureType.PrintableMap]:
        'Use this feature to print your map, customize the look and feel, and share it with your audience. For example, you are a travel or tourism organization and you want to print your map to hand out to visitors. You can customize the look and feel of your map to match your brand and print it out to hand out to visitors.',

    // Map settings
    [FeatureType.DisableAlsoFeaturedOn]:
        'Use this feature if you want to keep map viewers in your content ecosystem, rather than checking out other Proxi maps.',
    [FeatureType.HeaderLinkOut]:
        'Use this if you have other resources you want to direct map viewers to. For example, you are an editor of a media company and your map represents a visualization of your article, “10 best places to get happy hour in Seattle”. Your redirect link might lead to this article so people can engage further with your resources. ',
    [FeatureType.HideHeader]:
        'Use this if you want to entirely hide the map header here and in the shared version of the map. This is a great feature if you want to embed your map in a website and want to keep the map header from taking up space.',
    [FeatureType.HeaderLogo]:
        'Use this feature if you are a business or organization that wants to have an interactive map that looks like it is an extension of your brand. For example, you are a media company writing about local places to go this weekend. Use a map with your logo to increase brand awareness + trust.',
    [FeatureType.DateTime]:
        'Use this feature to create event maps, craft itineraries, and bring historical maps to life with this incredible feature. For example, you are a travel agent organizing an itinerary for a client. Associate date/times with points that have reservations so your client can visualize their entire itinerary in one spot',
    [FeatureType.DisableGoogleImages]:
        'Use this if you want map points to only show your photographs or none at all. For example, you run a local art walk and the artists want to show photographs of their own work, rather than Google pictures of the location.',
    [FeatureType.AdvancedInsights]:
        'Use this feature to unlock powerful insights into your audience behavior.  Utilize these insights to optimize your revenue-generating partnerships with businesses and refine your own marketing strategy. For example, you run your local Restaurant Week event and you want to understand how many people were interested in each location and how many people used OpenTable to book reservations.',
    [FeatureType.CustomShapes]
        : 'Use this feature to draw custom shapes on your map. For example, you are a real estate agent and you want to draw a custom shape around the neighborhood you specialize in. This will help your audience visualize the area you are talking about.',

    // PRO

    // Map management
    [FeatureType.DuplicateMap]:
        'Use this feature if you want to create many similar maps off a map template. For example, as a travel agent who has every local hotspot mapped in a certain location. You duplicate a map to give a specific client, but only transfer over points they will be interested in.',

    // Map sharing

    // Map settings
    [FeatureType.HideBuildYourOwnMap]:
        'Use this if you want to keep people from navigating away from your map. Removing this callout also gives you an enhanced white label experience, making your map truly look like an in-house tool.',
    [FeatureType.HideAllCommandCenterButtons]:
        'Use this if you want to hide and customize buttons on the map points and the map. It will provide the cleanest map viewing experience.',
    [FeatureType.ZapierAccess]:
        'Use this if you want to connect your Proxi map to other apps and tools. For example, you are a travel agent and you want to connect your map to your CRM to keep track of your clients.',
    [FeatureType.PasswordProtection]:
        'Use this if you want to keep your map private and only accessible to people with the password. For example, you are a travel agent and you want to share your map with a client, but you don’t want anyone else to see it.',
    [FeatureType.PaywallProtection]:
        'Use this if you want to keep your map private and only accessible to people who pay. For example, you are a travel blogger and you want to share your map with your audience, but you want to charge a fee to access it.',
    [FeatureType.EmailCaptureOnPoint]:
        'Use this if you want to collect emails from your map viewers on specific point. For example, you are a real estate agent and you want to collect emails from people who are interested in a specific property.',
    [FeatureType.ApiAccess]: 'Use this if you want to access the Proxi API and webhooks for advanced integrations.',

    // TEAMS
    // Advanced Tools

    [FeatureType.ChallengePassport]:
        'Use this feature to increase foot traffic and conversion to local destinations. For example, your organization might be hosting a *Mocktail Week*. Participating establishments would be placed on the map and visitors would check in upon arrival. Participants receive awards as they check off more places, establishments receive more foot traffic and passport challenge creators see results in real time + get analytics on participation.',
    [FeatureType.ScavengerHunt]:
        'Use this feature to create a fun and engaging scavenger hunt for your audience. For example, you are a local business association and you want to create a scavenger hunt for your local businesses to drive foot traffic to their stores.',
    [FeatureType.Trivia]:
        'Use this feature to create a fun and engaging trivia game for your audience. For example, you are a local business association and you want to create a trivia game for your local businesses to drive foot traffic to their stores.',
    [FeatureType.Election]:
        'Use this feature to engage your audience with a live election, allowing them to vote on one location per category',
    [FeatureType.BrandStandards]:
        'Use this feature to keep your map on brand. For example, you are a media company and you want to keep all of your map on brand with your logo and custom colors.',
};

export const FeatureDescriptionStringMap: {
    [key: string]: string;
} = {
    // STARTER

    // Map management
    [FeatureType.Backpack]:
        'Access all of your maps (including those with more than 25 points) and keep them up to date with a Proxi Subscription',
    [FeatureType.AddMapEditors]:
        'No one likes to map alone! Bring the team together by inviting co-editors and co-owners to build and customize your map. Collaboratively design and build the ultimate Proxi map with the helping hands of others.',
    [FeatureType.EmailCapture]:
        'Supercharge your lead generation with this dynamic toolkit - Proxi Prospect. Capture high-intent leads seamlessly through map pop-ups and point-specific lead inquiry forms. Elevate your marketing strategy by building targeted campaigns and watch your subscriber list grow.',

    // Map sharing

    [FeatureType.PrintableMap]:
        'Print your map! Customize the look and feel of your map and print it out to hand out to visitors. This is a great way to keep your audience engaged with your brand, even when they are offline.',

    // Map settings
    [FeatureType.DisableAlsoFeaturedOn]:
        'Curate and share only what you want. By default, Proxi will highlight other maps that each point is featured on. You can turn this feature off, or customize it so we only highlight other maps of yours.',
    [FeatureType.HeaderLinkOut]:
        'Dont let your audience click away! Include a custom redirect link in your map header to ensure your audience stays immersed in your content, right where you want them.',
    [FeatureType.HideHeader]:
        'Keep your map clean and simple! Selecting this setting will remove the map header from your map and the shared version of your map.',
    [FeatureType.HeaderLogo]:
        'Stamp your brands signature on your map! Add your logo to the top left corner for a white-label effect. Wow your audience with a professional, in-house look for your interactive Proxi Map.',
    [FeatureType.DateTime]:
        'Level up your maps with Date and Time attributes! Check this box to add some time-based magic to your map points. Your viewers can filter and explore points based on different timeframes, making it a truly dynamic experience.',
    [FeatureType.DisableGoogleImages]:
        'Make your map picture perfect! Selecting this setting will remove all Google images from being shown on map points.',
    [FeatureType.AdvancedInsights]:
        'Unlock and unleash audience insights! Discover valuable metrics on your viewership and engagement. Download and share these powerful metrics with your key stakeholders.',
    [FeatureType.CustomShapes]:
        'Draw custom shapes on your map! Selecting this setting will allow you to draw custom shapes on your map. This is a great way to highlight areas of interest, or create custom boundaries.',

    // PRO

    // Map management
    [FeatureType.DuplicateMap]:
        'Multiply your mapping power! Duplicate maps in a snap, effortlessly transferring settings and points to new creations. Streamline customization by leveraging your existing Proxi data.',

    // Map sharing

    // Map settings
    [FeatureType.HideBuildYourOwnMap]:
        'Keep your audience focused on navigating your map, and not building their own. Selecting this will hide Build Your Own Map button from map viewers on your map and within your map points.',
    [FeatureType.HideAllCommandCenterButtons]:
        'Clean and Simple Mapping! Hide Search, List of Points, Share, and Build Your Own Maps buttons in the shared version of the map.',
    [FeatureType.NumberOfUsesOfProxiAI]:
        'Unlock the power of Proxi AI! Proxi AI is a powerful tool that allows you to quickly and easily map information from you blog, instagram, documents and more. Proxi Pro or higher allows you to use Proxi AI up to 25 times per month.',
    [FeatureType.CustomizeAddPointForm]:
        'Customize the fields in your add point form and point information cards to collect exactly the data you need. Add custom text fields, dropdowns, checkboxes, and more. Perfect for collecting vendor information, real estate listings, event details, restaurant menus, or any structured data specific to your use case.',
    [FeatureType.NumberOfUsesOfRoutes]:
        'Unlock the power of Proxi Routes! Proxi Routes is a powerful tool that allows you to quickly and easily map out a route between points. Proxi Pro or higher allows you to use Proxi Routes up to 25 times per month. Need More? No problem! You can purchase additional uses of Proxi Routes by contacting help@proxi.co.',
    [FeatureType.PasswordProtection]:
        'Unlock the power of privacy and profitability with the Gated Access toolkit. Monetize your content with Paywalls, setting your price and enjoying a steady stream of monthly earnings. For maps meant for select eyes only, use Passwords to ensure your map stays private. Share your secret code with chosen viewers or keep it to yourself.',
    [FeatureType.PaywallProtection]:
        'Unlock the power of privacy and profitability with the Gated Access toolkit. Monetize your content with Paywalls, setting your price and enjoying a steady stream of monthly earnings. For maps meant for select eyes only, use Passwords to ensure your map stays private. Share your secret code with chosen viewers or keep it to yourself.',

    // TEAMS
    // Advanced Tools

    [FeatureType.ChallengePassport]:
        'Proxi Passport Challenge is the ultimate tool for crafting immersive and unique community-driven experiences. This digital alternative to traditional stamped passport booklets engages participants, drives foot traffic, and boosts revenue. Set up a challenge in 45 minutes on our self-service platform and share easily with just a link, no external app downloads are required! Enhance your marketing strategy and boost economic impact while ensuring participants enjoy a seamless experience and rewarding journey.',
    [FeatureType.ScavengerHunt]:
        'Proxi Scavenger Hunt transforms your map into an interactive and engaging experience. Create a scavenger hunt on your map to engage your audience and drive foot traffic to your locations. Participants will be able to check off locations as they visit them and you will be able to see results in real time.',
    [FeatureType.Trivia]:
        'Proxi Trivia transforms your map into an interactive and engaging experience. Create a trivia game on your map to engage your audience and drive foot traffic to your locations. Participants will be able to answer questions as they visit locations and you will be able to see results in real time.',
    [FeatureType.Election]:
        'The Best Of voting campaigns transform how your community engages with local favorites. Empower your audience to nominate and vote for top spots with our custom forms. Once nominations are in, seamlessly transition to the voting phase. Collect voter email addresses and tally votes to declare the winners. Proxi makes engaging your community and celebrating local favorites seamless and efficient.',
    [FeatureType.CustomFont]:
        'Customize your map with a custom font! Use your own font file to use in your map header and point cards.',
    [FeatureType.BrandStandards]:
        'Keep your maps on brand! Use your own custom colors and logo to keep all of your on brand with ease.',
    [FeatureType.TeamSharedBackpack]:
        'Co-manage lots of maps with your team with Proxi Teams. Share a backpack with your team to easily share points, folders, and more.',
    [FeatureType.AnalyticsAcrossMaps]:
        'Unlock powerful analytics across all of your maps. View your analytics across all of your maps in one place.',
    [FeatureType.LeadsAcrossMaps]:
        'Unlock powerful leads across all of your maps. View your leads across all of your maps in one place.',
    [FeatureType.CustomEmail]:
        'Set a custom reply-to email for all emails sent from your maps. This is a great way to keep your brand front and center in all communications.',
    [FeatureType.CustomDomain]:
        'Use a custom domain to share your maps. This is a great way to keep your brand front and center in all communications.',
    [FeatureType.ApiAccess]:
        'Use this if you want to access the Proxi API and webhooks for advanced integrations.',
    [FeatureType.Guide]:
        'Turn your map into a guide people register for and keep. When a visitor signs up, you capture their contact info and can send them messages, deals, and updates anytime. Use it for local coupon books, insider neighborhood guides, membership perks, or influencer recommendation lists. Every registration is a direct line to a real person who cares about your district.',

};

export const AddOnShortDescriptionMap: {
    [key: string]: string;
} = {
    [StandardProAddOns.CustomForms]: 'Customize the add-a-point form with personalized fields and dynamic question logic.',
    [StandardProAddOns.PasswordPaywall]: 'Protect your maps with passwords and boost revenue with paywalls.',
    [StandardProAddOns.LeadGeneration]: 'Generate, capture, and nurture leads with your map.',
    [StandardProAddOns.PassportChallenge]: 'Increase foot traffic with a digital alternative to a traditional stamped passport booklet.',
    [StandardProAddOns.Guide]: 'A branded guide visitors register for and save — with ongoing SMS and email communication.',
    [StandardProAddOns.ScavengerHunt]: 'Create a scavenger hunt on your map to engage your audience.',
    [StandardProAddOns.Trivia]: 'Add trivia questions to your map to engage your audience.',
    [StandardProAddOns.BestOf]: 'Nominate and vote for favorite places directly in your map.',
    [StandardProAddOns.IntegrationAutomation]: 'Integrate your map with other tools and automate workflows with API and Webhook Access.',
    [StandardProAddOns.CustomDomain]: 'Use a custom domain to share your maps and reply-to email for all emails sent from your maps.',
    [StandardProAddOns.HaveItAll]: 'Unlock all features that come with standard add ons',
};

export const FeatureImageMap: {
    [key: string]: string;
} = {
    // Basic

    // Map management

    // STARTER

    // Map management
    [FeatureType.AddMapEditors]: '/images/upgradeimages/coeditors.png',

    // Map sharing

    [FeatureType.PrintableMap]: '/images/upgradeimages/printablemap.png',

    // Map settings
    [FeatureType.DisableAlsoFeaturedOn]: '/images/upgradeimages/alsofeaturedon.png',

    // Map settings
    [FeatureType.HeaderLogo]: '/images/upgradeimages/headerlogo.png',
    [FeatureType.DisableGoogleImages]: '/images/upgradeimages/hidegoogleimages.png',
    [FeatureType.AdvancedInsights]: '/images/upgradeimages/insights.png',
    [FeatureType.CustomShapes]: '/images/upgradeimages/customshapes.png',

    // PRO

    // Map management
    [FeatureType.DuplicateMap]: '/images/upgradeimages/duplicate.png',

    // Map sharing

    // Map settings
    [FeatureType.HideBuildYourOwnMap]: '/images/upgradeimages/buildyourmapbutton.png',
    [FeatureType.HideAllCommandCenterButtons]: '/images/upgradeimages/hidecommandcenterbuttons.png',
    [FeatureType.PasswordProtection]: '/images/upgradeimages/passwordpaywall.png',
    [FeatureType.PaywallProtection]: '/images/upgradeimages/passwordpaywall.png',

    // Count
    [FeatureType.NumberOfUsesOfProxiAI]: '/images/upgradeimages/aiuses.gif',

    // TEAMS
    // Advanced Tools

    [FeatureType.ChallengePassport]: '/images/upgradeimages/challenge.png',
    [FeatureType.Election]: '/images/upgradeimages/election.png',
    [FeatureType.CustomizeAddPointForm]: '/images/upgradeimages/customforms.png',
    [FeatureType.AdditionalCustomFormElements]: '/images/upgradeimages/customforms.png',
    [FeatureType.AutotranslationLanguages]: '/images/upgradeimages/translation.png',
    [FeatureType.EmailCaptureOnPoint]: '/images/upgradeimages/leadgen.png',
    [FeatureType.EmailCapture]: '/images/upgradeimages/leadgen.png',
};

export enum TagCategoryDataType {
    Tag = 'Tag',
    TagGroup = 'Tag Group',
    Category = 'Category',
    ChallengeReward = 'Challenge Reward'
}

export interface TagInformation {
    google_tags: string[];
    user_tags: UserTag[];
}

export interface TagRequest {
    _id: ObjectId | undefined;
    name: string;
    icon: string;
    can_crowdsource?: boolean;
    tags?: TagRequest[];
}

export interface UserTag {
    _id: ObjectId;
    name: string;
    icon: string;
    can_crowdsource?: boolean;
    tags?: UserTag[];
}


export interface AddUserTagRequest {
    _id: ObjectId;
}

export interface AddTagRequest {
    user_tags: AddUserTagRequest[];
}

export interface CreateTagRequest {
    user_tags: TagRequest[];
}


export enum DiscoveryLevel {
    Private = 'private',
    ReadPublic = 'read_public',
    AddpointPublic = 'addpoint_public'
}

/**
 * Which map rendering engine a topic uses for its viewer.
 * New topics from the React app default to MapLibre; existing topics stay on Google
 * until explicitly migrated.
 */
export enum MapRenderer {
    Google = 'google',
    MapLibre = 'maplibre',
}

export interface SearchFilter {
    discovery_level?: DiscoveryLevel;
    minimum_point_count?: number;
    source_type?: string;
    creator_profiles?: ObjectId[];
    keyword_search?: KeywordSearch;
    location_match?: LocationMatch;
    /** Match points linked to this listing. Supersedes location_match. */
    directory_entry_id?: ObjectId;
    sort?: TopicSearchSort;
    organization_ids?: ObjectId[];
    num_results?: number;
}

export enum TopicSearchSort {
    SevenDayTopicViews = 'seven_day_views',
    FourteenDayTopicViews = 'fourteen_day_views',
    ThirtyDayTopicViews = 'thirty_day_views',
    TotalPointViews = 'total_point_views',
    TotalTopicViews = 'total_topic_views',
    TotalPointCount = 'total_point_count'
}

export interface LocationMatch {
    location: Location;
    max_distance: number;
}

export interface KeywordSearch {
    operator: CombineOperator;
    keywords: string[];
    sub_searches: KeywordSearch[];
}

export enum CombineOperator {
    And = 'and',
    Or = 'or'
}

export interface OpenTableInformation {
    id: ObjectId;
    url: string;
}

export interface AccessItem {
    name: string;
    value: string;
    limited?: boolean;
}

export interface PortalSession {
    id: string;
    token: string;
    access_url: string;
    status: string;
    created_at: number;
    expires_at: number;
    object: string;
    customer_id: string;
    redirect_url: string;
}

export const AccessCollection: {
    [key: string]: AccessItem[];
} = {
    [EntityType.Topic]: [
        {
            name: 'Owner',
            value: 'Owner',
            limited: true
        },
        {
            name: 'Editor',
            value: 'Editor'
        }
    ],
    [EntityType.Folder]: [
        {
            name: 'Owner',
            value: 'Owner',
            limited: true
        },
        {
            name: 'Editor',
            value: 'Editor'
        },
        {
            name: 'Collaborator',
            value: 'Collaborator'
        }
    ],
    [EntityType.Organization]: [
        {
            name: 'Administrator',
            value: 'Administrator'
        },
        // this one is named differently because it confused people and also we don't want to re-do the back end name right now
        {
            name: 'Editor',
            value: 'Owner'
        },
        {
            name: 'Collaborator',
            value: 'Collaborator'
        }
    ]
};

export interface CategoryImageUpload {
    _id: ObjectId | undefined;
    image: ImageUpload;
}

export type CreateTopicGateRequest = CreateTopicPasswordGateRequest | CreateTopicPaymentGateRequest | CreateTopicEmailGateRequest;

export interface CreateTopicPasswordGateRequest {
    Password: {
        _id?: ObjectId;
        name: string;
        password?: string;
        level: TopicPermissionLevel;
        is_active: boolean;
    };
}

export interface CreateTopicPaymentGateRequest {
    Payment: {
        _id?: ObjectId;
        name: string;
        price_in_cents: number;
        charge_id: string;
        level: TopicPermissionLevel;
        is_active: boolean;
    };
}

export interface CreateTopicEmailGateRequest {
    Email: {
        _id?: ObjectId;
        name: string;
        level: TopicPermissionLevel;
        is_active: boolean;
        require_opt_in: boolean;
        opt_in_language: string;
        require_verification: boolean;
    };
}

export type GetTopicGateResponse = GetTopicPasswordGateResponse | GetTopicEmailGateResponse;

export type GateDetails = PasswordGateDetails | EmailGateDetails;

export interface GetTopicPasswordGateResponse {
    Password: PasswordGateDetails
}

export interface PasswordGateDetails {
    _id: ObjectId;
    password: string;
    name: string;
    level: TopicPermissionLevel;
    is_active: boolean;
}



export interface GetTopicEmailGateResponse {
    Email: EmailGateDetails
}

export interface EmailGateDetails {
    _id: ObjectId;
    name: string;
    level: TopicPermissionLevel;
    is_active: boolean;
    require_opt_in: boolean;
    opt_in_language: string;
    require_verification: boolean;
}

export enum GateType {
    Paywall = 'Paywall',
    Password = 'Password',
    Email = 'Email'
}

export interface PaywallNote {
    paywall: PaywallNoteDetails;
}

export interface ChallengeNote {
    challenge: ChallengeNoteDetails;
}

export interface PaywallNoteDetails {
    email: string;
    topic_id: string;
    gate_id: string;
}

export interface ChallengeNoteDetails {
    email: string;
    challenge_id: string;
    activated_challenge_id: string;
}

export interface ChargebeeHostCheckoutNewRequest {
    purchase_items: ChargebeeCheckoutItem[],
    email?: string,
    organization_id?: string,
    referral_code?: string,
}

export interface ChargebeeCheckoutItem {
    item_price_id: string,
    price_in_cents?: number,
    quantity?: number,
}

export interface ChargebeeHostCheckoutExistingRequest {
    customer_id: string,
    existing_subscription_id: string,
    purchase_items: ChargebeeCheckoutItem[],
    replace_items: boolean,
    organization_id?: string,
    is_reactivation?: boolean,
    referral_code?: string,
}

export interface ChargebeeEstimateRequest {
    existing_subscription_id?: string,
    purchase_items?: ChargebeeCheckoutItem[],
    is_reactivation?: boolean,
    customer_id?: string,
}

export interface ChargebeeEstimate {
    created_at: number;
    object: string;
    subscription_estimate?: SubscriptionEstimate;
    subscription_estimates?: SubscriptionEstimate[];
    invoice_estimate?: NextInvoiceEstimate;
    invoice_estimates?: NextInvoiceEstimate[];
    next_invoice_estimate?: NextInvoiceEstimate;
}

export interface SubscriptionEstimate {
    resume_date?: number;
    pause_date?: number;
    next_billing_at?: number;
    trial_end_action?: string;
    status?: string;
    currency_code: string;
    id?: string;
}

export interface NextInvoiceEstimate {
    customer_id?: string;
    round_off_amount?: number;
    amount_due?: number;
    amount_paid?: number;
    credits_applied?: number;
    total?: number;
    sub_total: number;
    currency_code: string;
    price_type: string;
    recurring: boolean;
    date: number;
    line_items: LineItem[];
    discounts?: Discount[];
    taxes?: string[];
    line_item_taxes?: string[];
    line_item_discounts?: LineItemDiscount[];
}

export interface LineItem {
    id: string;
    date_from?: number;
    date_to?: number;
    unit_amount?: number;
    quantity?: number;
    amount: number;
    pricing_model: string;
    is_taxed: boolean;
    tax_amount: number;
    object: string;
    subscription_id?: string;
    customer_id: string;
    description: string;
    entity_type: string;
    entity_id: string;
    discount_amount: number;
    item_level_discount_amount: number;
}

export interface Discount {
    object: string;
    entity_type: string;
    description: string;
    amount: number;
    entity_id: string;
    discount_percentage: number;
    discount_type: string;
}

export interface LineItemDiscount {
    object: string;
    line_item_id: string;
    discount_type: string;
    discount_amount: number;
    coupon_id: string;
    entity_id: string;
}

export interface EstimateSummary {
    due_today: number;
    due_next: number;
    recurring_cost: number;
    /**
     * What the next full billing period costs with the customer's coupons applied, in cents.
     * The hardcoded price maps know list prices only, so this is the only coupon-aware total.
     */
    next_renewal_total?: number;
    credits: number;
    applied_discounts: string[];
    estimate_items: EstimateItemSummary[];
    purchase_object: ChargebeeCheckoutItem[];
}

export interface EstimateItemSummary {
    pretty_name: string;
    plan_id: string;
    amount: number;
    discount_amount: number;
    quantity: number;
}

export interface ChargebeeHostPortalRequest {
    customer_id: string,
    redirect_url: string,
}

export interface ChargebeeHostResponse {
    hosted_page: {
        id: string,
        url: string,
    }
}

export interface ChargebeeHostPortalResponse {
    portal_session: PortalSession
}

export interface OrganizationSubscription {
    _id: ObjectId;
    organization_id: ObjectId;
    subscription_id: string;
    buyer_email: string;
    product_id?: ObjectId;
    purchase_id?: ObjectId;
    status: string;
    interval?: ProductPeriod;
    price_cents?: number;
    currency?: string;
    last_invoice_id?: string;
    cancelled_at?: Date;
    created_at: Date;
    updated_at: Date;
    provider: PaymentProvider;
    support_email?: string;
}

export interface OrganizationSubscriptionResponse {
    organization_subscription: OrganizationSubscription;
    transaction_history: PurchaseSummaryResponse[];
}

export interface ActivatedChallengePurchasesResponse {
    organization_subscriptions: OrganizationSubscription[];
    transaction_history: PurchaseSummaryResponse[];
}

export interface EarnedReward {
    reward_id?: ObjectId;
    title: string;
    image_url: string;
    count: number;
}

export interface CheckinInfo {
    all_timestamps: string[];
    counted_timestamps: string[];
    categoryIcon: string;
    categoryName: string;
    placeName: string;
    pointValue: number;
    location: Location | undefined;
    challenge_location_id: ObjectId | undefined;
    receipts: ActivatedChallengeReceipt[];
}

export enum DrawingMode {
    Marker = 'marker',
    Circle = 'circle',
    Polygon = 'polygon',
    Line = 'line',
    Rectangle = 'rectangle'
}

export enum FilterType {
    MyMaps = 'MyMaps',
    AllMaps = 'All',
    SharedMaps = 'SharedMaps',
    ContributedMaps = 'ContributedMaps',
    PublishedMaps = 'PublishedMaps',
    BookmarkedMaps = 'BookmarkedMaps',
    PersonalMaps = 'PersonalMaps'
}

export enum TeamSettingsType {
    General = 'General',
    ManageTeamMembers = 'ManageTeamMembers',
    SubscriptionAndBilling = 'SubscriptionAndBilling',
    Integrations = 'Integrations',
    WhiteLabelling = 'WhiteLabelling',
    UsageAndLimits = 'UsageAndLimits',
    Payments = 'Payments',
}

export enum AccountSettingsType {
    General = 'General',
    SubscriptionAndBilling = 'SubscriptionAndBilling',
}

export interface SettingsOption {
    label: string;
    img: string;
    settingSection: TeamSettingsType | AccountSettingsType;
}
export interface OsmTimezoneResponse1 {
    id: number;
    identifier: string;
    shortIdentifier: string;
    rawOffset: number;
    rawBaseOffset: number;
    rawDstOffset: number;
    zone: number;
    currentTime: string;
}

// Stats

export enum StatType {
    UserMapsCreated = 'UserMapsCreated',
    TotalMapViews = 'TotalMapViews',
    UserPlacesVisited = 'UserPlacesVisited',
    TotalPointClicks = 'TotalPointClicks',
    TotalMapPoints = 'TotalMapPoints',
    EmailsCaptured = 'EmailsCaptured',
    ChallengeParticipants = 'ChallengeParticipants',
    AdvertisingRevenue = 'AdvertisingRevenue',
    PaywallRevenue = 'PaywallRevenue',
    MonthlyViews = 'MonthlyViews',
    MonthlyEngagements = 'MonthlyEngagements',
    TrendingMap = 'TrendingMap',
    MostEngagedMap = 'MostEngagedMap',
    MostViewedMap = 'MostViewedMap',
}

export interface IStats {
    name: string;
    calculation: string;
    value: number;
    action_string?: string;
    action_function?: any;
    topic?: GetTopicResponse;
    increase?: number;
    blocked?: boolean;
    moreInfoSubscribed?: string;
    moreInfoSubscribedAction?: any;
    moreInfoNotSubscribed?: string;
    moreInfoNotSubscribedAction?: any;
    subscriptionNeed?: boolean;
}

interface BaseSettings {
    fieldName: string;
    title: string;
    description?: string;
    isTopic?: boolean; // Add support for topic settings this is used to decide what callback event to use & add topic verification
    widthPercentage?: number; // use to make the width of the setting smaller to fit multiple on a row
    hasEntitlement?: boolean; // check if user has access: usually accountInfo.Xenablesd
    featureType?: FeatureType; // used to support opening upgrade modal
    isSubfield?: boolean; // should be featured BELOW another setting
    isHidden?: boolean; // for subfields only. Should it be hidden if the parent setting is false?
    subfields?: string[]; // for subfields only. List of subfields to show if the parent setting is true
}

interface TextSettingsDetails extends BaseSettings {
    fieldType: 'string';
    inputType: 'dropdown' | 'inputShort' | 'inputLong' | 'color' | 'image';
    textCharLimit?: number;
    initialValueString?: string;
    dropdownOptions?: Array<{
        value: string;
        label: string;
    }> | undefined;
}

interface NumberSettingsDetails extends BaseSettings {
    fieldType: 'number';
    inputType: 'slider' | 'inputShort';
    numberMax?: number;
    numberMin?: number;
    sliderStep?: number;
    initialValueNumber?: number;
}

interface ToggleSettingsDetails extends BaseSettings {
    fieldType: 'boolean';
    inputType: 'toggle';
    flippedLanguage: boolean; // e.g. if we store the value as "hide X" but the toggle is "show X"
    initialValueBoolean?: boolean;
}

interface VectorSettingsDetails extends BaseSettings {
    fieldType: 'vector';
    inputType: 'order' | 'orderOption' | 'multiSelect';
    initialValueVector?: any[];
    vectorOptions?: Array<{text: string; value: any}>; // used for orderOption and multiSelect
}


export type SettingStructure = TextSettingsDetails | NumberSettingsDetails | ToggleSettingsDetails | VectorSettingsDetails;


export enum PointOrderingOption {
    Alphabetical = 'Alphabetical',
    Category = 'Category',
    Distance = 'Distance',
    Start = 'Start',
    Number = 'Number',
}

/**
 * Reusable list sort model shared by every Collection-backed view (Map point
 * list today; Directory + Challenge later). Clauses apply left-to-right.
 */
export interface SortSpec {
    clauses?: SortClause[];
}

export interface SortClause {
    key: SortKey;
    /** The Collection custom field to sort by — set only when key === Field. */
    field_id?: ObjectId;
    direction: SortDirection;
}

export enum SortKey {
    Name = 'name',
    Address = 'address',
    Distance = 'distance',
    Date = 'date',
    Category = 'category',
    Number = 'number',
    Created = 'created',
    Featured = 'featured',
    Random = 'random',
    Field = 'field',
}

export enum SortDirection {
    Asc = 'asc',
    Desc = 'desc',
}

// Tiquts Types

interface TiqetsResponseVenue {
    id: string;
    name: string;
    postal_code: string;
    address: string;
}

interface TiqetsResponseRatings {
    total: number;
    average: number;
}

export interface TiqetsResponseProduct {
    id: string;
    language: string;
    title: string;
    city_id: string;
    country_id: string;
    tag_ids: string[];
    images: string[]; // You can replace this with the actual image type
    product_url: string;
    product_checkout_url: string;
    is_package: boolean;
    country_name: string;
    city_name: string;
    tagline: string;
    promo_label: string | null;
    geolocation: LatLng;
    distance: number;
    venue: TiqetsResponseVenue;
    ratings: TiqetsResponseRatings;
    price: number;
    prediscount_price: number | null;
    product_slug: string;
    languages: string[];
    sale_status: string;
    sale_status_reason: string;
    sale_status_expected_reopen: string | null;
    summary: string;
    display_price: number;
    display_booking_fee: number | null;
    skip_line: boolean;
    smartphone_ticket: boolean;
    advance_arrival_time: string | null;
    last_admission_window: string | null;
    description: string | null;
    discount_percentage: number;
}

interface TiqetsResponsePagination {
    page: number;
    page_size: number;
    total: number;
}

export interface TiqetsResponse {
    success: boolean;
    api_version: object;
    products: TiqetsResponseProduct[];
    pagination: TiqetsResponsePagination;
}

export enum PreviewType {
    Mobile = 'mobile',
    Desktop = 'desktop'
}

export enum OnboardingState {
    Intent = 'intent',
    Prompt = 'prompt',
    Category = 'category',
    Points = 'points',
    Subscribe = 'subscribe',
    Upgrade = 'upgrade',
    Inspiration = 'inspiration',
}

export enum IntentType {
    Map = 'map',
    Guide = 'guide',
    Passport = 'passport',
    Hunt = 'hunt',
    Event = 'event',
    BestOf = 'bestof',
}

// Try to keep aligned with KlavioEvent in backend
export enum KlayvioEvents {
    MapCreated = 'Map Created',
    MapUpdated = 'Map Updated',
    MapDeleted = 'Map Deleted',
    InvitedUserToMap = 'Invited User To Map',
    RemovedEditor = 'Removed Editor',
    WasInvitedToMap = 'Was Invited to Map',
    CheckoutClicked = 'Checkout Clicked',
    UpgradeModalOpened = 'Upgrade Modal Opened',
    DemoBooked = 'Demo Booked',
    DemoFormStarted = 'Demo Form Started',
    DemoVideoClicked = 'Demo Video Clicked',
    FeatureInterestRegistered = 'Feature Interest Registered',
}

// Try to keep aligned with KlavioEventPropertyKey in backend
export enum KlavioEventPropertyKey {
    EditorLink = 'Editor Link',
    ReadOnlyLink = 'Read Only Link',
    CollaborativeLink = 'Collaborative Link',
    MapId = 'Map ID',
    MapName = 'Map Name',
    MapIntentType = 'Map Intent Type',
    MapLocation = 'Map Location',
    PointId = 'Point ID',
    PointName = 'Point Name',
    InvitedEmail = 'Invited Email',
    InviteeEmail = 'Invitee Email',
    UpgradeTier = 'Upgrade Tier',
    UpgradeFeature = 'Upgrade Feature',
    RemovedEmail = 'Removed Email',
    ChallengeName = 'Challenge Name',
    ChallengeId = 'Challenge ID',
    MapImage = 'Map Image',
    FeatureName = 'Feature Name',
}

export interface KlavioEventProperty {
    key: KlavioEventPropertyKey;
    value: string;
}

export enum KlaviyoPropertyKey {
    FirstMapCreationDate = 'Proxi FirstMapCreationDate',
    IndustryLevel1 = 'Proxi IndustryLevel1',
    IndustryLevel2 = 'Proxi IndustryLevel2',
    DomainType = 'Proxi DomainType',
    OrganizationIDs = 'Proxi OrganizationIDs',
    BusinessCategories = 'Proxi BusinessCategories',
    UseCases = 'Proxi UseCases',
    CompanySizes = 'Proxi CompanySizes',
    CompanyWebsites = 'Proxi CompanyWebsites',
    MapsMadeBeforeKlaviyoTransition = 'Proxi MapsMadeBeforeKlaviyoTransition',
    MapRoleOwnerDataUpdate = 'Proxi MapRoleOwnerDataUpdate',
    MostPopularMapLastWeekID = 'Proxi MostPopularMapLastWeekID',
    MostPopularMapLastWeekName = 'Proxi MostPopularMapLastWeekName',
    MostPopularMapLastWeekTotalViews = 'Proxi MostPopularMapLastWeekTotalViews',
    MostPopularMapLastWeekGrowthPercentLastWeek = 'Proxi MostPopularMapLastWeekGrowthPercentLastWeek',
    MostPopularMapLastWeekNewViews = 'Proxi MostPopularMapLastWeekNewViews',
    MostPopularMapLastWeekEditLink = 'Proxi MostPopularMapLastWeekEditLink',
    MostPopularMapLastWeekReadLink = 'Proxi MostPopularMapLastWeekReadLink',
    MostPopularMapLastWeekImage = 'Proxi MostPopularMapLastWeekImage',
    MostPopularMapAllTimeID = 'Proxi MostPopularMapAllTimeID',
    MostPopularMapAllTimeName = 'Proxi MostPopularMapAllTimeName',
    MostPopularMapAllTimeTotalViews = 'Proxi MostPopularMapAllTimeTotalViews',
    MostPopularMapAllTimeGrowthPercentLastWeek = 'Proxi MostPopularMapAllTimeGrowthPercentLastWeek',
    MostPopularMapAllTimeNewViews = 'Proxi MostPopularMapAllTimeNewViews',
    MostPopularMapAllTimeImage = 'Proxi MostPopularMapAllTimeImage',
    MostPopularMapAllTimeEditLink = 'Proxi MostPopularMapAllTimeEditLink',
    MostPopularMapAllTimeReadLink = 'Proxi MostPopularMapAllTimeReadLink',
    UserRegions = 'Proxi UserRegions',
    ChargbeePlanIDs = 'Proxi ChargbeePlanIDs',
    ChargebeeAddOns = 'Proxi ChargebeeAddOns',
    EditorTopicIds = 'Proxi EditorTopicIds',
    ActiveInterface = 'Proxi ActiveInterface',
}

export interface TagSearchType {
    operator: CombineOperator;
    tags: TopLevelTagSearchType[] | SubTagSearchType[];
}

export interface TopLevelTagSearchType {
    topLevelTag: string;
}

export interface SubTagSearchType extends TopLevelTagSearchType {
    operator: CombineOperator;
    subTags: string[];
}

export interface BoundsPadding {
    top: number;
    right: number;
    bottom: number;
    left: number;
}

export enum MapUrlParams {
    Latitude = 'lat',
    Longitude = 'lng',
    Zoom = 'zoom',
    FocusPointId = 'point_id',
    SelectedCategories = 'categories',
    SelectedTags = 'tags',
}

export interface MapUrlParamsType {
    [MapUrlParams.Latitude]: number;
    [MapUrlParams.Longitude]: number;
    [MapUrlParams.Zoom]: number;
    [MapUrlParams.FocusPointId]: string;
    [MapUrlParams.SelectedCategories]: string[];
    [MapUrlParams.SelectedTags]: string[];
}

export interface MapSetUpOptions {
    center_lat: number | undefined;
    center_lng: number | undefined;
    zoom: number | undefined;
    focusPointId: string | undefined;
    selectedCategories: string[] | undefined;
    selectedTags: string[] | undefined;
    focusChallengeLocationId: string | undefined;
}

export interface GooglePlaceTypeSearch {
    google_place_id: string,
    autocomplete_id?: string,
    search_terms: string[],
    icon?: string,
}

export interface circle {
    center: LatLng;
    radius: number;
}

export interface viewport {
    high: LatLng;
    low: LatLng;
};

export interface TopicWebhook {
    AddPoint: WebhookDetails;
    EditPoint: WebhookDetails;
    DeletePoint: WebhookDetails;
}

export interface WebhookDetails {
    url: string;
    failure_notificaiton_email: string;
}

// Knowingly taking on some technical debt here. This is a temporary solution to a problem that is rare.
export enum IntegrationException {
    WelcomeHomeRelocations = 'Welcome Home Relocations',
    JLLRealEstate = 'JLL Real Estate',
    AustraliaMeatAndLivestock = 'Australia Meat and Livestock',
   TheGoSolution = 'The Go Solution',
    TrudiFord = 'Trudi Ford',
    DefHero = 'Def Hero',
    Telco = 'Telco',
    WilliamEScooter = 'E-scooter',
    ProsperityMarket = 'Prosperity Market',
    SeattleGood = 'Seattle Good',
    TeamBuildingAnywhere = 'Team Building Anywhere',
    HighStreetSafari = 'High Street Safari',
    Roswell = 'Roswell',
    FranchiseDemographics = 'Franchise Demographics',
}

export interface TranslationLanguage {
    language: string;
    icon: string | undefined;
}

export interface WeglotTranslateOptions {
    words: Array<{ t: number; w: string; }>;
    languageTo: string;
}

export interface TranslationApiResponse {
    l_from: string;
    l_to: string;
    request_url: string;
    bot: number;
    title: string;
    from_words: string[];
    to_words: string[];
    ids: number[];
}

export interface TranslationElement {
    original_text: string;
    translated_text: string;
    language: string;
    id?: string;
}

export interface TranslationSet{
    id: ObjectId;
    organization_id: ObjectId;
    language: string;
    translations: TranslationElement[];
    created_at: string;
    updated_at: string;
}

export enum TranslationType {
    Custom,
    Weglot
}

export interface TranslationWithType {
    type: TranslationType;
    element: TranslationElement;
}

export interface TranslationPhrases {
    [key: string]: string;
}

// Searchable Elements
export interface SearchablePoint {
    point: GetPointResponse,
    terms: string[],
}

// Search Types
export enum AutocompleteSearchType {
    Point = 'point',
    Category = 'category',
    Tag = 'tag',
    GoogleSearch='place (world search)',
    Keyword = 'keyword',
}

export interface OverlayPosition {
    radius: number;
    center: LatLng;
    rotation: number;
}

// Election Related Types

export interface CreateElectionRequest {
    name: string;
    active: boolean;
    description: string;
    category_descriptions: ElectionCategoryDescription[];
    email_terms: string;
    email_opt_in_required: boolean;
    verify_email: boolean;

    nomination_is_live: boolean;
    show_leaders?: number;

    start_time?: string;
    end_time?: string;

    point_button_text: string;
    submit_button_text: string;
    multi_select: boolean; // user can vote for more than one place per category
    frequency_quantity: number;
    frequency_recurrance: 'daily' | 'weekly' | 'monthly' | 'once' | 'unlimited';
    check_ip_address: boolean;

    excluded_categories: ObjectId[];
    excluded_points: ObjectId[];
}

export interface CreateElectionResponse {
    _id: ObjectId;
}

export interface GetElectionResponse {
    _id: ObjectId;
    topic_id: ObjectId;
    active: boolean;
    name: string;
    description: string;
    category_descriptions: ElectionCategoryDescription[];
    email_terms: string;
    email_opt_in_required: boolean;
    verify_email: boolean;

    nomination_is_live: boolean;
    show_leaders?: number;

    start_time?: string;
    end_time?: string;

    point_button_text: string;
    submit_button_text: string;
    multi_select: boolean; // user can vote for more than one place per category
    frequency_quantity: number;
    frequency_recurrance: 'daily' | 'weekly' | 'monthly' | 'once' | 'unlimited';
    check_ip_address: boolean;

    excluded_categories: ObjectId[];
    excluded_points: ObjectId[];

    show_photo_feed: boolean;

    link_set?: ElectionLinkSet;

    created_at: string;
    updated_at: string;
}

export interface ElectionLinkSet {
    ballot_link: string;
    nomination_link: string;
}

export interface CreateBallotRequest {
	topic_id: ObjectId;
    election_id: ObjectId;
	email: string;
	ip_address?: string;
	email_opt_in: boolean;
	email_opt_in_language: string;

	timestamp: string;
	ballot: BallotSection[];
}

export interface CreateBallotResponse {
    _id: ObjectId;
    timestamp: string;
}

export interface GetBallotResponse {
    _id: ObjectId;
    timestamp: string;
    topic_id: ObjectId;
    email: string;
    ip_address: string;
    email_opt_in: boolean;
    email_opt_in_language: string;

    ballot: BallotSection[];

    created_at: string;
    updated_at: string;
}

export interface PendingVote {
    topic_category_id: ObjectId;
    selected_points: ObjectId[];
}
export interface BallotSection {
    category_id: ObjectId;
    selected: BallotSelection[];
}

export interface BallotSelection {
    point_id: ObjectId;
    point_name?: string;
}

export interface ElectionStanding {
    category_id: string;
    leaders: ElectionPlace[];
}

export interface ElectionPlace {
    point_id: string;
    votes: number;
}

export interface VoterDetail {
    email: string;
    opt_in: boolean;
    opt_in_language: string;
    num_votes: number;
}

export interface ElectionCategoryDescription {
    category_id: string;
    description: string;
    // add unique short link here
}


export interface ChallengeLocationSummary {
    point_info: GetPointResponse | undefined;
    challenge_location_info: ChallengeLocation | undefined;
    checked_in: boolean | undefined;
    gave_up: boolean | undefined;
    used_hint: boolean | undefined;
    earned_points: number | undefined;
    icon: string | undefined;
    geo_location_eligible: boolean | undefined;
    distance: number | undefined;
}

export interface ChallengeLocationFilters {
    nearby_sort: 'asc' | 'desc' | undefined;
    distance: number | undefined;
    active: boolean;
    checked_in: boolean | 'all';
    geo_location_eligible: boolean | undefined;
    value_sort: 'asc' | 'desc' | undefined;
    point_value_min: number | undefined;
    point_value_max: number | undefined;
    category_names: string[];
    tags: TagSearchType | undefined;
    search: string[] | undefined;
}

export interface ChallengeLocationVisits {
    point_info: GetPointResponse;
    challenge_location_info: ChallengeLocation | undefined;
    checkins: CheckinInfo[];
}

export interface CreateGptTopicRequest {
    location: string;
    email: string;
    max_points?: number;
    creation_method: GptCreationMethod;
}

export interface SuggestGptTopicRequest {
    location: string;
    email: string;
    creation_method: GptCreationMethod;
}

export type GptCreationMethod =
    | { prompt: string }
    | {data: GptDataSubmission[] };

export interface SuggestGptPlaces {
    location: string;
    creation_method: GptCreationMethod;
    max_points?: number;
}

export interface ProcessGptPlacesToTopic {
    places: GptPlace[];
    topic_id: string; // Assuming ObjectId is a string
}

export interface GptPlace {
    name: string;
    description: string;
    category_name: string;
}

export interface ProcessGptPlacesRequest {
    places: GptPlace[];
    topic_id: string; // Assuming ObjectId is a string
    source?: PointSource;
    location?: string;
}

export interface GptDataSubmission {
    data: string;
    data_type: GptDataSubmissionType;
}

export type GptDataSubmissionType = 'text' | 'image_url';

export interface TtmGptJsonContentResponse {
    places: GptPlace[];
    general: TtmGptJsonGeneralResponse;
}

export interface TtmGptJsonGeneralResponse {
    suggested_map_name: string;
    suggested_map_description: string;
    suggested_map_zoom_level: number;
    suggested_map_focus_area: string;
    new_categories?: string[];
}

export enum PointCardView {
    List = 'list',
    Cards = 'cards',
    Table= 'table',
}


// Comment Types
export interface CreateCommentRequest {
    _id?: ObjectId
    content: string;
    username: string;
    is_public: boolean;
    parent_comment_id?: ObjectId;
    send_notification: boolean;
}

export interface CreateCommentResponse {
    _id: ObjectId;
}

export interface GetCommentResponse {
    _id: ObjectId;
    user_id?: ObjectId;
    email?: string;
    content: string;
    is_public: boolean;
    username?: string;
    parent_comment_id?: ObjectId;
    edited: boolean;
    is_entity_writer: boolean;
    is_comment_writer: boolean;
    reactions?: Reaction[];
    created_at: string;
    updated_at: string;
}

export interface Reaction {
    // Wire shape: Rust HashSet → JSON array (uniqueness preserved by sender).
    users: string[];
    reaction: string;
}

export interface EditReactionRequest {
    comment_id: ObjectId;
    reaction: string;
}

export interface OrderedComment {
    id: string;
    comment: GetCommentResponse;
    children: OrderedComment[];
}

export interface CommentSettings {
    allow_comments: boolean;
    allow_comment_replies: boolean;
    allow_comment_reactions: boolean;
    comment_title?: CustomText;
    comment_instructions?: string;
    notify_on_comment: boolean;
}

/** Per-Map comment configuration. Two public surfaces — map-level
 *  ("general feedback") and per-point ("reviews") — each with its
 *  own independent `CommentSurfaceSettings`. A surface is "on" iff
 *  the corresponding sub-object is `Some(_)`; `undefined` = surface
 *  disabled. */
export interface MapCommentSettings {
    map?: CommentSurfaceSettings;
    point?: CommentSurfaceSettings;
}

/** Per-surface comment settings — applies to either map-level or
 *  point-level. Both surfaces use the same shape so the editor row
 *  and renderer can share components. */
export interface CommentSurfaceSettings {
    title?: CustomText;
    instructions?: string;
    allow_replies: boolean;
    allow_reactions: boolean;
    notify_on_comment: boolean;
}

export interface CustomText {
    singular: string;
    plural: string;
}

export interface CreateRouteRequest {
    origin: CreateLocationRequest;
    destination: CreateLocationRequest;
    intermediates: CreateLocationRequest[];
    travel_mode: DirectionType;
    optimize_order: boolean;
    /** Map the route was planned on, so it shows in that map's numbers. */
    map_id?: ObjectId;
}

export interface RouteResponse {
    routes: Route[];
}

export interface Route {
    legs: RouteLeg[];
    polyline: Polyline;
    optimizedIntermediateWaypointIndex: number[];
    localizedValues: LocalizedValues;
}

export interface RouteLeg {
    steps?: RouteStep[];
    polyline: Polyline;
    localizedValues: LocalizedValues;
}

export interface LocalizedValues {
    distance?: LocalizedText;
    duration?: LocalizedText;
    staticDuration?: LocalizedText;
    transitFare?: LocalizedText;
}

export interface LocalizedText {
    text: string;
}

export interface RouteStep {
    navigationInstruction?: NavigationInstruction;
    localizedValues?: LocalizedValues;
}

export interface NavigationInstruction {
    maneuver: string;
    instructions: string;
}

export interface Polyline {
    encodedPolyline: string;
}

export interface Webhook {
    _id?: ObjectId;
    name: string;
    url: string;
    event: AppEvent;
    entity_type: EventTargetType;
    entity_id: ObjectId;
    is_active: boolean;
    created_at?: string;
    updated_at?: string;
}

export interface CreateWebhookRequest {
    name: string;
    url: string;
    event: AppEvent;
    entity_type: EventTargetType;
    entity_id: ObjectId;
    is_active: boolean;
}

export interface UpdateWebhookRequest {
    name?: string;
    url?: string;
    event?: AppEvent;
    entity_type?: EventTargetType;
    entity_id?: ObjectId;
    is_active?: boolean;
}

export interface WebhookTestRequest {
    webhook_id: ObjectId;
    sample_data?: any;
}

// Feature-based Payment Types
export enum PaymentProvider {
    Stripe = 'stripe',
    Chargebee = 'chargebee',
}

export enum ProductPricingType {
    Fixed = 'fixed',
    PerUnit = 'per_unit',
    Donation = 'donation',
    Metered = 'metered',
}

export enum ProductPeriod {
    Weekly = 'weekly',
    Monthly = 'monthly',
    Yearly = 'yearly',
}

export interface FormPaymentSettings {
    enabled: boolean;
    pricing_type: ProductPricingType;
    amount_cents?: number; // For fixed pricing
    suggested_amounts?: number[]; // For donation pricing
    dynamic_field_id?: string; // Field ID to use for dynamic pricing
    dynamic_multiplier?: number; // Multiplier for dynamic pricing (e.g., 100 = $1 per unit)
    post_payment_status: PostPaymentStatus;
    payment_required_message?: string;
    visibility_logic?: VisibilityRule[][];
}

export enum PostPaymentStatus {
    Active = 'active',
    PendingApproval = 'pending_approval',
}

export interface OrganizationPurchaseHistory {
    transactions: PurchaseSummaryResponse[];
    total_revenue_cents: RevenuePerCurrency[];
}

export interface RevenuePerCurrency {
    currency: string;
    revenue_cents: number;
    earned_cents: number;
}

export interface PurchaseItem {
    product_id: ObjectId;
    price_cents: number;
    quantity: number;
    recurring_interval?: ProductPeriod;
}

export interface ConnectedAccountStatus {
    account_id: string;
    status: 'not_connected' | 'awaiting_onboarding' | 'payment_enabled' | 'payment_disabled';
    charges_enabled: boolean;
    payouts_enabled: boolean;
    details_submitted: boolean;
    onboarding_completed: boolean;
    dashboard_url?: string;
}

// PURCHASE SET UP TYPES
export interface CreateOrganizationProductRequest {
    _id?: ObjectId;
    name: string;
    description: string;
    statement_descriptor: string;
    pricing_structure: PriceStructure;
    support_email: string;
    currency_code: string;
    proxi_fee_structure: ProxiFeeStructure;
    tax_structure: TaxStructure;
    item_details: OrganizationProductDetails;
}

export interface GetOrganizationProductResponse {
    _id: ObjectId;
    organization_id: ObjectId;
    item_details: OrganizationProductDetails;
    name: string;
    description: string;
    statement_descriptor: string;
    pricing_structure: PriceStructure;
    support_email: string;
    currency_code: string;
    proxi_fee_structure: ProxiFeeStructure;
    tax_structure: TaxStructure;
    status: OrganizationProductStatus;
    created_at: string;
    updated_at: string;
}

export interface GetPublicOrganizationProductResponse {
    _id: ObjectId;
    organization_id: ObjectId;
    item_details: OrganizationProductDetails;
    name: string;
    description: string;
    statement_descriptor: string;
    pricing_structure: PriceStructure;
    support_email: string;
    currency_code: string;
    proxi_fee_structure: ProxiFeeStructure;
    tax_structure: TaxStructure;
    status: OrganizationProductStatus;
    buyer_covers_fees?: boolean;
}

export type ProxiFeeStructure = ProxiFeePercentageType | ProxiFeeFixedType | ProxiFeePayerType | 'none';

export interface ProxiFeePercentageType {
    percentage: ProxiFeePercentageSettings;
}

export interface ProxiFeeFixedType {
    fixed: ProxiFeeFixedSettings;
}

export interface ProxiFeePayerType {
    payer: ProxiFeePayerSettings;
}

export interface ProxiFeePercentageSettings {
    percentage: number; // percentage  (1.5% = 1.5)
    ask_for_customer_coverage: boolean;
}

export interface ProxiFeeFixedSettings {
    amount: number; // amount stored as whole numbers (1 = 1 cent)
    ask_for_customer_coverage: boolean;
}

export interface ProxiFeePayerSettings {
    payer: string;
    ask_for_customer_coverage: boolean;
}

export type TaxStructure = 'none' | TaxedSettingsType;

export interface TaxedSettingsType {
    taxed: TaxSettings;
}

export interface TaxSettings {
    office_country: string; // ISO 3166-1 alpha-2 country code
    office_postal_code: string;
    product_tax_code: string;
}

export interface PurchaseSummaryResponse {
    status: PurchaseStatus;
    total_price_cents: number;
    currency: string;
    updated_at: string;
    payment_complete_at?: string;
    checkout_start_at?: string;
    pending_transaction_id?: ObjectId;
    purchase_id?: ObjectId;
    invoice_id?: string;
    purchase_records?: PurchaseRecords;
    purchase_items: PurchaseItem[];
    refund_amount_cents?: number;
    refund_currency?: string;
    refund_reason?: string;
    organization_id: ObjectId;
    card_brand: string;
    card_last4: string;
    support_email: string;
    receipt_url?: string;
    user_email: string;
    payment_provider: PaymentProvider;
    subscription_id: string | undefined;
}

export type PurchaseRecords = PaywallPurchaseRecordType | FormPurchaseRecordType | ActivatedChallengePurchaseRecordType | BallotPurchaseRecordType;

export interface BallotPurchaseRecordType {
    Ballot: BallotPurchaseRecords;
}

export interface BallotPurchaseRecords {
    topic_id: ObjectId;
    election_id: ObjectId;
    ballot_id: ObjectId;
}

export interface PaywallPurchaseRecordType {
    Paywall: PaywallPurchaseRecords;
}

export interface PaywallPurchaseRecords {
    topic_id: ObjectId;
    gate_id: ObjectId;
}

export interface FormPurchaseRecordType {
    Form: FormPurchaseRecords;
}

export interface FormPurchaseRecords {
    topic_id: ObjectId;
    point_id: ObjectId;
}

export interface ActivatedChallengePurchaseRecordType {
    ActivatedChallenge: ActivatedChallengePurchaseRecords;
}

export interface ActivatedChallengePurchaseRecords {
    challenge_id: ObjectId;
    activated_challenge_id: ObjectId;
}

export enum PurchaseStatus {
    Pending = 'pending',
    Completed = 'completed',
    Refunded = 'refunded',
    Failed = 'failed',
    Cancelled = 'cancelled',
    Expired = 'expired',
}

export type PriceStructure = DonationPriceStructureDetails | FixedPriceStructureDetails | PerUnitPriceStructureDetails | RecurringPriceStructureDetails;

export interface DonationPriceStructureDetails {
    Donation: DonationSettings;
}

export interface DonationSettings {
    suggested_amounts_cents: number[];
    allow_custom_amount: boolean;
    max_amount_cents: number;
    min_amount_cents: number;
    default_amount_cents: number;
    required: boolean;
}

export interface FixedPriceStructureDetails {
    Fixed: FixedPriceSettings;
}

export interface FixedPriceSettings {
    amount_cents: number;
}

export interface PerUnitPriceStructureDetails {
    PerUnit: PerUnitPriceSettings;
}

export interface PerUnitPriceSettings {
    unit_price_cents: number;
    min_units: number;
    max_units: number;
    default_units: number;
}

export interface RecurringPriceStructureDetails {
    Recurring: RecurringPriceSettings;
}

export interface RecurringPriceSettings {
    amount_cents: number;
    interval: ProductPeriod;
}

export type OrganizationProductDetails = PaywallPurchaseableDetailsLoad | FormPurchaseableDetailsLoad | ActivatedChallengePurchaseableDetailsLoad | BallotPurchaseableDetailsLoad | ProgramPurchaseableDetailsLoad | ChallengeSupportPurchaseableDetailsLoad;

export interface ChallengeSupportPurchaseableDetailsLoad {
    ChallengeSupport: ChallengeSupportPurchaseableDetails;
}

export interface ChallengeSupportPurchaseableDetails {
    challenge_id: ObjectId;
}

export interface PaywallPurchaseableDetailsLoad {
    Paywall: PaywallPurchaseableDetails;
}

export interface PaywallPurchaseableDetails {
    topic_id: ObjectId;
    gate_id: ObjectId | undefined;
}

export interface ProgramPurchaseableDetailsLoad {
    Program: ProgramPurchaseableDetails;
}

export interface ProgramPurchaseableDetails {
    program_id: ObjectId;
}

export interface FormPurchaseableDetailsLoad {
    Form: FormPurchaseableDetails;
}

export interface FormPurchaseableDetails {
    topic_id: ObjectId;
    visibility_rules: VisibilityRule[][];
    post_submission_action: 'auto_approve' | 'await_admin_approval';
}

export interface ActivatedChallengePurchaseableDetailsLoad {
    ActivatedChallenge: ActivatedChallengePurchaseableDetails;
}

export interface ActivatedChallengePurchaseableDetails {
    challenge_id: ObjectId;
    activated_challenge_id: ObjectId;
}

export interface BallotPurchaseableDetailsLoad {
    Ballot: BallotPurchaseableDetails;
}

export interface BallotPurchaseableDetails {
    election_id: ObjectId;
    topic_id: ObjectId;
}

export interface PurchaseItemRequest {
    product_id: string;
    quantity: number;
    custom_amount_cents?: number;
}

export enum OrganizationProductStatus {
    Active = 'active',
    Inactive = 'inactive',
    Archived = 'archived',
    SoldOut = 'sold_out',
}

export enum PaymentTargetType {
    ActivatedChallenge = 'activated_challenge',
    Form = 'form',
    Paywall = 'paywall',
    Ballot = 'ballot',
    Program = 'program',
    ChallengeSupport = 'challenge_support',
}

export interface StripePendingTransactionRequest {
    email: string;
    target_entity_id: string;
    purchase_items: PurchaseItemRequest[];
    content: PaymentContentDraft | undefined;
    existing_entity_id: string | undefined;
}

export type PaymentContentDraft = NewPaymentContentRequestType | ExistingPaymentContentRequestType;

export interface NewPaymentContentRequestType {
    New : NewPaymentContentRequest;
}

export interface ExistingPaymentContentRequestType {
    Existing : ExistingPaymentContentRequest;
}

export type NewPaymentContentRequest = NewActivatedChallengePaymentDraft | NewFormPaymentDraft | NewPaywallPaymentDraft | NewBallotPaymentDraft;
export type ExistingPaymentContentRequest = ExistingActivatedChallengePaymentDraft | ExistingFormPaymentDraft | ExistingPaywallPaymentDraft | ExistingBallotPaymentDraft;

export interface NewBallotPaymentDraft {
    Ballot: CreateBallotRequest;
}

export interface NewActivatedChallengePaymentDraft {
    ActivatedChallenge: CreateActivatedChallengeRequest;
}

export interface NewFormPaymentDraft {
    Form: CreatePointRequest;
}

export interface NewPaywallPaymentDraft {
    Paywall: CreatePaywallRoleAssignmentRequest;
}

export interface ExistingBallotPaymentDraft {
    Ballot: ObjectId;
}

export interface ExistingActivatedChallengePaymentDraft {
    ActivatedChallenge: ObjectId;
}

export interface ExistingFormPaymentDraft {
    Form: CreatePointRequest;
}

export interface ExistingPaywallPaymentDraft {
    Paywall: ObjectId;
}

export interface CreatePaywallRoleAssignmentRequest {
    topic_id: ObjectId;
    gate_id: ObjectId;
    email: string;
}

export enum ViewerActionKind {
    Visited = 'visited',
    Saved = 'saved',
    Liked = 'liked',
}

export interface ViewerAction {
    id: ObjectId;
    account_id: ObjectId;
    point_id: ObjectId;
    topic_id: ObjectId;
    kind: ViewerActionKind;
    note?: string;
    created_at: string;
    updated_at: string;
}

export interface UpsertViewerActionRequest {
    note?: string;
}

export interface PointRating {
    id: ObjectId;
    account_id: ObjectId;
    point_id: ObjectId;
    topic_id: ObjectId;
    value: number;
    max_stars_at_rating: number;
    created_at: string;
    updated_at: string;
}

export interface PointRatingSummary {
    point_id: ObjectId;
    /** Mean rating, normalized to a 0..=1 fraction (so old 4★/5
     *  entries combine sensibly with new 8★/10 entries). Multiply
     *  client-side by the current `max_stars` for display. */
    avg: number;
    count: number;
}

export interface UpsertPointRatingRequest {
    value: number;
    max_stars: number;
}
